import { useState, useEffect, useMemo, memo, useRef, createContext, useContext, useCallback, useSyncExternalStore, type InputHTMLAttributes } from "react";
import { useTranslation, Trans } from "react-i18next";
import type { TFunction } from "i18next";
import { fmtDate, fmtDateTime, intlLocale } from "@/lib/locale";
import { useSearch, useLocation, Link } from "wouter";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, PieChart, Pie, Legend,
  LineChart, Line,
} from "recharts";
import { computeFloorplanStats, filterStatementsToLenders } from "@/lib/floorplanStats";
import { STAFFING_BENCHMARKS, INVENTORY_TYPE_LABELS, type DealerInventoryType } from "@/lib/onboardingBenchmarks";
import {
  computeTotalIncomeForSave,
  resolveRestoredTotalIncome,
} from "@/lib/opAnalyticsInputUtils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useOpAnalytics } from "@/hooks/useOpAnalytics";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormattedNumberInput } from "@/components/formatted-number-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { openSavedTaxReturn, SavedTaxReturnConflictError, type LoadedSavedTaxReturn } from "@/lib/savedTaxReturnEditor";
import { uploadFileDirect } from "@/lib/directUpload";
import { TaxReturnSource } from "@/lib/taxReturnSource";
import { taxReturnSaveRequest, taxReturnSaveScope } from "@/lib/taxReturnSaveRequest";
import { readTaxReturnDraft, writeTaxReturnDraft, taxReturnDraftKey, taxReturnDraftScope } from "@/lib/taxReturnDraft";
import { useTaxReturnDraft } from "@/hooks/use-tax-return-draft";
import { trackEvent } from "@/hooks/use-activity-tracker";
import {
  TrendingUp, AlertTriangle, DollarSign,
  BarChart3, CheckCircle2, FileText, Calculator, Info,
  ChevronDown, ChevronRight, ChevronLeft, Pencil, Check, Target, Users, Megaphone,
  RotateCcw, Lightbulb, Building2, Wallet, FlaskConical,
  Upload, Loader2, RefreshCw, ArrowUpRight, ArrowDownRight, ArrowRight, Minus,
  Activity, ShieldCheck, ClipboardList, Landmark, X, Star, Zap, Trophy, Trash2, Plus,
  Lock,
} from "lucide-react";
import { BankHealthTab } from "@/components/bank-health-tab";
import { useDean } from "@/contexts/dean-context";
import { TaxReturnChatbot } from "@/components/tax-return-chatbot";
import WebsiteTrackingSection from "@/components/website-tracking-section";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt$(v: number | null | undefined, decimals = 0): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: decimals });
}
function fmtNum(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { maximumFractionDigits: 0 });
}
function fmtPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}
function nf(v: string): number {
  const p = parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(p) ? p : 0;
}

// Thin wrapper over the shared FormattedNumberInput: callers here work with
// raw numeric strings. Caret position is preserved while typing.
function FormattedInput({
  value, onChange, liveFormat: _liveFormat, ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "type" | "value" | "min" | "max" | "prefix"> & {
  value: string;
  onChange: (val: string) => void;
  liveFormat?: boolean;
}) {
  return (
    <FormattedNumberInput
      {...props}
      value={value}
      decimals={2}
      onValueChange={(raw) => onChange(raw)}
    />
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

type ExtractedFinancials = {
  taxYear?: string;
  revenue?: number;
  cogs?: number;
  grossProfit?: number;
  totalOperatingExpenses?: number;
  expenses: {
    dynamicItems?: { label: string; amount: number }[];
  };
  warnings: string[];
  rental?: {
    rentalGrossIncome: number | null;
    rentalTotalExpenses: number | null;
    rentalNetIncome: number | null;
  } | null;
};

type SavedPayload = {
  lastSavedAt: string;
  input: any;
  output: any;
  sourceTaxYear?: number | null;
} | null;

// ─── Delta Badge ──────────────────────────────────────────────────────────────

function DeltaBadge({ actual, reference, higherIsBetter = true }: { actual: number; reference: number; higherIsBetter?: boolean }) {
  if (!reference || !Number.isFinite(actual / reference)) return null;
  const pct = ((actual - reference) / reference) * 100;
  const positive = higherIsBetter ? pct >= 0 : pct <= 0;
  const sign = pct >= 0 ? "+" : "";
  return (
    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${positive ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-destructive/10 text-destructive"}`}>
      {sign}{pct.toFixed(1)}%
    </span>
  );
}

// ─── Live Input Context (shared between Analytics, Expenses, Marketing, and Dealer Profile tabs) ───

type LiveInp = {
  annualUnitsSold?: number;
  grossProfit?: number;
  totalIncome?: number;
  revenue?: number;
  annualTotalOperatingExpenses?: number;
  avgGrossProfitPerUnit?: number;
  expenses?: {
    dynamicItems?: { label: string; amount: number }[];
  };
};


const OperationsLiveCtx = createContext<{
  liveInp: LiveInp | null;
  annualUnitsSold: string;
  setAnnualUnitsSold: (v: string) => void;
  manualRevenue: string;
  setManualRevenue: (v: string) => void;
  manualGrossProfit: string;
  setManualGrossProfit: (v: string) => void;
  manualTotalIncome: string;
  setManualTotalIncome: (v: string) => void;
  manualOpEx: string;
  setManualOpEx: (v: string) => void;
  dynamicExpenseItems: { label: string; amount: number }[];
  setDynamicExpenseItems: (v: { label: string; amount: number }[]) => void;
  loadedSavedReturn: LoadedSavedTaxReturn | null;
  setLoadedSavedReturn: React.Dispatch<React.SetStateAction<LoadedSavedTaxReturn | null>>;
  displayTaxYear: number | null;
  opAnalyticsFetching: boolean;
  isExtractionPending: boolean;
  setIsExtractionPending: (v: boolean) => void;
}>({
  liveInp: null,
  annualUnitsSold: "",
  setAnnualUnitsSold: () => {},
  manualRevenue: "",
  setManualRevenue: () => {},
  manualGrossProfit: "",
  setManualGrossProfit: () => {},
  manualTotalIncome: "",
  setManualTotalIncome: () => {},
  manualOpEx: "",
  setManualOpEx: () => {},
  dynamicExpenseItems: [],
  setDynamicExpenseItems: () => {},
  loadedSavedReturn: null,
  setLoadedSavedReturn: () => {},
  displayTaxYear: null,
  opAnalyticsFetching: true,
  isExtractionPending: false,
  setIsExtractionPending: () => {},
});

const OperationsCtx = createContext<{
  activeTab: string;
  setActiveTab: (tab: string) => void;
}>({
  activeTab: "analytics",
  setActiveTab: () => {},
});

function OperationsLiveProvider({ children }: { children: React.ReactNode }) {
  const { data: user } = useQuery<{
    id: string; ownerUserId?: string | null; impersonatingUserId?: string | null;
  } | null>({ queryKey: ["/api/auth/me"] });
  // The route itself can survive auth refresh. Scope the entire editor, not
  // just its saved-return list: loaded snapshots and derived local values must
  // never carry over to a different actor or owning dealer.
  return <ScopedOperationsLiveProvider key={taxReturnSaveScope(user)}>{children}</ScopedOperationsLiveProvider>;
}

function ScopedOperationsLiveProvider({ children }: { children: React.ReactNode }) {
  const [annualUnitsSold, setAnnualUnitsSold] = useState("");
  const [manualRevenue, setManualRevenue] = useState("");
  const [manualGrossProfit, setManualGrossProfit] = useState("");
  const [manualTotalIncome, setManualTotalIncome] = useState("");
  const [manualOpEx, setManualOpEx] = useState("");
  const [dynamicExpenseItems, setDynamicExpenseItems] = useState<{ label: string; amount: number }[]>([]);
  const [loadedSavedReturn, setLoadedSavedReturn] = useState<LoadedSavedTaxReturn | null>(null);
  const [isExtractionPending, setIsExtractionPending] = useState(false);

  const liveInp = useMemo<LiveInp | null>(() => {
    const grossProfitVal = nf(manualGrossProfit);
    const opExVal = manualOpEx !== "" ? nf(manualOpEx) : 0;
    const unitsVal = nf(annualUnitsSold);
    const avgGross = grossProfitVal > 0 && unitsVal > 0 ? grossProfitVal / unitsVal : undefined;
    return {
      annualUnitsSold: unitsVal || undefined,
      grossProfit: grossProfitVal || undefined,
      totalIncome: manualTotalIncome !== "" ? nf(manualTotalIncome) : undefined,
      revenue: nf(manualRevenue) || undefined,
      annualTotalOperatingExpenses: opExVal || undefined,
      avgGrossProfitPerUnit: avgGross,
      expenses: { dynamicItems: dynamicExpenseItems },
    };
  }, [annualUnitsSold, manualGrossProfit, manualTotalIncome, manualRevenue, manualOpEx, dynamicExpenseItems]);

  const { data: opLatest, isFetching: opAnalyticsFetching } = useOpAnalytics();
  const displayTaxYear: number | null = useMemo(() => {
    if (isExtractionPending) return null;
    const sourceTaxYear = opLatest?.sourceTaxYear ?? null;
    if (!loadedSavedReturn) return sourceTaxYear;
    return Number(loadedSavedReturn.name.match(/\d{4}/)?.[0]) || sourceTaxYear;
  }, [isExtractionPending, loadedSavedReturn, opLatest]);

  // Auto-restore the last-viewed saved return at the PROVIDER level so that the
  // correct tax year and figures are available on every tab from the first
  // render — not just after the Analytics tab (which owns the legacy restore)
  // has been mounted. Without this, landing directly on the Expenses tab after a
  // reload showed the latest-extraction year (e.g. 2025) until the user visited
  // the Analytics tab and came back.
  const { data: providerCurrentUser } = useQuery<{ id: string; ownerUserId?: string | null } | null>({ queryKey: ["/api/auth/me"] });
  const { data: providerSavedReturns = [] } = useQuery<any[]>({ queryKey: ["/api/saved-tax-returns"] });
  const providerRestoreAttempted = useRef(false);
  useEffect(() => {
    if (providerRestoreAttempted.current) return;
    if (isExtractionPending) return;
    if (!providerCurrentUser?.id) return;
    if (providerSavedReturns.length === 0) return;
    providerRestoreAttempted.current = true;
    if (loadedSavedReturn) return;
    const key = `lastTaxReturnId:${providerCurrentUser.ownerUserId ?? providerCurrentUser.id}`;
    let lastId: string | null = null;
    try { lastId = localStorage.getItem(key); } catch {}
    if (!lastId) return;
    const entry = providerSavedReturns.find(r => r.id === lastId);
    if (!entry) { try { localStorage.removeItem(key); } catch {} return; }
    try {
      const snap = typeof entry.snapshotJson === "string" ? JSON.parse(entry.snapshotJson) : entry.snapshotJson;
      setManualRevenue(snap.revenue != null ? String(snap.revenue) : "");
      setManualGrossProfit(snap.grossProfit != null ? String(snap.grossProfit) : "");
      setManualTotalIncome(snap.totalIncome != null ? String(snap.totalIncome) : "");
      setManualOpEx(snap.annualTotalOperatingExpenses != null ? String(snap.annualTotalOperatingExpenses) : "");
      setAnnualUnitsSold(snap.annualUnitsSold != null ? String(snap.annualUnitsSold) : "");
      setDynamicExpenseItems(Array.isArray(snap.dynamicExpenses) ? snap.dynamicExpenses : []);
      setLoadedSavedReturn(openSavedTaxReturn(entry));
    } catch {}
  }, [providerSavedReturns, providerCurrentUser, loadedSavedReturn, isExtractionPending]);

  const value = useMemo(() => ({
    liveInp, annualUnitsSold, setAnnualUnitsSold, manualRevenue, setManualRevenue,
    manualGrossProfit, setManualGrossProfit, manualTotalIncome, setManualTotalIncome,
    manualOpEx, setManualOpEx, dynamicExpenseItems, setDynamicExpenseItems,
    loadedSavedReturn, setLoadedSavedReturn, displayTaxYear, opAnalyticsFetching,
    isExtractionPending, setIsExtractionPending,
  }), [liveInp, annualUnitsSold, manualRevenue, manualGrossProfit, manualTotalIncome, manualOpEx, dynamicExpenseItems, loadedSavedReturn, displayTaxYear, opAnalyticsFetching, isExtractionPending]);

  return <OperationsLiveCtx.Provider value={value}>{children}</OperationsLiveCtx.Provider>;
}

// ─── Assessment Tab ─────────────────────────────────────────────────────────────

type TrendDirection = "Improving" | "Stable" | "Declining" | "No Data Yet";

interface DomainDataGap {
  label: string;
  action: string;
  route: string;
}

interface DomainFinding {
  domain: string;
  confidence: number;
  trend: TrendDirection;
  narrative: string;
  benchmarkNote: string;
  whatsWorking: string[];
  needsImprovement: string[];
  recommendations: string[];
  dataGaps: DomainDataGap[];
}

interface DomainSubScore {
  domain: string;
  weight: number;
  rawScore: number;
  benchmarkRatio: number;
  confidence: number;
  contribution: number;
  tip: string | null;
}

interface DealerScoreResult {
  score: number;
  tier: "Rookie" | "Rising" | "Competitive" | "Elite";
  grade: string;
  domainSubScores: DomainSubScore[];
  topImprovementTips: string[];
  tierGap: { nextTier: string; pointsNeeded: number } | null;
  encouragement: string;
}

interface AssessmentData {
  generatedAt: string;
  overallConfidence: number;
  domains: DomainFinding[];
  dataGaps: { label: string; action: string; route: string }[];
  dealerScore: number | null;
  dealerScoreTier: string | null;
  dealerScoreResult: DealerScoreResult | null;
}

function TrendBadge({ trend, onInsufficientDataClick, light }: { trend: TrendDirection; onInsufficientDataClick?: () => void; light?: boolean }) {
  const { t } = useTranslation(["operations", "common"]);
  if (trend === "Improving") {
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${light ? "bg-white/20 text-white" : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"}`} data-testid="badge-trend-improving">
        <ArrowUpRight className="w-3 h-3" /> {t("operations:trend.improving")}
      </span>
    );
  }
  if (trend === "Declining") {
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${light ? "bg-white/20 text-white" : "bg-destructive/10 text-destructive"}`} data-testid="badge-trend-declining">
        <ArrowDownRight className="w-3 h-3" /> {t("operations:trend.declining")}
      </span>
    );
  }
  if (trend === "Stable") {
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${light ? "bg-white/20 text-white" : "bg-amber-500/10 text-amber-600 dark:text-amber-400"}`} data-testid="badge-trend-stable">
        <Minus className="w-3 h-3" /> {t("operations:trend.stable")}
      </span>
    );
  }
  if (onInsufficientDataClick) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onInsufficientDataClick(); }}
        className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full cursor-pointer transition-colors ${light ? "bg-white/20 text-white hover:bg-white/30" : "text-muted-foreground bg-muted hover:bg-muted/80"}`}
        data-testid="badge-trend-no-data"
      >
        <Activity className="w-3 h-3" /> {t("operations:trend.noDataYet")}
      </button>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${light ? "bg-white/20 text-white" : "text-muted-foreground bg-muted"}`} data-testid="badge-trend-no-data">
      <Activity className="w-3 h-3" /> {t("operations:trend.noDataYet")}
    </span>
  );
}


interface DataGapsDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  gaps: DomainDataGap[];
  onGapAction: (gap: DomainDataGap) => void;
  trend?: TrendDirection;
}

function DataGapsDialog({ open, onClose, title, gaps, onGapAction, trend }: DataGapsDialogProps) {
  const { t } = useTranslation(["operations", "common"]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="dialog-data-gaps">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Info className="w-4 h-4 text-muted-foreground" />
            {t("operations:dataGaps.title", { title })}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t("operations:dataGaps.description")}
          </DialogDescription>
        </DialogHeader>
        {gaps.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">{t("operations:dataGaps.none")}</p>
        ) : (
          <div className="space-y-2 pt-1">
            {gaps.map((gap, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-1.5 border-b border-border/50 last:border-0" data-testid={`dialog-gap-item-${i}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground/80">{gap.label}</p>
                  <p className="text-xs text-muted-foreground truncate">{gap.action}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0 h-7 text-xs"
                  onClick={() => { onGapAction(gap); onClose(); }}
                  data-testid={`dialog-gap-action-${i}`}
                >
                  {t("operations:dataGaps.go")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function resolveGapTab(route: string): string {
  if (route.includes("marketing")) return "marketing";
  return "analytics";
}

function trendAccentClasses(trend: TrendDirection): { tile: string; icon: string; gradient: string; glow: string } {
  if (trend === "Improving") return {
    tile: "border-emerald-300/60 dark:border-emerald-700/60",
    icon: "bg-white/20 text-white",
    gradient: "from-emerald-500 to-emerald-600 dark:from-emerald-600 dark:to-emerald-700",
    glow: "hover:shadow-emerald-500/30",
  };
  if (trend === "Declining") return {
    tile: "border-rose-300/60 dark:border-rose-700/60",
    icon: "bg-white/20 text-white",
    gradient: "from-rose-500 to-red-600 dark:from-rose-600 dark:to-red-700",
    glow: "hover:shadow-rose-500/30",
  };
  if (trend === "Stable") return {
    tile: "border-amber-300/60 dark:border-amber-700/60",
    icon: "bg-white/20 text-white",
    gradient: "from-amber-400 to-amber-500 dark:from-amber-500 dark:to-amber-600",
    glow: "hover:shadow-amber-500/30",
  };
  return {
    tile: "border-border",
    icon: "bg-white/20 text-white",
    gradient: "from-slate-500 to-slate-600",
    glow: "hover:shadow-slate-400/30",
  };
}

function DomainIcon({ domain, className }: { domain: string; className?: string }) {
  const cls = className ?? "w-4 h-4";
  if (domain === "Financial Health") return <DollarSign className={cls} />;
  if (domain === "Inventory Efficiency") return <BarChart3 className={cls} />;
  if (domain === "Expense Structure") return <Calculator className={cls} />;
  if (domain === "Cash Flow & Liquidity") return <Wallet className={cls} />;
  if (domain === "Marketing Performance") return <Megaphone className={cls} />;
  if (domain === "Lending Mix") return <Building2 className={cls} />;
  if (domain === "Team & Staffing") return <Users className={cls} />;
  return <Target className={cls} />;
}

function DomainTile({ finding, index, onClick, onGapHintClick }: { finding: DomainFinding; index: number; onClick: () => void; onGapHintClick?: () => void }) {
  const { t } = useTranslation(["operations", "common"]);
  const accent = trendAccentClasses(finding.trend);
  const stableGap = finding.trend === "Stable" && finding.dataGaps.length > 0 ? finding.dataGaps[0] : null;
  return (
    <button
      type="button"
      className={`group w-full text-left rounded-2xl border bg-gradient-to-br ${accent.gradient} ${accent.tile} p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl ${accent.glow} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60`}
      onClick={onClick}
      data-testid={`tile-domain-${index}`}
    >
      <div className="flex items-start gap-4">
        <div className={`shrink-0 p-3 rounded-xl ${accent.icon} backdrop-blur-sm border border-white/10`}>
          <DomainIcon domain={finding.domain} className="w-6 h-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold leading-tight text-white drop-shadow-sm" data-testid={`tile-name-${index}`}>{finding.domain}</p>
          <div className="mt-2">
            <TrendBadge trend={finding.trend} light />
          </div>
          {stableGap && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (onGapHintClick) { onGapHintClick(); } else { onClick(); } }}
              className="mt-2 text-[11px] leading-snug text-white/60 hover:text-white/90 transition-colors text-left cursor-pointer"
              data-testid={`tile-stable-gap-hint-${index}`}
            >
              {t("operations:domain.unlockTrend", { action: stableGap.action })}
            </button>
          )}
        </div>
      </div>
    </button>
  );
}

function DomainDetailSheet({
  domains,
  activeIndex,
  onClose,
  onNavigate,
  onRegenerate,
  isRegenerating,
}: {
  domains: DomainFinding[];
  activeIndex: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onRegenerate: () => void;
  isRegenerating: boolean;
}) {
  const { t } = useTranslation(["operations", "common"]);
  const { setActiveTab } = useContext(OperationsCtx);
  const [, navigate] = useLocation();
  const [gapDialogOpen, setGapDialogOpen] = useState(false);
  const isOpen = activeIndex !== null;
  const finding = activeIndex !== null ? domains[activeIndex] : null;
  const hasGaps = finding ? finding.dataGaps.length > 0 : false;

  useEffect(() => {
    if (!isOpen) setGapDialogOpen(false);
  }, [isOpen]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen || activeIndex === null) return;
    if (e.key === "ArrowLeft" && activeIndex > 0) onNavigate(activeIndex - 1);
    if (e.key === "ArrowRight" && activeIndex < domains.length - 1) onNavigate(activeIndex + 1);
  }, [isOpen, activeIndex, domains.length, onNavigate]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const accent = finding ? trendAccentClasses(finding.trend) : null;
  const stableGap = finding && finding.trend === "Stable" && finding.dataGaps.length > 0 ? finding.dataGaps[0] : null;

  return (
    <>
      {finding && (
        <DataGapsDialog
          open={gapDialogOpen}
          onClose={() => setGapDialogOpen(false)}
          title={finding.domain}
          gaps={finding.dataGaps}
          trend={finding.trend}
          onGapAction={(gap) => { if (gap.route.startsWith("/operations")) { setActiveTab(resolveGapTab(gap.route)); onClose(); } else { onClose(); navigate(gap.route); } }}
        />
      )}
      <Dialog open={isOpen} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent
          hideClose
          className="max-w-lg w-full flex flex-col p-0 overflow-hidden gap-0 max-h-[90vh]"
          data-testid="sheet-domain-detail"
        >
          {finding && activeIndex !== null && accent && (
            <>
              {/* Modal header with gradient banner */}
              <div className={`shrink-0 bg-gradient-to-br ${accent.gradient} px-5 pt-5 pb-4`}>
                <div className="flex items-start gap-3">
                  <div className={`shrink-0 p-2.5 rounded-xl ${accent.icon} border border-white/20`}>
                    <DomainIcon domain={finding.domain} className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <DialogTitle className="text-base font-bold leading-tight text-white" data-testid="sheet-domain-name">{finding.domain}</DialogTitle>
                    <DialogDescription className="sr-only">{t("operations:domain.detailFor", { domain: finding.domain })}</DialogDescription>
                    <div className="mt-1">
                      <TrendBadge trend={finding.trend} light onInsufficientDataClick={hasGaps ? () => setGapDialogOpen(true) : undefined} />
                    </div>
                    {stableGap && (
                      <button
                        type="button"
                        onClick={() => setGapDialogOpen(true)}
                        className="mt-1.5 text-[11px] leading-snug text-white/60 hover:text-white/90 transition-colors text-left"
                        data-testid="sheet-stable-gap-hint"
                      >
                        {t("operations:domain.unlockTrend", { action: stableGap.action })}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      disabled={activeIndex === 0}
                      onClick={() => onNavigate(activeIndex - 1)}
                      className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      aria-label={t("operations:domain.previousDomain")}
                      data-testid="btn-sheet-prev"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      disabled={activeIndex === domains.length - 1}
                      onClick={() => onNavigate(activeIndex + 1)}
                      className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      aria-label={t("operations:domain.nextDomain")}
                      data-testid="btn-sheet-next"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors ml-1"
                      aria-label={t("common:close")}
                      data-testid="btn-sheet-close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal body */}
              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
                {hasGaps && (
                  <button
                    type="button"
                    onClick={() => setGapDialogOpen(true)}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="btn-sheet-gaps"
                  >
                    <Info className="w-3 h-3" />
                    {t("operations:domain.dataGapCount", { count: finding.dataGaps.length })}
                  </button>
                )}

                {/* Narrative */}
                {finding.narrative && (
                  <p className="text-sm leading-relaxed text-foreground/90" data-testid="text-sheet-narrative">{finding.narrative}</p>
                )}

                {/* Benchmark comparison */}
                {finding.benchmarkNote && (
                  <div className="flex gap-2 p-3 rounded-lg bg-muted/50 border border-border/50">
                    <Target className="w-4 h-4 shrink-0 text-muted-foreground mt-0.5" />
                    <p className="text-xs text-muted-foreground" data-testid="text-sheet-benchmark">{finding.benchmarkNote}</p>
                  </div>
                )}

                {/* What's Working / Needs Improvement */}
                {(finding.whatsWorking.length > 0 || finding.needsImprovement.length > 0) && (
                  <div className="grid grid-cols-1 gap-3">
                    {finding.whatsWorking.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">{t("operations:domain.whatsWorking")}</p>
                        <ul className="space-y-1.5">
                          {finding.whatsWorking.map((item, i) => (
                            <li key={i} className="flex gap-1.5 text-sm text-foreground/80" data-testid={`text-sheet-working-${i}`}>
                              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500 mt-0.5" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {finding.needsImprovement.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide">{t("operations:domain.needsImprovement")}</p>
                        <ul className="space-y-1.5">
                          {finding.needsImprovement.map((item, i) => (
                            <li key={i} className="flex gap-1.5 text-sm text-foreground/80" data-testid={`text-sheet-improvement-${i}`}>
                              <AlertTriangle className="w-4 h-4 shrink-0 text-amber-500 mt-0.5" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Recommendations */}
                {finding.recommendations.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-primary uppercase tracking-wide">{t("operations:domain.recommendations")}</p>
                    <ul className="space-y-2">
                      {finding.recommendations.map((rec, i) => (
                        <li key={i} className="flex gap-2 text-sm" data-testid={`text-sheet-rec-${i}`}>
                          <Lightbulb className="w-4 h-4 shrink-0 text-primary mt-0.5" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Confidence footer */}
                <div className="pt-2 flex flex-col items-center gap-2">
                  {hasGaps && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setGapDialogOpen(true)}
                      className="gap-1.5"
                      data-testid="btn-sheet-open-gaps"
                    >
                      <Info className="w-3.5 h-3.5" />
                      {t("operations:domain.viewDataGaps", { count: finding.dataGaps.length })}
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground text-center" data-testid="text-sheet-confidence">
                    {t("operations:domain.confidence", { pct: finding.confidence })} · {finding.confidence >= 70 ? t("operations:domain.confidenceHigh") : finding.confidence >= 40 ? t("operations:domain.confidenceMedium") : t("operations:domain.confidenceLow")}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRegenerate}
                    disabled={isRegenerating}
                    className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                    data-testid="button-sheet-refresh-analysis"
                  >
                    {isRegenerating
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />{t("operations:domain.refreshing")}</>
                      : <><RefreshCw className="w-3.5 h-3.5" />{t("operations:domain.refreshAnalysis")}</>
                    }
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Dealer Score Components ──────────────────────────────────────────────────

type ScoreTier = "Rookie" | "Rising" | "Competitive" | "Elite";

function tierConfig(tier: ScoreTier | string | null) {
  if (tier === "Elite") return { label: "Elite", gradient: "from-yellow-400 to-amber-500", ring: "ring-amber-400", text: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-900/20", icon: Trophy };
  if (tier === "Competitive") return { label: "Competitive", gradient: "from-slate-400 to-slate-500", ring: "ring-slate-400", text: "text-slate-500 dark:text-slate-300", bg: "bg-slate-50 dark:bg-slate-800/50", icon: Star };
  if (tier === "Rising") return { label: "Rising", gradient: "from-amber-600 to-yellow-700", ring: "ring-yellow-700", text: "text-yellow-700 dark:text-yellow-600", bg: "bg-yellow-50 dark:bg-yellow-900/20", icon: Zap };
  return { label: "Rookie", gradient: "from-stone-400 to-stone-500", ring: "ring-stone-400", text: "text-stone-600 dark:text-stone-400", bg: "bg-stone-50 dark:bg-stone-800/50", icon: Target };
}

function useCountUp(target: number, duration = 1200) {
  const [value, setValue] = useState(0);
  const startRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    startRef.current = null;
    const animate = (now: number) => {
      if (!startRef.current) startRef.current = now;
      const elapsed = now - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [target, duration]);

  return value;
}

function ScoreBreakdownDrawer({
  open,
  onClose,
  scoreResult,
}: {
  open: boolean;
  onClose: () => void;
  scoreResult: DealerScoreResult | null;
}) {
  const { t } = useTranslation(["operations", "common"]);
  if (!scoreResult) return null;
  const cfg = tierConfig(scoreResult.tier);
  const TierIcon = cfg.icon;
  const sorted = [...scoreResult.domainSubScores].sort((a, b) => a.rawScore - b.rawScore);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full max-w-md overflow-y-auto p-0" data-testid="sheet-score-breakdown">
        {/* Header gradient */}
        <div className={`bg-gradient-to-br ${cfg.gradient} px-6 pt-6 pb-5`}>
          <SheetHeader>
            <SheetTitle className="text-white text-xl font-bold flex items-center gap-2" data-testid="sheet-score-title">
              <TierIcon className="w-5 h-5" />
              {t("operations:scoreBreakdown.title")}
            </SheetTitle>
            <SheetDescription className="text-white/80 text-sm">
              {scoreResult.encouragement}
            </SheetDescription>
          </SheetHeader>
          <div className="flex items-center gap-4 mt-4">
            <div className="text-center">
              <div className="text-5xl font-black text-white drop-shadow" data-testid="sheet-score-number">{scoreResult.score}</div>
              <div className="text-xs text-white/70 mt-0.5">{t("operations:scoreBreakdown.outOf100")}</div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="bg-white/20 text-white text-sm font-bold px-3 py-1 rounded-full" data-testid="sheet-score-grade">{scoreResult.grade}</span>
              <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full" data-testid="sheet-score-tier">{t(`operations:scoreTier.${scoreResult.tier}`, scoreResult.tier)}</span>
            </div>
          </div>
          {scoreResult.tierGap && (
            <p className="text-white/80 text-xs mt-3" data-testid="sheet-score-tier-gap">
              🎯 <Trans t={t} i18nKey="operations:scoreBreakdown.pointsToReach" values={{ points: scoreResult.tierGap.pointsNeeded, tier: scoreResult.tierGap.nextTier }} components={{ strong: <strong /> }} />
            </p>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* Domain sub-scores */}
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
              {t("operations:scoreBreakdown.domainBreakdown")}
            </h3>
            <div className="space-y-3" data-testid="score-domain-list">
              {sorted.map((d) => (
                <div key={d.domain} data-testid={`score-domain-${d.domain.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-foreground/80">{d.domain}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{t("operations:scoreBreakdown.weight", { pct: Math.round(d.weight * 100) })}</span>
                      <span className="text-xs font-bold tabular-nums">{Math.round(d.rawScore)}/100</span>
                    </div>
                  </div>
                  <Progress value={d.rawScore} className="h-2" />
                  {d.tip && (
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{d.tip}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Top improvement tips */}
          {scoreResult.topImprovementTips.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                {t("operations:scoreBreakdown.topActions")}
              </h3>
              <div className="space-y-2" data-testid="score-tips-list">
                {scoreResult.topImprovementTips.map((tip, i) => (
                  <div key={i} className="flex gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800" data-testid={`score-tip-${i}`}>
                    <span className="shrink-0 w-5 h-5 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center font-bold">{i + 1}</span>
                    <p className="text-xs text-foreground/80 leading-relaxed">{tip}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DealerScoreBanner({
  assessment,
  onRegenerate,
  isRegenerating,
  onOpenBreakdown,
  onOpenGaps,
  isBackgroundRefreshing,
}: {
  assessment: AssessmentData;
  onRegenerate: () => void;
  isRegenerating: boolean;
  onOpenBreakdown: () => void;
  onOpenGaps?: () => void;
  isBackgroundRefreshing?: boolean;
}) {
  const { t } = useTranslation(["operations", "common"]);
  const score = assessment.dealerScore;
  const tier = assessment.dealerScoreTier as ScoreTier | null;
  const scoreResult = assessment.dealerScoreResult;
  const hasScore = score != null;
  const cfg = tierConfig(tier);
  const TierIcon = cfg.icon;

  const displayScore = useCountUp(hasScore ? score : 0, 1000);
  const grade = scoreResult?.grade ?? "—";
  const hasData = assessment.domains.some((d) => d.confidence > 0);

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border-2 ${hasScore ? cfg.ring : "border-border"} ${hasScore ? cfg.bg : "bg-card"} p-5 transition-all`}
      data-testid="banner-dealer-score"
    >
      <div className="flex items-start justify-between gap-4">
        {/* Score display */}
        <div className="flex items-center gap-5">
          {/* Circular score badge */}
          <button
            type="button"
            onClick={hasScore ? onOpenBreakdown : undefined}
            className={`relative shrink-0 w-20 h-20 rounded-full flex flex-col items-center justify-center border-4 ${hasScore ? cfg.ring : "border-border"} bg-background shadow-inner transition-transform hover:scale-105 ${hasScore ? "cursor-pointer" : "cursor-default"}`}
            aria-label={t("operations:scoreBanner.viewBreakdown")}
            data-testid="btn-score-circle"
          >
            <span className={`text-2xl font-black tabular-nums ${hasScore ? cfg.text : "text-muted-foreground"}`} data-testid="text-score-number">
              {hasScore ? displayScore : "—"}
            </span>
            {hasScore && <span className={`text-xs font-bold ${cfg.text}`} data-testid="text-score-grade">{grade}</span>}
          </button>

          <div>
            <div className="flex items-center gap-2">
              {hasScore && <TierIcon className={`w-4 h-4 ${cfg.text}`} />}
              <span className={`text-lg font-bold ${hasScore ? cfg.text : "text-foreground"}`} data-testid="text-score-tier">
                {hasScore ? t(`operations:scoreTier.${tier}`, tier as string) : t("operations:scoreBanner.noScoreYet")}
              </span>
              {isBackgroundRefreshing && (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400" data-testid="badge-score-refreshing">
                  <Loader2 className="w-3 h-3 animate-spin" />{t("operations:scoreBanner.recalculating")}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {hasScore
                ? t("operations:scoreBanner.dealerScoreValue", { score })
                : t("operations:scoreBanner.generatePrompt")
              }
            </p>
            {scoreResult?.tierGap && (
              <p className={`text-xs font-medium mt-1 ${cfg.text}`} data-testid="text-score-tier-gap">
                {t("operations:scoreBanner.ptsTo", { points: scoreResult.tierGap.pointsNeeded, tier: scoreResult.tierGap.nextTier })}
              </p>
            )}
            {hasScore && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="text-confidence-subnote">
                {t("operations:domain.confidence", { pct: assessment.overallConfidence })} · {assessment.overallConfidence >= 70 ? t("operations:domain.confidenceHigh") : assessment.overallConfidence >= 40 ? t("operations:domain.confidenceMedium") : t("operations:domain.confidenceLow")}
                {assessment.dataGaps.length > 0 && onOpenGaps && (
                  <>
                    {" · "}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpenGaps(); }}
                      className="underline underline-offset-2 hover:text-foreground transition-colors"
                      data-testid="btn-confidence-gaps-link"
                    >
                      {t("operations:domain.dataGapCount", { count: assessment.dataGaps.length })}
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    variant={hasScore ? "outline" : "default"}
                    onClick={onRegenerate}
                    disabled={isRegenerating || !hasData}
                    data-testid="button-regenerate-score"
                  >
                    {isRegenerating
                      ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{t("operations:scoreBanner.scoring")}</>
                      : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" />{t("operations:scoreBanner.generateNew")}</>
                    }
                  </Button>
                </span>
              </TooltipTrigger>
              {!hasData && (
                <TooltipContent side="left" className="max-w-[180px] text-xs">
                  {t("operations:scoreBanner.unlockTooltip")}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          {hasScore && (
            <Button
              size="sm"
              variant="ghost"
              className={`text-xs ${cfg.text} hover:${cfg.bg}`}
              onClick={onOpenBreakdown}
              data-testid="button-improve-score"
            >
              <ArrowUpRight className="w-3.5 h-3.5 mr-1" />
              {t("operations:scoreBanner.improveScore")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function AssessmentContent({
  assessment,
  onRegenerateScore,
  isRegeneratingScore,
  onRegenerate,
  isRegenerating,
  isBackgroundRefreshing,
}: {
  assessment: AssessmentData;
  onRegenerateScore: () => void;
  isRegeneratingScore: boolean;
  onRegenerate: () => void;
  isRegenerating: boolean;
  isBackgroundRefreshing?: boolean;
}) {
  const [overallGapDialogOpen, setOverallGapDialogOpen] = useState(false);
  const [tileGapIndex, setTileGapIndex] = useState<number | null>(null);
  const [sheetIndex, setSheetIndex] = useState<number | null>(null);
  const [scoreDrawerOpen, setScoreDrawerOpen] = useState(false);
  const { t } = useTranslation(["operations", "common"]);
  const { setActiveTab } = useContext(OperationsCtx);
  const [, navigate] = useLocation();
  const hasOverallGaps = assessment.dataGaps.length > 0;
  const tileGapFinding = tileGapIndex !== null ? assessment.domains[tileGapIndex] : null;

  const handleNavigate = useCallback((index: number) => {
    setSheetIndex(index);
  }, []);

  return (
    <>
      <DataGapsDialog
        open={overallGapDialogOpen}
        onClose={() => setOverallGapDialogOpen(false)}
        title={t("operations:assessment.overallAssessment")}
        gaps={assessment.dataGaps}
        onGapAction={(gap) => { if (gap.route.startsWith("/operations")) { setActiveTab(resolveGapTab(gap.route)); } else { setOverallGapDialogOpen(false); navigate(gap.route); } }}
      />

      {tileGapFinding && (
        <DataGapsDialog
          open={tileGapIndex !== null}
          onClose={() => setTileGapIndex(null)}
          title={tileGapFinding.domain}
          gaps={tileGapFinding.dataGaps}
          trend={tileGapFinding.trend}
          onGapAction={(gap) => { setTileGapIndex(null); if (gap.route.startsWith("/operations")) { setActiveTab(resolveGapTab(gap.route)); } else { navigate(gap.route); } }}
        />
      )}

      <DomainDetailSheet
        domains={assessment.domains}
        activeIndex={sheetIndex}
        onClose={() => setSheetIndex(null)}
        onNavigate={handleNavigate}
        onRegenerate={onRegenerate}
        isRegenerating={isRegenerating}
      />

      <ScoreBreakdownDrawer
        open={scoreDrawerOpen}
        onClose={() => setScoreDrawerOpen(false)}
        scoreResult={assessment.dealerScoreResult}
      />

      {/* Dealer Score Banner — prominent at top */}
      <DealerScoreBanner
        assessment={assessment}
        onRegenerate={onRegenerateScore}
        isRegenerating={isRegeneratingScore}
        onOpenBreakdown={() => setScoreDrawerOpen(true)}
        onOpenGaps={hasOverallGaps ? () => setOverallGapDialogOpen(true) : undefined}
        isBackgroundRefreshing={isBackgroundRefreshing}
      />

      {/* Domain tile grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="grid-domain-tiles">
        {assessment.domains.map((domain, i) => (
          <DomainTile
            key={domain.domain}
            finding={domain}
            index={i}
            onClick={() => setSheetIndex(i)}
            onGapHintClick={domain.trend === "Stable" && domain.dataGaps.length > 0 ? () => setTileGapIndex(i) : undefined}
          />
        ))}
      </div>
    </>
  );
}

function AssessmentTab() {
  const { t } = useTranslation(["operations", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: assessment, isLoading } = useQuery<AssessmentData | null>({
    queryKey: ["/api/assessment/summary"],
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: refreshStatus } = useQuery<{ refreshing: boolean; state?: string }>({
    queryKey: ["/api/assessment/refresh-status"],
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.refreshing ? 5_000 : false;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const isBackgroundRefreshing = refreshStatus?.refreshing ?? false;

  const prevRefreshingRef = useRef(false);
  useEffect(() => {
    if (prevRefreshingRef.current && !isBackgroundRefreshing) {
      qc.invalidateQueries({ queryKey: ["/api/assessment/summary"] });
    }
    prevRefreshingRef.current = isBackgroundRefreshing;
  }, [isBackgroundRefreshing, qc]);

  const generateMutation = useMutation({
    // The server now runs the 60–90s Claude generation on a background worker and
    // returns 202 immediately, so this request resolves in milliseconds. Progress
    // is tracked via the /assessment/refresh-status poll below, which flips
    // isBackgroundRefreshing and re-fetches the summary on completion.
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/assessment/generate");
      return res.json() as Promise<{ queued: boolean; message?: string }>;
    },
    onSuccess: () => {
      // Kick the status poll immediately so the button shows "Generating…" right away.
      prevRefreshingRef.current = true;
      qc.invalidateQueries({ queryKey: ["/api/assessment/refresh-status"] });
      toast({ title: t("operations:assessment.toastRefreshStartedTitle"), description: t("operations:assessment.toastRefreshStartedDesc"), duration: 4000 });
    },
    onError: (err: any) => {
      toast({ title: t("operations:assessment.toastRefreshErrorTitle"), description: err.message || t("operations:assessment.toastRefreshErrorDesc"), variant: "destructive" });
    },
  });

  // While the manual button POST is in flight OR the background worker is running,
  // the assessment is "being generated" from the user's perspective.
  const isGenerating = generateMutation.isPending || isBackgroundRefreshing;

  const scoreMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/assessment/score");
      return res.json() as Promise<DealerScoreResult>;
    },
    onSuccess: (scoreResult: DealerScoreResult) => {
      const prev = qc.getQueryData<AssessmentData | null>(["/api/assessment/summary"]);
      if (prev) {
        qc.setQueryData(["/api/assessment/summary"], {
          ...prev,
          dealerScore: scoreResult.score,
          dealerScoreTier: scoreResult.tier,
          dealerScoreResult: scoreResult,
        });
      }
      toast({ title: t("operations:assessment.toastScoreUpdatedTitle"), description: t("operations:assessment.toastScoreUpdatedDesc", { score: scoreResult.score, tier: scoreResult.tier }), duration: 4000 });
    },
    onError: (err: any) => {
      toast({ title: t("operations:assessment.toastScoreFailedTitle"), description: err.message || t("operations:assessment.toastScoreFailedDesc"), variant: "destructive" });
    },
  });

  // Auto-compute score when assessment loads but has no score yet
  const assessmentKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!assessment) return;
    const key = assessment.generatedAt ?? "loaded";
    if (assessment.dealerScore == null && assessment.domains.some((d) => d.confidence > 0) && assessmentKeyRef.current !== key) {
      assessmentKeyRef.current = key;
      scoreMutation.mutate();
    }
  }, [assessment]);

  const formattedDate = assessment?.generatedAt
    ? fmtDateTime(assessment.generatedAt)
    : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("operations:assessment.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            {t("operations:assessment.heading")}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t("operations:assessment.subheading")}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {formattedDate && (
            <span className="text-xs text-muted-foreground" data-testid="text-last-updated">
              {t("operations:assessment.updated", { date: formattedDate })}
            </span>
          )}
          <Button
            size="sm"
            variant={assessment ? "outline" : "default"}
            onClick={() => generateMutation.mutate()}
            disabled={isGenerating}
            data-testid="button-refresh-assessment"
          >
            {isGenerating
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{t("operations:assessment.generating")}</>
              : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" />{assessment ? t("operations:assessment.refreshAnalysis") : t("operations:assessment.generateAssessment")}</>
            }
          </Button>
        </div>
      </div>

      {/* No assessment yet */}
      {!assessment && !isGenerating && !isLoading && (
        <Card className="border-dashed" data-testid="card-no-assessment">
          <CardContent className="py-12 text-center">
            <ShieldCheck className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-semibold mb-1">{t("operations:assessment.emptyTitle")}</h3>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-4">
              {t("operations:assessment.emptyBody")}
            </p>
            <Button onClick={() => generateMutation.mutate()} disabled={isGenerating} data-testid="button-generate-first">
              {isGenerating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("operations:assessment.generating")}</> : t("operations:assessment.generateAssessment")}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Generating spinner */}
      {isGenerating && !assessment && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">{t("operations:assessment.analyzing")}</p>
            <p className="text-xs text-muted-foreground mt-1">{t("operations:assessment.underMinute")}</p>
          </CardContent>
        </Card>
      )}

      {assessment && (
        <AssessmentContent
          assessment={assessment}
          onRegenerateScore={() => scoreMutation.mutate()}
          isRegeneratingScore={scoreMutation.isPending}
          onRegenerate={() => generateMutation.mutate()}
          isRegenerating={isGenerating}
          isBackgroundRefreshing={isBackgroundRefreshing}
        />
      )}
    </div>
  );
}

// ─── Analytics Tab ─────────────────────────────────────────────────────────────

function AnalyticsTab() {
  const { t } = useTranslation(["operations", "common"]);
  const recoveryUnavailable = useSyncExternalStore(
    taxReturnSaveRequest.subscribe,
    taxReturnSaveRequest.getRecoveryUnavailable,
    taxReturnSaveRequest.getRecoveryUnavailable,
  );
  const { toast } = useToast();
  const { setIsOpen: openDean } = useDean();
  const qc = useQueryClient();
  const {
    annualUnitsSold, setAnnualUnitsSold,
    manualRevenue, setManualRevenue,
    manualGrossProfit, setManualGrossProfit,
    manualTotalIncome, setManualTotalIncome,
    manualOpEx, setManualOpEx,
    dynamicExpenseItems, setDynamicExpenseItems,
    loadedSavedReturn, setLoadedSavedReturn,
    opAnalyticsFetching, setIsExtractionPending,
  } = useContext(OperationsLiveCtx);

  // Cost Recovery Calculator input
  const [incurredCost, setIncurredCost] = useState("");

  // P&L Manual Entry — expense breakdown (dynamic, from Claude's tax-return extraction)
  const [showExpenseDetail, setShowExpenseDetail] = useState(false);

  // Required inputs
  const [avgInventoryUnits, setAvgInventoryUnits] = useState("");
  const [avgTurnDaysOverride, setAvgTurnDaysOverride] = useState("");

  const { data: latest, isFetching: latestFetching } = useOpAnalytics();
  const { data: currentUser } = useQuery<{ id: string; ownerUserId?: string | null; impersonatingUserId?: string | null } | null>({ queryKey: ["/api/auth/me"] });

  const [computing, setComputing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [computeError, setComputeError] = useState("");
  const [savedReturnPreview, setSavedReturnPreview] = useState<any>(null);

  const [isLocked, setIsLocked] = useState(false);
  const [inputsSnapshot, setInputsSnapshot] = useState<{ avgInventoryUnits: string; annualUnitsSold: string; avgTurnDaysOverride: string } | null>(null);
  // Mirrors "the dealer is actively editing the inputs panel" (snapshot taken
  // on Edit click) so async effects can check it without stale closures.
  const editingInputsRef = useRef(false);
  editingInputsRef.current = inputsSnapshot !== null;
  const [holdingDays, setHoldingDays] = useState("60");
  const [isEditingTaxReturn, setIsEditingTaxReturn] = useState(false);
  const [taxReturnSnapshot, setTaxReturnSnapshot] = useState<{
    annualUnitsSold: string;
    manualRevenue: string;
    manualGrossProfit: string;
    manualOpEx: string;
    dynamicExpenseItems: { label: string; amount: number }[];
    taxReturnSourceFields: string[];
  } | null>(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [taxResumeResolved, setTaxResumeResolved] = useState(false);
  useEffect(() => { setIsExtractionPending(pdfUploading); }, [pdfUploading, setIsExtractionPending]);
  const [extractionProgressText, setExtractionProgressText] = useState<string | null>(null);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [noFormsDialogPending, setNoFormsDialogPending] = useState<Record<string, any> | null>(null);
  const [docDeleting, setDocDeleting] = useState(false);
  const [extractionSource, setExtractionSource] = useState<string | null>(null);
  // Tracks which local state fields were populated from the last tax return upload (provenance)
  const [taxReturnSourceFields, setTaxReturnSourceFields] = useState<string[]>([]);
  // Stores the original AI-extracted values so the user can revert after manual edits
  const [extractedOtherIncome, setExtractedOtherIncome] = useState<number | null>(null);
  const [rentalData, setRentalData] = useState<{ rentalGrossIncome: number | null; rentalTotalExpenses: number | null; rentalNetIncome: number | null } | null>(null);
  const [aiExtractedValues, setAiExtractedValues] = useState<{
    revenue?: string;
    grossProfit?: string;
    opEx?: string;
  }>({});
  // Persistent warnings from the last extraction (large doc, skipped pages)
  const [extractionBannerWarnings, setExtractionBannerWarnings] = useState<string[]>([]);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const autoRestoreAttempted = useRef(false);

  // Inline "year not detected" notice input
  const [pendingTaxYear, setPendingTaxYear] = useState("");
  const [savingTaxYear, setSavingTaxYear] = useState(false);

  // Saved tax return state
  const [showPastStatements, setShowPastStatements] = useState(false);
  const [isPendingNewReturn, setIsPendingNewReturn] = useState(false);
  const [showSaveReturnDialog, setShowSaveReturnDialog] = useState(false);
  const saveReturnNameRef = useRef<HTMLInputElement>(null);
  const saveReturnYearRef = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (!showSaveReturnDialog) return;
    const timer = setTimeout(() => {
      if (saveTaxReturnYear) {
        saveReturnNameRef.current?.focus();
      } else {
        saveReturnYearRef.current?.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [showSaveReturnDialog]);
  const [showAddReturnChoiceDialog, setShowAddReturnChoiceDialog] = useState(false);
  const [showManualEntryDialog, setShowManualEntryDialog] = useState(false);
  const [manualEntryYear, setManualEntryYear] = useState("");
  const [manualEntryRevenue, setManualEntryRevenue] = useState("");
  const [manualEntryGrossProfit, setManualEntryGrossProfit] = useState("");
  const [manualEntryTotalExpenses, setManualEntryTotalExpenses] = useState("");
  const [compareMode, setCompareMode] = useState(false);
  const [selectedCompareIds, setSelectedCompareIds] = useState<string[]>([]);
  const [editingReturnId, setEditingReturnId] = useState<string | null>(null);
  const [editingReturnName, setEditingReturnName] = useState("");
  const [editingReturnYear, setEditingReturnYear] = useState("");
  const [deletingReturnId, setDeletingReturnId] = useState<string | null>(null);
  const [showCompareView, setShowCompareView] = useState(false);
  const [trendsMode, setTrendsMode] = useState(false);
  const [showTrendsDialog, setShowTrendsDialog] = useState(false);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [inlineCompareIdA, setInlineCompareIdA] = useState<string>("");
  const [inlineCompareIdB, setInlineCompareIdB] = useState<string>("");
  const [trendYearFrom, setTrendYearFrom] = useState<number | null>(null);
  const [trendYearTo, setTrendYearTo] = useState<number | null>(null);
  const [visibleTrendMetrics, setVisibleTrendMetrics] = useState<{ revenue: boolean; grossProfit: boolean; noi: boolean; rentalGross: boolean; rentalExpenses: boolean; rentalNet: boolean }>(() => {
    try {
      const stored = sessionStorage.getItem("trendMetricToggles");
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          revenue: parsed.revenue !== false,
          grossProfit: parsed.grossProfit !== false,
          noi: parsed.noi !== false,
          rentalGross: parsed.rentalGross === true,
          rentalExpenses: parsed.rentalExpenses === true,
          rentalNet: parsed.rentalNet !== false,
        };
      }
    } catch {}
    return { revenue: true, grossProfit: true, noi: true, rentalGross: false, rentalExpenses: false, rentalNet: true };
  });

  const updateTrendMetrics = (updater: (prev: { revenue: boolean; grossProfit: boolean; noi: boolean; rentalGross: boolean; rentalExpenses: boolean; rentalNet: boolean }) => { revenue: boolean; grossProfit: boolean; noi: boolean; rentalGross: boolean; rentalExpenses: boolean; rentalNet: boolean }) => {
    setVisibleTrendMetrics(prev => {
      const next = updater(prev);
      try { sessionStorage.setItem("trendMetricToggles", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Refs for mutable polling state shared between handlePdfUpload and resume-on-mount logic
  const pollCancelledRef = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartedAtRef = useRef<number>(Date.now());
  const pollLastProgressTokenRef = useRef<string | null>(null);
  // Tracks the job ID currently being polled — used by cleanupPolling to record the
  // dismissed job even when the localStorage active-job entry is already absent
  // (e.g. job was resumed via the /active server fallback and localStorage was empty).
  const currentPollJobIdRef = useRef<string | null>(null);
  // Counts consecutive network/fetch errors during polling — allows a few retries
  // before giving up, so a brief server restart does not kill an in-flight extraction.
  const pollNetworkRetryCountRef = useRef(0);
  const MAX_POLL_NETWORK_RETRIES = 4;

  // localStorage key scoped to the authenticated user to avoid cross-account bleed
  const taxJobStorageKey = currentUser?.id ? `taxReturnJobId:${currentUser.id}` : null;
  // Tracks jobs dismissed via the X button so resume logic never reconnects to them
  const taxDismissedJobKey = currentUser?.id ? `taxReturnDismissedJob:${currentUser.id}` : null;
  const sourceScope = taxReturnDraftScope(currentUser);
  const completedSourceRef = useRef(new TaxReturnSource(sourceScope));
  const previousSourceScopeRef = useRef(sourceScope);
  if (completedSourceRef.current.scope !== sourceScope) {
    completedSourceRef.current = new TaxReturnSource(sourceScope);
    if (previousSourceScopeRef.current) completedSourceRef.current.clear();
    if (sourceScope) previousSourceScopeRef.current = sourceScope;
  }
  const displayedTaxDocument = sourceScope && !loadedSavedReturn
    ? completedSourceRef.current.document(latest === undefined ? undefined : latest?.input?.taxReturnDoc ?? null) : null;
  const {
    name: saveTaxReturnName, year: saveTaxReturnYear,
    setName: setSaveTaxReturnName, setYear: setSaveTaxReturnYear, discard: discardTaxReturnDraft,
  } = useTaxReturnDraft(taxReturnDraftKey(sourceScope, displayedTaxDocument),
    latest?.input?.taxYear ? String(latest.input.taxYear) : "", sourceScope);
  const uploadGenerationRef = useRef(0);

  useEffect(() => {
    // Auth can change without unmounting this tab. Never leave the previous
    // actor's completion dialog, filename or progress visible in the new scope.
    setPdfUploading(false);
    setExtractionSource(null);
    setNoFormsDialogPending(null);
    setShowSaveReturnDialog(false);
    currentPollJobIdRef.current = null;
  }, [sourceScope]);

  function clearCompletedSource() {
    discardTaxReturnDraft();
    completedSourceRef.current.clear();
    uploadGenerationRef.current++;
    stopPolling();
    setPdfUploading(false);
    setExtractionProgressText(null);
    setExtractionError(null);
    currentPollJobIdRef.current = null;
    if (taxJobStorageKey) { try { localStorage.removeItem(taxJobStorageKey); } catch {} }
    setNoFormsDialogPending(null);
  }

  async function confirmCompletedSource(source: TaxReturnSource) {
    const generation = uploadGenerationRef.current;
    const stale = () => source !== completedSourceRef.current || generation !== uploadGenerationRef.current;
    // Do not deduplicate against an analytics request that started before job
    // completion. Its old document must not detach the completed source, nor
    // overwrite the fresh response after this read.
    await qc.cancelQueries({ queryKey: ["/api/op-analytics/latest"], exact: true });
    if (stale()) return;
    try {
      const fresh = await qc.fetchQuery<any>({ queryKey: ["/api/op-analytics/latest"], staleTime: 0 });
      if (!stale()) source.confirmDocument(fresh?.input?.taxReturnDoc ?? null);
    } catch {
      // A failed analytics read cannot revoke positive completed-job evidence.
      // Keep the source pending confirmation; saving still validates it server-side.
    }
  }

  // Wrapper that removes a field from taxReturnSourceFields when the user manually edits it.
  // This ensures delete only clears values that are still tax-return-sourced, not manual edits.
  const makeManualSetter = (
    setter: (v: string) => void,
    provenanceKeys: string[],
  ) => (value: string) => {
    setter(value);
    setTaxReturnSourceFields((prev) => prev.filter((k) => !provenanceKeys.includes(k)));
  };

  const setManualRevenueUser = makeManualSetter(setManualRevenue, ["revenue"]);
  const setManualGrossProfitUser = makeManualSetter(setManualGrossProfit, ["grossProfit"]);
  const setManualOpExUser = makeManualSetter(setManualOpEx, ["annualTotalOperatingExpenses", "annualTotalExpenses"]);

  const { data: profile } = useQuery<any>({ queryKey: ["/api/dealer-profile"] });

  const [inputsExpanded, setInputsExpanded] = useState(false);

  const { data: statements = [] } = useQuery<any[]>({ queryKey: ["/api/parsed-statements"] });
  const { data: lenders = [] } = useQuery<any[]>({ queryKey: ["/api/lenders"] });
  const { data: savedTaxReturns = [], refetch: refetchSavedTaxReturns, isSuccess: savedReturnsReady } = useQuery<any[]>({ queryKey: ["/api/saved-tax-returns"] });

  // An unsaved completed upload must remain saveable after leaving this tab or
  // reloading, when neither the polling handle nor the upload UI state exists.
  useEffect(() => {
    if (!displayedTaxDocument || loadedSavedReturn || pdfUploading || !savedReturnsReady) return;
    const alreadySaved = savedTaxReturns.some(entry =>
      (displayedTaxDocument.sourceJobId && entry.sourceJobId === displayedTaxDocument.sourceJobId) ||
      (displayedTaxDocument.cloudPath && entry.cloudPath === displayedTaxDocument.cloudPath));
    if (!alreadySaved) {
      setIsPendingNewReturn(true);
    }
  }, [displayedTaxDocument, loadedSavedReturn, pdfUploading, savedReturnsReady, savedTaxReturns, latest]);

  const renameTaxReturnMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiRequest("PATCH", `/api/saved-tax-returns/${id}`, { name }),
    onSuccess: (_: any, { id, name }: { id: string; name: string }) => {
      refetchSavedTaxReturns();
      setEditingReturnId(null);
      setEditingReturnYear("");
      setEditingReturnName("");
      setLoadedSavedReturn(prev => prev?.id === id ? { ...prev, name } : prev);
    },
    onError: () => toast({ title: t("operations:analytics.toastRenameFailed"), variant: "destructive" }),
  });

  const deleteTaxReturnMutation = useMutation({
    mutationFn: async (id: string) => {
      const isLoaded = loadedSavedReturn?.id === id;
      const url = isLoaded
        ? `/api/saved-tax-returns/${id}?clearAnalyticsFields=true`
        : `/api/saved-tax-returns/${id}`;
      const res = await apiRequest("DELETE", url);
      return res.json() as Promise<{ ok: boolean; docCleared: boolean; clearedFields: string[] }>;
    },
    onSuccess: async (res: any, id: string) => {
      const deletedReturn = savedTaxReturns.find((r: any) => r.id === id);
      const wasLoaded = loadedSavedReturn?.id === id;
      refetchSavedTaxReturns();
      setDeletingReturnId(null);
      setLoadedSavedReturn(prev => {
        if (prev?.id === id) {
          setManualRevenue("");
          setManualGrossProfit("");
          setManualOpEx("");
          setManualTotalIncome("");
          setExtractedOtherIncome(null);
          setAnnualUnitsSold("");
          setAvgInventoryUnits("");
          setAvgTurnDaysOverride("");
          setDynamicExpenseItems([]);
          return null;
        }
        return prev;
      });
      if (currentUser?.id) {
        const key = `lastTaxReturnId:${currentUser.ownerUserId ?? currentUser.id}`;
        if (localStorage.getItem(key) === id) localStorage.removeItem(key);
      }

      const shouldClearDoc =
        deletedReturn?.cloudPath &&
        latest?.input?.taxReturnDoc?.cloudPath === deletedReturn.cloudPath;

      if (wasLoaded || shouldClearDoc) clearCompletedSource();
      if (shouldClearDoc) {
        if (res?.docCleared) {
          // The server already cleared the document reference atomically as part of
          // the delete. Update local state and refresh the cache to reflect this.
          const cleared: string[] = res.clearedFields ?? [];
          if (cleared.includes("revenue")) setManualRevenue("");
          if (cleared.includes("grossProfit")) setManualGrossProfit("");
          setManualTotalIncome("");
          setExtractedOtherIncome(null);
          if (cleared.includes("annualTotalOperatingExpenses") || cleared.includes("annualTotalExpenses")) setManualOpEx("");
          if (cleared.includes("expenses")) setDynamicExpenseItems([]);
          setExtractionSource(null);
          setIsEditingTaxReturn(false);
          setTaxReturnSourceFields([]);
          qc.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
        } else {
          // Server did not clear the reference (e.g. cloudPath mismatch or cleanup
          // error). Fall back to calling the document-clear endpoint explicitly.
          const docCleared = await handleDeleteTaxReturnDoc({ suppressToastOnFailure: true });
          if (!docCleared) {
            // Last resort: patch the local cache to hide the stale file card so the
            // UI stays consistent. The server will have been retried via the delete
            // endpoint on next page load if the reference still exists.
            qc.setQueryData(["/api/op-analytics/latest"], (old: any) => {
              if (!old) return old;
              return {
                ...old,
                input: old.input
                  ? { ...old.input, taxReturnDoc: null }
                  : old.input,
              };
            });
            setExtractionSource(null);
            setIsEditingTaxReturn(false);
            setTaxReturnSourceFields([]);
            toast({
              title: t("operations:analytics.toastSavedReturnDeleted"),
              description: t("operations:analytics.toastSavedReturnDeletedDesc"),
              variant: "destructive",
            });
          }
        }
      }

      // When the deleted return was the currently loaded one and the cloudPath-match
      // path above did not run, the server cleared all analysis fields via the
      // clearAnalyticsFields flag. Invalidate the cache only when the server confirmed
      // the patch succeeded (docCleared=true) so the UI reflects the blank state
      // immediately and stays blank after a page refresh.
      if (wasLoaded && !shouldClearDoc && res?.docCleared) {
        qc.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
      }
    },
    onError: () => toast({ title: t("operations:analytics.toastDeleteFailed"), variant: "destructive" }),
  });

  const saveTaxReturnMutation = useMutation({
    mutationFn: async (payload: { name: string; fileName: string; cloudPath: string | null; snapshotJson: string; sourceJobId?: string | null }) => {
      const scope = taxReturnSaveScope(currentUser);
      const saveRequestKey = await taxReturnSaveRequest.keyFor(scope, payload);
      const res = await apiRequest("POST", "/api/saved-tax-returns", { ...payload, saveRequestKey });
      const savedItem = await res.json();
      taxReturnSaveRequest.complete(scope, saveRequestKey);
      return { savedItem, scope };
    },
    onSuccess: ({ savedItem, scope }, variables) => {
      if (!taxReturnSaveRequest.isCurrent(scope)) return;
      // Immediately write the new item into the local cache so the modal
      // opens with it visible — no async refetch needed.
      qc.setQueryData(["/api/saved-tax-returns"], (old: any[] = []) => [savedItem, ...old.filter(item => item.id !== savedItem.id)]);
      setShowSaveReturnDialog(false);
      discardTaxReturnDraft();
      setIsEditingTaxReturn(false);
      setIsPendingNewReturn(false);
      // Mark as the active saved return — this hides the name input panel
      setLoadedSavedReturn(openSavedTaxReturn(savedItem));
      if (currentUser?.id) localStorage.setItem(`lastTaxReturnId:${currentUser.ownerUserId ?? currentUser.id}`, savedItem.id);
      setShowPastStatements(true);
      toast({ title: t("operations:analytics.toastReturnSaved"), description: t("operations:analytics.toastReturnSavedDesc", { name: variables.name }), duration: 3000 });
      // Background sync to keep server & cache consistent
      qc.invalidateQueries({ queryKey: ["/api/saved-tax-returns"] });
      qc.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
      // A new saved return becomes the most-recent one, so the enriched
      // inventory cost-to-carry (computed live from the latest tax return)
      // must be refreshed.
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
    },
    onError: (e: any) => {
      console.error("[SaveTaxReturn] error:", e);
      toast({ title: t("operations:analytics.toastSaveFailed"), description: e.message ?? String(e), variant: "destructive" });
    },
  });

  const linkedStatements = useMemo(
    () => filterStatementsToLenders(statements, lenders),
    [statements, lenders]
  );

  const autoStats = useMemo(() => computeFloorplanStats(linkedStatements), [linkedStatements]);
  const autoAvgTurnDays = autoStats.avgTurnDays;

  const avgTurnDays = nf(avgTurnDaysOverride) > 0
    ? nf(avgTurnDaysOverride)
    : (autoAvgTurnDays ?? 45);

  const allReceivableUnits = useMemo(() => {
    const units: Array<{ vin?: string; stock?: string; daysOutstanding: number; principal?: number }> = [];
    for (const s of linkedStatements) {
      try {
        const recvs = JSON.parse(s.receivablesJson || "[]");
        for (const r of recvs) {
          const days = r.daysOutstanding ?? r.daysFloored ?? 0;
          if (days <= 0) continue;
          units.push({ vin: r.vin, stock: r.stock, daysOutstanding: days, principal: r.principal });
        }
      } catch {}
    }
    return units;
  }, [linkedStatements]);

  const autoAgedUnits = useMemo(
    () => allReceivableUnits.filter(u => u.daysOutstanding > avgTurnDays),
    [allReceivableUnits, avgTurnDays]
  );
  const autoAgedDays = useMemo(
    () => autoAgedUnits.map(u => u.daysOutstanding),
    [autoAgedUnits]
  );

  // Operating Expenses entered directly; derive Net Profit = Total Income − OpEx
  // When totalIncome is available (from tax extraction) it includes other_income,
  // net_gain_4797, dividends, etc. on top of gross_profit, giving the correct
  // IRS ordinary-business-income figure. Falls back to grossProfit when not set.
  const derivedOpEx = manualOpEx !== "" ? nf(manualOpEx) : 0;
  const derivedNetProfit = (() => {
    // Use totalIncome when it was set from extraction (presence check, not > 0,
    // so that zero or negative totalIncome is handled correctly).
    // base may be zero or negative (e.g. a loss year) — still compute net profit.
    const totalInc = nf(manualTotalIncome);
    const gp = nf(manualGrossProfit);
    const base = manualTotalIncome !== "" ? totalInc : gp;
    const hasBase = manualTotalIncome !== "" ? true : gp > 0;
    return hasBase && manualOpEx !== "" ? base - derivedOpEx : null;
  })();

  // Whether the tax-return analysis area should be unlocked. It stays locked
  // (behind an "upload a tax return" prompt) until the dealer actually has tax
  // return data — either persisted, loaded, being entered/uploaded now, or
  // present in-session. This nudges dealers to upload their return.
  const taxReturnUnlocked =
    isEditingTaxReturn ||
    isPendingNewReturn ||
    pdfUploading ||
    !!loadedSavedReturn ||
    manualRevenue !== "" ||
    manualGrossProfit !== "" ||
    manualOpEx !== "" ||
    !!(latest?.input && (
      latest.input.revenue != null ||
      latest.input.grossProfit != null ||
      latest.input.annualTotalOperatingExpenses != null ||
      latest.input.taxReturnDoc != null
    ));

  // Build financials object from manual entry
  const activeFinancials: ExtractedFinancials | null = useMemo(() => {
    const rev = nf(manualRevenue);
    const gp = nf(manualGrossProfit);
    const opex = derivedOpEx;
    if (rev === 0 && gp === 0 && opex === 0) return null;
    return {
      revenue: rev > 0 ? rev : undefined,
      cogs: rev > 0 && gp > 0 ? rev - gp : undefined,
      grossProfit: gp > 0 ? gp : undefined,
      totalOperatingExpenses: opex > 0 ? opex : undefined,
      expenses: { dynamicItems: dynamicExpenseItems },
      warnings: [],
    };
  }, [manualRevenue, manualGrossProfit, derivedOpEx, dynamicExpenseItems]);

  const derivedGrossProfit = activeFinancials?.grossProfit
    ?? ((activeFinancials?.revenue ?? 0) > 0 && (activeFinancials?.cogs ?? 0) > 0
      ? (activeFinancials!.revenue! - activeFinancials!.cogs!)
      : undefined);

  const derivedAvgGross =
    (derivedGrossProfit != null && nf(annualUnitsSold) > 0)
      ? derivedGrossProfit / nf(annualUnitsSold)
      : undefined;

  // Restore inputs from saved data
  useEffect(() => {
    const inp = latest?.input;
    if (!inp) return;
    // Don't re-lock the inputs panel while the dealer is actively editing —
    // auto-save/auto-compute refresh `latest` mid-edit and must not close it.
    if (!editingInputsRef.current) setIsLocked(true);
    // Never overwrite the three basic inputs while the dealer is typing in
    // them — compute/auto-save invalidate `latest` mid-edit and a stale
    // response must not clobber a newer keystroke.
    if (!editingInputsRef.current) {
      if (inp.avgInventoryUnits && !avgInventoryUnits) setAvgInventoryUnits(String(inp.avgInventoryUnits));
      // Fill-if-empty only: units sold is also editable from the tax-return
      // card (while the inputs panel is locked), and a `latest` refetch must
      // never clobber a value the dealer just typed there.
      if (inp.annualUnitsSold && !annualUnitsSold && !loadedSavedReturn) setAnnualUnitsSold(String(inp.annualUnitsSold));
      if (inp.avgTurnDaysWasOverride && inp.avgTurnDays && !avgTurnDaysOverride) {
        setAvgTurnDaysOverride(String(inp.avgTurnDays));
      }
      // Seed the auto-save echo guard on the initial restore so filling the
      // fields from the server doesn't immediately POST the same values back.
      // Seed strictly from the SERVER values (never current client state) and
      // never while an edit is pending — otherwise a user edit made before
      // `latest` resolved would be recorded as "already saved" and suppressed.
      if (!autoSaveSeededRef.current && pendingAutoSaveRef.current === null && !lastAutoSavedRef.current) {
        autoSaveSeededRef.current = true;
        lastAutoSavedRef.current = JSON.stringify(buildBasicInputsBody({
          avgInventoryUnits: inp.avgInventoryUnits ? String(inp.avgInventoryUnits) : "",
          annualUnitsSold: inp.annualUnitsSold ? String(inp.annualUnitsSold) : "",
          avgTurnDaysOverride: inp.avgTurnDaysWasOverride && inp.avgTurnDays ? String(inp.avgTurnDays) : "",
        }));
      }
    }
    // Restore Revenue, Gross Profit, Total Income, and OpEx from saved data on initial load
    if (inp.revenue && !manualRevenue) setManualRevenue(String(inp.revenue));
    if (inp.grossProfit && !manualGrossProfit) setManualGrossProfit(String(inp.grossProfit));
    const restoredTotalIncome = resolveRestoredTotalIncome(inp.totalIncome, manualTotalIncome);
    if (restoredTotalIncome !== null) setManualTotalIncome(restoredTotalIncome);
    if (inp.annualTotalOperatingExpenses && !manualOpEx) setManualOpEx(String(inp.annualTotalOperatingExpenses));
    // Restore dynamic expense breakdown from Claude extraction
    if (Array.isArray(inp.expenses?.dynamicItems) && inp.expenses.dynamicItems.length > 0 && dynamicExpenseItems.length === 0) {
      setDynamicExpenseItems(inp.expenses.dynamicItems);
    }
    // Restore rental data from saved input
    if (inp.rental && !rentalData) setRentalData(inp.rental);
    // Restore provenance so subsequent upload/delete knows which fields were tax-return-sourced
    if (inp.taxReturnSourceFields?.length && !taxReturnSourceFields.length) {
      setTaxReturnSourceFields(inp.taxReturnSourceFields);
    }
  }, [latest]);

  // Auto-restore the last viewed saved return on page mount
  useEffect(() => {
    if (autoRestoreAttempted.current) return;
    // Resume wins over the last-viewed saved snapshot. Otherwise automatic
    // restoration calls loadSavedReturn -> clearCompletedSource and destroys
    // the only handle for a job that completed while this tab was unmounted.
    if (!taxResumeResolved || pdfUploading) return;
    if (!currentUser?.id) return;
    if (savedTaxReturns.length === 0) return;
    autoRestoreAttempted.current = true;
    const key = `lastTaxReturnId:${currentUser.ownerUserId ?? currentUser.id}`;
    const lastId = localStorage.getItem(key);
    if (!lastId) return;
    // The provider may already have restored this same return (so the correct
    // year is shown on every tab). In that case we still run loadSavedReturn to
    // populate the Analytics-tab-local fields (rental, other income). Only bail
    // when a *different* return is already loaded so we don't clobber it.
    if (loadedSavedReturn && loadedSavedReturn.id !== lastId) return;
    if (loadedSavedReturn?.editor.blocked) return;
    const entry = savedTaxReturns.find(r => r.id === lastId);
    if (entry) {
      loadSavedReturn(entry);
    } else {
      localStorage.removeItem(key);
    }
  }, [savedTaxReturns, currentUser, taxResumeResolved, pdfUploading]);

  // Sync manualOpEx with the sum of dynamicExpenseItems whenever items change.
  // Skip initial mount so we don't overwrite a saved manualOpEx for users with no items.
  const expItemsInitRef = useRef(false);
  useEffect(() => {
    if (!expItemsInitRef.current) {
      expItemsInitRef.current = true;
      return;
    }
    const sum = dynamicExpenseItems.reduce((acc, it) => acc + (it.amount || 0), 0);
    setManualOpEx(String(Math.round(sum)));
  }, [dynamicExpenseItems]);

  // Auto-populate from dealer profile
  useEffect(() => {
    if (!profile) return;
    const profileInventory = profile.avgUnitsOnLot != null ? parseFloat(String(profile.avgUnitsOnLot)) : null;
    const profileMonthly = profile.avgSalesPerMonth != null ? parseFloat(String(profile.avgSalesPerMonth)) : null;
    const profileAnnual = profileMonthly != null && profileMonthly > 0 ? profileMonthly * 12 : null;
    if (profileInventory != null && profileInventory > 0 && !avgInventoryUnits) {
      setAvgInventoryUnits(String(Math.round(profileInventory)));
    }
    if (profileAnnual != null && profileAnnual > 0 && !annualUnitsSold && !latest?.input?.annualUnitsSold) {
      setAnnualUnitsSold(String(Math.round(profileAnnual)));
    }
  }, [profile]);

  const canCompute =
    nf(avgInventoryUnits) > 0 &&
    nf(annualUnitsSold) > 0 &&
    avgTurnDays > 0 &&
    (derivedAvgGross ?? 0) > 0;

  async function compute(silent = false) {
    setComputeError("");
    if (!canCompute || loadedSavedReturn?.editor.blocked) return;
    setComputing(true);
    try {
      const turnDaysWasOverride = nf(avgTurnDaysOverride) > 0;
      const body: Record<string, any> = {
        avgInventoryUnits: nf(avgInventoryUnits),
        annualUnitsSold: nf(annualUnitsSold),
        avgGrossProfitPerUnit: derivedAvgGross!,
        avgTurnDays,
        avgTurnDaysWasOverride: turnDaysWasOverride,
        revenue: activeFinancials?.revenue,
        grossProfit: derivedGrossProfit,
        agedUnitAgesDays: autoAgedDays,
        expenses: { dynamicItems: dynamicExpenseItems },
        annualTotalOperatingExpenses: activeFinancials?.totalOperatingExpenses,
        annualTotalExpenses: activeFinancials?.totalOperatingExpenses,
        manualInput: true,
        taxReturnSourceFields,
      };
      if (manualTotalIncome !== "") body.totalIncome = nf(manualTotalIncome);
      body.otherIncome = extractedOtherIncome != null ? extractedOtherIncome : 0;
      if (loadedSavedReturn) {
        body.previewOnly = true;
        body.savedTaxReturn = { id: loadedSavedReturn.id, sourceVersion: loadedSavedReturn.editor.sourceVersion };
      }

      const res = await fetch("/api/op-analytics/compute", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || "Compute failed");
      if (loadedSavedReturn && !loadedSavedReturn.editor.blocked) {
        setSavedReturnPreview({ id: loadedSavedReturn.id, output: j.output });
      }
      if (!loadedSavedReturn) qc.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
      if (!silent) {
        setIsLocked(true);
        toast({ title: t("operations:analytics.toastAnalyticsSaved"), description: t("operations:analytics.toastAnalyticsSavedDesc"), duration: 3000 });
      }
    } catch (e: any) {
      setComputeError(e.message || "Compute failed");
      toast({ title: t("common:error"), description: e.message, variant: "destructive" });
    } finally {
      setComputing(false);
    }
  }

  // Auto-compute whenever inputs are ready — debounced 600ms. Runs even while
  // the inputs panel is unlocked so cost-to-carry and breakeven update as the
  // dealer types (edits also auto-save; see the effect below).
  const computeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!canCompute) return;
    if (computeTimerRef.current) clearTimeout(computeTimerRef.current);
    computeTimerRef.current = setTimeout(() => { compute(true); }, 600);
    return () => { if (computeTimerRef.current) clearTimeout(computeTimerRef.current); };
  }, [canCompute, isLocked, avgInventoryUnits, annualUnitsSold, avgTurnDays, derivedAvgGross,
      manualRevenue, manualGrossProfit, manualOpEx, dynamicExpenseItems, extractedOtherIncome]);

  // ── Auto-save of the analytics inputs (units sold, lot units, turn days) ──
  // The dealer expects an edited value to stick even if they leave the page
  // without pressing Save. While the panel is unlocked, changes are saved
  // after an 800ms pause; any still-pending save is flushed with `keepalive`
  // on unmount so navigating away can't lose the edit.
  const lastAutoSavedRef = useRef<string>("");
  const autoSaveSeededRef = useRef(false);
  const pendingAutoSaveRef = useRef<string | null>(null);
  const buildBasicInputsBody = (vals: { avgInventoryUnits: string; annualUnitsSold: string; avgTurnDaysOverride: string }) => {
    const body: Record<string, any> = { manualInput: true };
    if (nf(vals.avgInventoryUnits) > 0) body.avgInventoryUnits = nf(vals.avgInventoryUnits);
    if (nf(vals.annualUnitsSold) > 0) body.annualUnitsSold = nf(vals.annualUnitsSold);
    if (nf(vals.avgTurnDaysOverride) > 0) body.avgTurnDays = nf(vals.avgTurnDaysOverride);
    return body;
  };
  // Serialize all auto-save POSTs so a slow earlier save can never land after
  // (and overwrite) a newer one — e.g. an in-flight autosave finishing after
  // Cancel re-persisted the pre-edit values.
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const postBasicInputs = (json: string, keepalive = false) => {
    pendingAutoSaveRef.current = null;
    lastAutoSavedRef.current = json;
    // Mark the cached `latest` stale synchronously for EVERY save — including
    // the unmount keepalive flush, because on SPA navigation the query cache
    // outlives this component and a remount would otherwise restore the stale
    // cached value into the (empty) fields, where the fill-if-empty guard
    // then blocks the fresh refetch from correcting it.
    qc.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
    saveChainRef.current = saveChainRef.current.then(() => {
      if (loadedSavedReturn?.editor.blocked) return;
      const input = JSON.parse(json);
      if (loadedSavedReturn) {
        input.savedTaxReturn = { id: loadedSavedReturn.id, sourceVersion: loadedSavedReturn.editor.sourceVersion };
      }
      return fetch("/api/op-analytics/save-inputs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        keepalive,
        body: JSON.stringify(input),
      })
        .then(async (response) => {
          if (!response.ok) throw new Error("Analytics save failed");
          // Invalidate again after the write lands so any refetch that raced
          // the save is itself marked stale.
          if (!keepalive) qc.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
        })
        .catch(() => {
          if (loadedSavedReturn) {
            loadedSavedReturn.editor.blocked = true;
            qc.invalidateQueries({ queryKey: ["/api/saved-tax-returns"] });
            toast({
              title: t("common:warning"), description: t("operations:analytics.toastSnapshotConflict"),
              variant: "destructive", duration: 10000,
            });
          }
        });
    });
    return saveChainRef.current;
  };
  // When a saved tax return is loaded, the provider re-restores its snapshot on
  // every page mount — so an auto-saved edit must ALSO be written into that
  // snapshot or it reverts as soon as the dealer navigates away and back.
  const patchSavedReturnSnapshot = (keepalive = false) => {
    const ret = loadedSavedReturn;
    if (!ret) return;
    // MERGE into the existing snapshot — never rebuild it from scratch. The
    // auto-save only owns annualUnitsSold; rebuilding would silently drop
    // fields it doesn't know about (rental, other income, zeros, future keys).
    let existingSnapshot: Record<string, any>;
    try {
      existingSnapshot = JSON.parse(ret.editor.snapshotJson);
    } catch {
      return;
    }
    const units = nf(annualUnitsSold);
    if (!(units > 0) || existingSnapshot.annualUnitsSold === units) return;
    saveChainRef.current = saveChainRef.current.then(() =>
      ret.editor.save(previous => JSON.stringify({ ...JSON.parse(previous), annualUnitsSold: units }), keepalive)
        .then(updated => {
          if (updated) {
            qc.setQueryData(["/api/saved-tax-returns"], (old: any[] = []) =>
              old.map((r: any) => (r.id === updated.id ? updated : r))
            );
          }
        })
        .catch((error) => {
          // Preserve local form values and the old cursor. A refreshed list is
          // for explicit re-selection only; it cannot authorize a silent retry.
          qc.invalidateQueries({ queryKey: ["/api/saved-tax-returns"] });
          toast({
            title: t("common:warning"),
            description: error instanceof SavedTaxReturnConflictError
              ? t("operations:analytics.toastSnapshotConflict")
              : t("operations:analytics.toastSnapshotSaveFailed"),
            variant: "destructive",
            duration: 10000,
          });
        })
    );
  };
  const patchSnapshotRef = useRef<(keepalive?: boolean) => void>(() => {});
  patchSnapshotRef.current = patchSavedReturnSnapshot;
  useEffect(() => {
    // Not gated on isLocked: units sold is editable from the tax-return card
    // while the inputs panel is locked, and that edit must persist too. The
    // lastAutoSavedRef echo guard (seeded on restore) prevents redundant
    // saves when values are merely restored from the server.
    const body = buildBasicInputsBody({ avgInventoryUnits, annualUnitsSold, avgTurnDaysOverride });
    // Nothing meaningful entered yet — don't spam empty saves.
    if (Object.keys(body).length <= 1) return;
    const json = JSON.stringify(body);
    if (json === lastAutoSavedRef.current) {
      // Current values match what's already persisted — drop any stale queued
      // payload so the unmount keepalive flush can't post an abandoned edit
      // (e.g. Cancel restored the snapshot before the debounce timer fired).
      pendingAutoSaveRef.current = null;
      return;
    }
    pendingAutoSaveRef.current = json;
    const timer = setTimeout(() => {
      if (pendingAutoSaveRef.current === json) {
        patchSnapshotRef.current();
        void postBasicInputs(json);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [isLocked, avgInventoryUnits, annualUnitsSold, avgTurnDaysOverride]);
  // Flush a pending auto-save if the dealer navigates away mid-edit.
  useEffect(() => () => {
    if (pendingAutoSaveRef.current) {
      patchSnapshotRef.current(true);
      void postBasicInputs(pendingAutoSaveRef.current, true);
    }
  }, []);

  async function handleSaveInputs() {
    // Explicit Save supersedes any queued/in-flight auto-save: drop the
    // pending payload and wait for earlier POSTs so ours is the last writer.
    pendingAutoSaveRef.current = null;
    await saveChainRef.current;
    const hasExpenseRows = dynamicExpenseItems.some(
      (i) => (i.label ?? "").trim() !== "" || i.amount > 0,
    );
    const hasAny =
      nf(avgInventoryUnits) > 0 ||
      nf(annualUnitsSold) > 0 ||
      nf(avgTurnDaysOverride) > 0 ||
      nf(manualRevenue) > 0 ||
      nf(manualGrossProfit) > 0 ||
      manualTotalIncome !== "" ||
      derivedOpEx > 0 ||
      hasExpenseRows;
    if (!hasAny) {
      toast({ title: t("operations:analytics.toastNothingToSave"), description: t("operations:analytics.toastNothingToSaveDesc"), variant: "destructive" });
      return;
    }
    // Snapshot all relevant state NOW before any awaits — avoids stale-closure issues
    const savedReturnAtSaveTime = loadedSavedReturn;
    const revenueAtSave = nf(manualRevenue);
    const gpAtSave = nf(manualGrossProfit);
    const totalIncomeAtSave = computeTotalIncomeForSave(manualTotalIncome);
    const unitsAtSave = nf(annualUnitsSold);
    const opExAtSave = derivedOpEx;
    const expensesAtSave = dynamicExpenseItems;
    setSaving(true);
    try {
      const body: Record<string, any> = {};
      if (nf(avgInventoryUnits) > 0) body.avgInventoryUnits = nf(avgInventoryUnits);
      if (nf(annualUnitsSold) > 0) body.annualUnitsSold = nf(annualUnitsSold);
      if (nf(avgTurnDaysOverride) > 0) body.avgTurnDays = nf(avgTurnDaysOverride);
      if (revenueAtSave > 0) body.revenue = revenueAtSave;
      if (gpAtSave > 0) body.grossProfit = gpAtSave;
      if (totalIncomeAtSave !== null) body.totalIncome = totalIncomeAtSave;
      if (opExAtSave > 0) body.annualTotalOperatingExpenses = opExAtSave;
      if (isEditingTaxReturn || expensesAtSave.length > 0) body.expenses = { dynamicItems: expensesAtSave };
      if (rentalData) body.rental = rentalData;
      body.manualInput = true;
      body.taxReturnSourceFields = taxReturnSourceFields;
      // If editing a loaded saved return, persist the updated numbers back into that return's snapshot
      if (savedReturnAtSaveTime) {
        const updatedSnapshot = {
          revenue: revenueAtSave || undefined,
          grossProfit: gpAtSave || undefined,
          totalIncome: totalIncomeAtSave ?? undefined,
          annualUnitsSold: unitsAtSave || undefined,
          annualTotalOperatingExpenses: opExAtSave || undefined,
          dynamicExpenses: expensesAtSave,
        };
        try {
          const updated = await savedReturnAtSaveTime.editor.save(JSON.stringify(updatedSnapshot));
          qc.setQueryData(["/api/saved-tax-returns"], (old: any[] = []) =>
            old.map((r: any) => r.id === updated.id ? updated : r)
          );
          qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
        } catch (patchErr: any) {
          qc.invalidateQueries({ queryKey: ["/api/saved-tax-returns"] });
          toast({
            title: t("common:warning"),
            description: patchErr instanceof SavedTaxReturnConflictError
              ? t("operations:analytics.toastSnapshotConflict")
              : t("operations:analytics.toastSnapshotSaveFailed"),
            variant: "destructive", duration: 10000,
          });
          return; // Keep local edits; do not publish stale values to analytics.
        }
        // Always refresh the list so the next load reads the latest DB state
        qc.invalidateQueries({ queryKey: ["/api/saved-tax-returns"] });
      }

      const res = await fetch("/api/op-analytics/save-inputs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...body,
          ...(savedReturnAtSaveTime ? {
            savedTaxReturn: { id: savedReturnAtSaveTime.id, sourceVersion: savedReturnAtSaveTime.editor.sourceVersion },
          } : {}),
        }),
      });
      if (res.status === 409 && savedReturnAtSaveTime) {
        savedReturnAtSaveTime.editor.blocked = true;
        qc.invalidateQueries({ queryKey: ["/api/saved-tax-returns"] });
        throw new Error(t("operations:analytics.toastSnapshotConflict"));
      }
      if (!res.ok) throw new Error((await res.json()).error || "Save failed");
      qc.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
      setIsEditingTaxReturn(false);
      setTaxReturnSnapshot(null);
      setExtractionSource(null);
      toast({ title: t("operations:analytics.toastInputsSaved"), description: t("operations:analytics.toastInputsSavedDesc"), duration: 3000 });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e: any) {
      toast({ title: t("operations:analytics.toastSaveFailed"), description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  function applyResult(result: Record<string, any>, fileName?: string, clearSource = false) {
    if (clearSource) clearCompletedSource();
    setIsPendingNewReturn(true);
    // A new upload always starts fresh — clear any previously loaded saved return
    setLoadedSavedReturn(null);
    if (currentUser?.id) localStorage.removeItem(`lastTaxReturnId:${currentUser.ownerUserId ?? currentUser.id}`);
    setPendingTaxYear("");
    setManualRevenue("");
    setManualGrossProfit("");
    setManualTotalIncome("");
    setManualOpEx("");
    setDynamicExpenseItems([]);
    setExtractedOtherIncome(typeof result.otherIncome === "number" ? result.otherIncome : null);
    setRentalData(result.rental ?? null);
    const newSource: string[] = result.taxReturnSourceFields ?? [];
    const newAiValues: { revenue?: string; grossProfit?: string; opEx?: string } = {};
    if (result.revenue != null) { setManualRevenue(String(result.revenue)); newAiValues.revenue = String(result.revenue); }
    if (result.grossProfit != null) { setManualGrossProfit(String(result.grossProfit)); newAiValues.grossProfit = String(result.grossProfit); }
    if (result.totalIncome != null) { setManualTotalIncome(String(result.totalIncome)); }
    if (result.totalOperatingExpenses != null) { setManualOpEx(String(result.totalOperatingExpenses)); newAiValues.opEx = String(result.totalOperatingExpenses); }
    if (Array.isArray(result.dynamicExpenses) && result.dynamicExpenses.length > 0) {
      setDynamicExpenseItems(result.dynamicExpenses);
    }
    setTaxReturnSourceFields(newSource);
    setAiExtractedValues(newAiValues);

    // Build persistent banner warnings (large doc + skipped pages).
    // Filter out warnings about fields that are always entered manually and
    // will never appear in a tax return (units sold, avg profit per unit).
    const SUPPRESSED_WARNING_PATTERNS = [
      /units?\s+sold/i,
      /avg.*profit.*per.*unit/i,
      /average.*profit.*per.*unit/i,
    ];
    const bannerWarnings: string[] = [];
    if (Array.isArray(result.warnings)) {
      for (const w of result.warnings) {
        if (typeof w === "string" && !SUPPRESSED_WARNING_PATTERNS.some(p => p.test(w))) {
          bannerWarnings.push(w);
        }
      }
    }
    if (typeof result.skippedPageCount === "number" && result.skippedPageCount > 0) {
      bannerWarnings.push(
        `${result.skippedPageCount} page${result.skippedPageCount === 1 ? "" : "s"} could not be read during extraction and ${result.skippedPageCount === 1 ? "was" : "were"} skipped. Some values may be missing.`,
      );
    }
    setExtractionBannerWarnings(bannerWarnings);

    setIsEditingTaxReturn(true);
    if (clearSource) {
      setExtractionSource(null);
    } else if (result.taxReturnDoc?.fileName || fileName) {
      setExtractionSource(result.taxReturnDoc?.fileName ?? fileName);
    }
    // Pre-fill year from parser result and open the save dialog (only when
    // recognizable data was found — pagesFound === 0 is handled elsewhere).
    if (!clearSource) {
      // Use the completed source, not the render closure's older document.
      // A repeated resume must not overwrite a dealer's existing draft.
      const key = taxReturnDraftKey(sourceScope, completedSourceRef.current.document(undefined));
      if (key && !readTaxReturnDraft(key)) {
        writeTaxReturnDraft({ key, name: "", year: result.taxYear ? String(result.taxYear) : "" });
      }
      setShowSaveReturnDialog(true);
    }
    qc.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
  }

  const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes — large PDFs with Claude vision take 5-7 min

  function stopPolling() {
    pollCancelledRef.current = true;
    if (pollTimerRef.current !== null) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }

  function cleanupPolling(clearInput = true, markDismissed = false) {
    stopPolling();
    setPdfUploading(false);
    setExtractionProgressText(null);
    setExtractionError(null);
    if (clearInput && pdfInputRef.current) pdfInputRef.current.value = "";
    // Clear the persisted job ID from localStorage on any terminal state.
    // When dismissed by the user (X button), record the job ID first so
    // tryResume never reconnects to it on future mounts.
    if (taxJobStorageKey) {
      try {
        if (markDismissed && taxDismissedJobKey) {
          // Prefer the localStorage active-job entry; fall back to the ref which
          // stays populated even when the job was resumed via the server /active
          // fallback (and localStorage was therefore already empty).
          const currentJobId = localStorage.getItem(taxJobStorageKey) ?? currentPollJobIdRef.current;
          if (currentJobId) localStorage.setItem(taxDismissedJobKey, currentJobId);
        }
        localStorage.removeItem(taxJobStorageKey);
      } catch {}
    }
    currentPollJobIdRef.current = null;
  }

  // Serialized polling via setTimeout chaining — avoids overlapping requests
  // that setInterval would produce when server responses are slow.
  // fileName is optional — when resuming after navigation it may not be known.
  async function schedulePoll(jobId: string, fileName?: string): Promise<void> {
    if (pollCancelledRef.current) return;
    const source = completedSourceRef.current;

    // Timeout guard: if the job has been polling for more than POLL_TIMEOUT_MS,
    // surface a clear error so the user is never stuck in an infinite loading state.
    if (Date.now() - pollStartedAtRef.current > POLL_TIMEOUT_MS) {
      toast({
        title: t("operations:analytics.toastExtractionTimedOut"),
        description: t("operations:analytics.toastExtractionTimedOutDesc"),
        variant: "destructive",
      });
      cleanupPolling();
      return;
    }

    try {
      const statusRes = await fetch(`/api/parse-tax-return/status/${jobId}`, { credentials: "include" });
      if (pollCancelledRef.current || source !== completedSourceRef.current || currentPollJobIdRef.current !== jobId) return;
      if (!statusRes.ok) {
        // 503 / 502 / connection errors during a server restart — retry silently a few
        // times before surfacing an error to the user.  The server resets in-flight
        // tax_return jobs to 'pending' on startup so the job itself survives the restart.
        if (statusRes.status >= 500 && pollNetworkRetryCountRef.current < MAX_POLL_NETWORK_RETRIES) {
          pollNetworkRetryCountRef.current++;
          pollTimerRef.current = setTimeout(() => schedulePoll(jobId, fileName), 6000);
          return;
        }
        // 404 means the job no longer exists (e.g. server restarted and cleanup ran)
        if (statusRes.status === 404) {
          toast({
            title: t("operations:analytics.toastExtractionFailed"),
            description: t("operations:analytics.toastExtractionJobNotFoundDesc"),
            variant: "destructive",
          });
        } else {
          toast({ title: t("operations:analytics.toastUploadError"), description: t("operations:analytics.toastLostConnection"), variant: "destructive" });
        }
        pollNetworkRetryCountRef.current = 0;
        cleanupPolling();
        return;
      }
      // Successful response — reset the network-error retry counter.
      pollNetworkRetryCountRef.current = 0;
      const statusJson = await statusRes.json();
      if (pollCancelledRef.current || source !== completedSourceRef.current || currentPollJobIdRef.current !== jobId) return;

      if (statusJson.status === "processing" || statusJson.status === "pending") {
        const prog = statusJson.progress;
        if (prog) setExtractionProgressText(`Extracting page ${prog.page} of ${prog.totalPages}…`);
        // Reset the inactivity deadline whenever progress advances so that a
        // legitimately long but active job is never killed mid-extraction.
        const token = `${prog?.page ?? ""}|${prog?.totalPages ?? ""}`;
        if (token !== pollLastProgressTokenRef.current) {
          pollLastProgressTokenRef.current = token;
          pollStartedAtRef.current = Date.now();
        }
        // Schedule next poll only after this response is fully received
        pollTimerRef.current = setTimeout(() => schedulePoll(jobId, fileName), 2000);
        return;
      }

      if (statusJson.status === "error") {
        const errMsg = statusJson.error || "Could not read the document.";
        setExtractionError(errMsg);
        toast({ title: t("operations:analytics.toastExtractionFailed"), description: errMsg, variant: "destructive" });
        setPdfUploading(false);
        stopPolling();
        if (taxJobStorageKey) { try { localStorage.removeItem(taxJobStorageKey); } catch {} }
        return;
      }

      // done
      const doneResult = statusJson.result ?? {};
      source.complete(jobId, doneResult.taxReturnDoc ?? { fileName: fileName ?? "Tax Return" }, latest?.input?.taxReturnDoc);
      await confirmCompletedSource(source);
      if (pollCancelledRef.current || source !== completedSourceRef.current || currentPollJobIdRef.current !== jobId) return;
      if (doneResult.pagesFound === 0) {
        // No recognizable tax forms found — show confirmation dialog before applying
        cleanupPolling();
        setNoFormsDialogPending(doneResult);
        return;
      }
      applyResult(doneResult, fileName);
      cleanupPolling();
      toast({ title: t("operations:analytics.toastExtractedSaved"), description: t("operations:analytics.toastExtractedSavedDesc"), duration: 4000 });
      // Surface any extraction warnings (e.g. large document truncation) to the user.
      // Suppress warnings about fields that are always manually entered (units sold, avg profit per unit).
      const SUPPRESSED = [/units?\s+sold/i, /avg.*profit.*per.*unit/i, /average.*profit.*per.*unit/i];
      const extractionWarnings: string[] = (Array.isArray(doneResult.warnings) ? doneResult.warnings : [])
        .filter((w: string) => !SUPPRESSED.some(p => p.test(w)));
      for (const warning of extractionWarnings) {
        toast({ title: t("operations:analytics.toastExtractionWarning"), description: warning, variant: "destructive", duration: 8000 });
      }
    } catch (pollErr: any) {
      if (pollCancelledRef.current || source !== completedSourceRef.current || currentPollJobIdRef.current !== jobId) return;
      // Network/fetch error — may indicate the server is briefly restarting.
      // Retry a few times with a longer interval before surfacing an error.
      if (pollNetworkRetryCountRef.current < MAX_POLL_NETWORK_RETRIES) {
        pollNetworkRetryCountRef.current++;
        pollTimerRef.current = setTimeout(() => schedulePoll(jobId, fileName), 6000);
        return;
      }
      pollNetworkRetryCountRef.current = 0;
      toast({ title: t("operations:analytics.toastUploadError"), description: t("operations:analytics.toastCouldNotReach"), variant: "destructive" });
      cleanupPolling();
    }
  }

  // On mount: attempt to resume an in-flight extraction job.
  // Primary: check localStorage for a persisted jobId (requires taxJobStorageKey to be ready).
  // Fallback: query /api/parse-tax-return/active for any server-side active job
  // (handles the case where localStorage was cleared, e.g. different browser).
  // Depends on taxJobStorageKey so that localStorage is re-checked once the auth
  // query resolves and the key is available (previously it could be null on first pass).
  const resumeAttemptedRef = useRef(false);
  useEffect(() => {
    if (!sourceScope) return;
    setTaxResumeResolved(false);
    // Only attempt resume once per component lifecycle — skip re-runs after first success
    if (resumeAttemptedRef.current) return;
    let didResume = false;
    let cancelled = false;
    const source = completedSourceRef.current;
    const generation = uploadGenerationRef.current;
    const stale = () => cancelled || source !== completedSourceRef.current || generation !== uploadGenerationRef.current;

    async function tryResume() {
      // Try localStorage first
      let resumeJobId: string | null = null;
      if (taxJobStorageKey) {
        try { resumeJobId = localStorage.getItem(taxJobStorageKey); } catch {}
      }

      // If no localStorage entry, ask the server for any active job
      if (!resumeJobId) {
        try {
          const res = await fetch("/api/parse-tax-return/active", { credentials: "include" });
          if (res.ok) {
            const data = await res.json();
            if (data?.jobId) resumeJobId = data.jobId;
          }
        } catch {}
      }

      if (!resumeJobId || didResume || stale()) return;

      // If the user previously dismissed this job via the X button, skip reconnection.
      if (taxDismissedJobKey) {
        try {
          const dismissedId = localStorage.getItem(taxDismissedJobKey);
          if (dismissedId && dismissedId === resumeJobId) return;
        } catch {}
      }

      // Check the job status before deciding how to reconnect
      try {
        const statusRes = await fetch(`/api/parse-tax-return/status/${resumeJobId}`, { credentials: "include" });
        if (stale()) return;
        // Clean up stale localStorage key for any non-200 (e.g. 404 = job expired)
        if (!statusRes.ok) {
          if (taxJobStorageKey) {
            try { localStorage.removeItem(taxJobStorageKey); } catch {}
          }
          return;
        }
        const statusJson = await statusRes.json();
        if (stale()) return;

        if (statusJson.status === "done") {
          // Job completed while user was away — apply result immediately
          didResume = true;
          if (taxJobStorageKey) {
            try { localStorage.removeItem(taxJobStorageKey); } catch {}
          }
          const doneResult = statusJson.result ?? {};
          source.complete(resumeJobId, doneResult.taxReturnDoc ?? { fileName: "Tax Return" }, latest?.input?.taxReturnDoc);
          await confirmCompletedSource(source);
          if (stale()) return;
          if (doneResult.pagesFound === 0) {
            setNoFormsDialogPending(doneResult);
          } else {
            applyResult(doneResult);
            toast({ title: t("operations:analytics.toastExtractedSaved"), description: t("operations:analytics.toastExtractedSavedDesc"), duration: 4000 });
          }
          return;
        }

        if (statusJson.status === "error") {
          // Terminal error — clean up localStorage and surface to user
          didResume = true;
          if (taxJobStorageKey) {
            try { localStorage.removeItem(taxJobStorageKey); } catch {}
          }
          toast({ title: t("operations:analytics.toastExtractionFailed"), description: statusJson.error || t("operations:analytics.toastExtractionFailedRetry"), variant: "destructive" });
          return;
        }

        if (statusJson.status === "processing" || statusJson.status === "pending") {
          // Job is still running — reconnect to it. Track in ref so X-dismiss works
          // even if localStorage was empty (resume came from server /active fallback).
          didResume = true;
          currentPollJobIdRef.current = resumeJobId;
          pollCancelledRef.current = false;
          pollStartedAtRef.current = Date.now();
          pollLastProgressTokenRef.current = null;
          pollNetworkRetryCountRef.current = 0;
          setPdfUploading(true);
          setExtractionProgressText("Reconnecting to extraction…");
          pollTimerRef.current = setTimeout(() => schedulePoll(resumeJobId!), 2000);
        } else {
          // Unknown terminal status — clean up localStorage and stale dismissed key
          if (taxJobStorageKey) {
            try { localStorage.removeItem(taxJobStorageKey); } catch {}
          }
          if (taxDismissedJobKey) {
            try { localStorage.removeItem(taxDismissedJobKey); } catch {}
          }
        }
      } catch {}
    }

    tryResume().then(() => {
      if (!cancelled && source === completedSourceRef.current) {
        resumeAttemptedRef.current = true;
        setTaxResumeResolved(true);
      }
    });

    return () => {
      // On unmount, cancel in-flight polls (but keep localStorage so remount can resume)
      cancelled = true;
      resumeAttemptedRef.current = false;
      stopPolling();
    };
  }, [taxJobStorageKey, sourceScope]);

  async function handlePdfUpload(file: File) {
    clearCompletedSource();
    const source = completedSourceRef.current;
    const generation = uploadGenerationRef.current;
    const stale = () => source !== completedSourceRef.current || generation !== uploadGenerationRef.current;
    // Cancel any existing poll before starting a new upload.
    // Also clear any dismissed-job record so the new job resumes normally.
    stopPolling();
    if (taxDismissedJobKey) { try { localStorage.removeItem(taxDismissedJobKey); } catch {} }
    pollCancelledRef.current = false;
    pollStartedAtRef.current = Date.now();
    pollLastProgressTokenRef.current = null;
    pollNetworkRetryCountRef.current = 0;

    setPdfUploading(true);
    setExtractionError(null);
    setExtractionProgressText("Uploading…");
    try {
      // Step 1: direct-to-storage upload, then finalize — returns {jobId}
      const { uploadId } = await uploadFileDirect(file, "tax_return");
      if (stale()) return;
      const uploadRes = await fetch("/api/parse-tax-return/finalize", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploadId }),
      });
      const uploadJson = await uploadRes.json();
      if (stale()) return;
      if (!uploadRes.ok) {
        toast({ title: t("operations:analytics.toastExtractionFailed"), description: uploadJson.message || t("operations:analytics.toastCouldNotRead"), variant: "destructive" });
        cleanupPolling();
        return;
      }

      const jobId = uploadJson.jobId as string;

      // Persist the job ID to localStorage so we can resume if the user navigates away.
      // Also track it in the ref so cleanupPolling can record a dismissed job ID even
      // if localStorage is cleared before the user clicks X.
      currentPollJobIdRef.current = jobId;
      if (taxJobStorageKey) {
        try { localStorage.setItem(taxJobStorageKey, jobId); } catch {}
      }

      setExtractionProgressText("Starting extraction…");

      // Step 2: Start serialized polling chain
      pollTimerRef.current = setTimeout(() => schedulePoll(jobId, file.name), 2000);
    } catch (err: any) {
      if (stale()) return;
      toast({ title: t("operations:analytics.toastUploadError"), description: err.message || t("operations:analytics.toastSomethingWrong"), variant: "destructive" });
      cleanupPolling();
    }
  }

  async function handleSaveTaxYear() {
    if (!pendingTaxYear) {
      toast({ title: t("operations:analytics.toastYearRequired"), description: t("operations:analytics.toastYearRequiredDesc"), variant: "destructive" });
      return;
    }
    setSavingTaxYear(true);
    try {
      if (loadedSavedReturn?.editor.blocked) throw new Error(t("operations:analytics.toastSnapshotConflict"));
      const res = await fetch("/api/op-analytics/save-inputs", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          taxYear: pendingTaxYear,
          ...(loadedSavedReturn ? {
            savedTaxReturn: { id: loadedSavedReturn.id, sourceVersion: loadedSavedReturn.editor.sourceVersion },
          } : {}),
        }),
      });
      const resJson = await res.json();
      if (res.status === 409 && loadedSavedReturn) {
        loadedSavedReturn.editor.blocked = true;
        qc.invalidateQueries({ queryKey: ["/api/saved-tax-returns"] });
        throw new Error(t("operations:analytics.toastSnapshotConflict"));
      }
      if (!res.ok) throw new Error(resJson?.error || resJson?.message || "Save failed");
      qc.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
      if (saveTaxReturnYear === "") setSaveTaxReturnYear(pendingTaxYear);
      setPendingTaxYear("");
      toast({ title: t("operations:analytics.toastTaxYearSaved"), description: t("operations:analytics.toastTaxYearSavedDesc", { year: pendingTaxYear }), duration: 3000 });
    } catch (err: any) {
      toast({ title: t("operations:analytics.toastSaveFailed"), description: err.message || t("operations:analytics.toastCouldNotSaveYear"), variant: "destructive" });
    } finally {
      setSavingTaxYear(false);
    }
  }

  async function handleDeleteTaxReturnDoc({ suppressToastOnFailure = false }: { suppressToastOnFailure?: boolean } = {}): Promise<boolean> {
    setDocDeleting(true);
    try {
      const res = await fetch("/api/tax-return-document", {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        // Send client-side provenance so server uses the latest known source fields,
        // not potentially stale server-persisted data. This handles the case where the
        // user manually edited a field (removing it from taxReturnSourceFields here) but
        // autosave hadn't yet persisted the updated provenance to the server.
        body: JSON.stringify({ taxReturnSourceFields }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Delete failed");
      clearCompletedSource();

      // Clear only the fields that were recorded as tax-return-sourced.
      const cleared: string[] = json.clearedFields ?? [];
      if (cleared.includes("revenue")) setManualRevenue("");
      if (cleared.includes("grossProfit")) setManualGrossProfit("");
      setManualTotalIncome("");  // always clear — it's never manually overridden
      setExtractedOtherIncome(null);  // always clear — derived solely from extraction
      if (cleared.includes("annualTotalOperatingExpenses") || cleared.includes("annualTotalExpenses")) setManualOpEx("");
      if (cleared.includes("expenses.dynamicItems")) setDynamicExpenseItems([]);

      setExtractionSource(null);
      setIsEditingTaxReturn(false);
      setTaxReturnSourceFields([]);
      setPendingTaxYear("");
      qc.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
      toast({ title: t("operations:analytics.toastDocRemoved"), description: t("operations:analytics.toastDocRemovedDesc"), duration: 3000 });
      return true;
    } catch (err: any) {
      if (!suppressToastOnFailure) {
        toast({ title: t("operations:analytics.toastDeleteFailed"), description: err.message, variant: "destructive" });
      }
      return false;
    } finally {
      setDocDeleting(false);
    }
  }

  function handleSaveTaxReturn() {
    if (!saveTaxReturnYear) {
      toast({ title: t("operations:analytics.toastYearRequired"), description: t("operations:analytics.toastYearRequiredSelect"), variant: "destructive" });
      return;
    }
    if (!saveTaxReturnName.trim()) {
      toast({ title: t("operations:analytics.toastNameRequired"), description: t("operations:analytics.toastNameRequiredDesc"), variant: "destructive" });
      return;
    }
    if (!annualUnitsSold || nf(annualUnitsSold) <= 0) {
      toast({ title: t("operations:analytics.toastUnitsRequired"), description: t("operations:analytics.toastUnitsRequiredDesc"), variant: "destructive" });
      return;
    }
    const combinedName = `${saveTaxReturnYear} – ${saveTaxReturnName.trim()}`;
    const snapshot = {
      revenue: nf(manualRevenue) || undefined,
      grossProfit: nf(manualGrossProfit) || undefined,
      totalIncome: manualTotalIncome !== "" ? nf(manualTotalIncome) : undefined,
      otherIncome: extractedOtherIncome ?? undefined,
      annualUnitsSold: nf(annualUnitsSold) || undefined,
      annualTotalOperatingExpenses: derivedOpEx || undefined,
      dynamicExpenses: dynamicExpenseItems,
      rental: rentalData ?? undefined,
    };
    const doc = displayedTaxDocument;
    const fileName = doc?.fileName ?? extractionSource ?? "Tax Return";
    const cloudPath = doc?.cloudPath ?? null;
    const sourceJobId = doc?.sourceJobId;
    saveTaxReturnMutation.mutate({
      name: combinedName,
      fileName,
      cloudPath,
      snapshotJson: JSON.stringify(snapshot),
      ...(sourceJobId ? { sourceJobId } : {}),
    });
  }

  function loadSavedReturn(entry: any) {
    clearCompletedSource();
    try {
      const snap = typeof entry.snapshotJson === "string" ? JSON.parse(entry.snapshotJson) : entry.snapshotJson;
      setManualRevenue(snap.revenue != null ? String(snap.revenue) : "");
      setManualGrossProfit(snap.grossProfit != null ? String(snap.grossProfit) : "");
      setManualTotalIncome(snap.totalIncome != null ? String(snap.totalIncome) : "");
      setManualOpEx(snap.annualTotalOperatingExpenses != null ? String(snap.annualTotalOperatingExpenses) : "");
      setAnnualUnitsSold(snap.annualUnitsSold != null ? String(snap.annualUnitsSold) : "");
      setDynamicExpenseItems(Array.isArray(snap.dynamicExpenses) ? snap.dynamicExpenses : []);
      setExtractedOtherIncome(typeof snap.otherIncome === "number" ? snap.otherIncome : null);
      setRentalData(snap.rental ?? null);
      setLoadedSavedReturn(openSavedTaxReturn(entry));
      setIsPendingNewReturn(false);
      setIsEditingTaxReturn(false);
      setShowPastStatements(false);
      setCompareMode(false);
      setSelectedCompareIds([]);
      if (currentUser?.id) {
        localStorage.setItem(`lastTaxReturnId:${currentUser.ownerUserId ?? currentUser.id}`, entry.id);
      }
    } catch {
      toast({ title: t("operations:analytics.toastLoadFailed"), description: t("operations:analytics.toastLoadFailedDesc"), variant: "destructive" });
    }
  }

  function handleManualEntrySubmit() {
    clearCompletedSource();
    const revenue = nf(manualEntryRevenue);
    const grossProfit = nf(manualEntryGrossProfit);
    const totalExpenses = nf(manualEntryTotalExpenses);
    const year = manualEntryYear.trim();

    setManualRevenue(String(revenue));
    setManualGrossProfit(String(grossProfit));
    setManualOpEx(String(totalExpenses));
    setManualTotalIncome("");
    setDynamicExpenseItems([]);
    setLoadedSavedReturn(null);
    if (currentUser?.id) localStorage.removeItem(`lastTaxReturnId:${currentUser.ownerUserId ?? currentUser.id}`);
    setExtractionSource(null);
    setTaxReturnSourceFields([]);
    setAiExtractedValues({});
    setExtractedOtherIncome(null);
    setExtractionBannerWarnings([]);
    setIsEditingTaxReturn(false);
    setIsPendingNewReturn(false);

    const name = year ? `${year} Tax Return` : "Manual Entry";
    const snapshot = {
      revenue,
      grossProfit,
      annualTotalOperatingExpenses: totalExpenses,
      annualUnitsSold: 0,
      dynamicExpenses: [],
    };

    saveTaxReturnMutation.mutate({
      name,
      fileName: "Manual Entry",
      cloudPath: null,
      snapshotJson: JSON.stringify(snapshot),
    });

    setShowManualEntryDialog(false);
    setManualEntryYear("");
    setManualEntryRevenue("");
    setManualEntryGrossProfit("");
    setManualEntryTotalExpenses("");
  }

  const output = loadedSavedReturn && savedReturnPreview?.id === loadedSavedReturn.id
    ? savedReturnPreview.output : latest?.output;
  const sourceTaxYear: number | null = pdfUploading ? null : (latest?.sourceTaxYear ?? null);
  const displayTaxYear: number | null = sourceTaxYear;

  const effectiveGrossPerUnit: number = (() => {
    // Prefer live local state so flipping saved returns reflects immediately.
    const liveGP = nf(manualGrossProfit);
    const liveUnits = nf(annualUnitsSold);
    if (liveGP > 0 && liveUnits > 0) return liveGP / liveUnits;
    const fromOutput = output?.breakevenDays?.effectiveGrossPerUnit ?? 0;
    if (fromOutput > 0) return fromOutput;
    const gp = latest?.input?.grossProfit ?? 0;
    const units = latest?.input?.annualUnitsSold ?? 0;
    if (gp > 0 && units > 0) return gp / units;
    return latest?.input?.avgGrossProfitPerUnit ?? 0;
  })();

  const displayDailyCostPerUnit: number = (() => {
    // Prefer live local state so flipping saved returns reflects immediately.
    const invUnits = nf(avgInventoryUnits);
    if (derivedOpEx > 0 && invUnits > 0) return derivedOpEx / (invUnits * 365);
    const fromOutput = output?.breakevenDays?.dailyCostPerUnit ?? 0;
    if (fromOutput > 0) return fromOutput;
    const totalOpEx =
      (latest?.input?.annualTotalOperatingExpenses ?? 0) ||
      (latest?.input?.annualTotalExpenses ?? 0);
    return totalOpEx > 0 && invUnits > 0 ? totalOpEx / (invUnits * 365) : 0;
  })();

  const displayBreakevenDays: number = (() => {
    const fromOutput = output?.breakevenDays?.days ?? 0;
    if (fromOutput > 0) return fromOutput;
    const roundedDailyCost = Math.round(displayDailyCostPerUnit * 100) / 100;
    return roundedDailyCost > 0
      ? Math.floor(effectiveGrossPerUnit / roundedDailyCost)
      : 0;
  })();

  const displayHasExpenseData: boolean = (() => {
    // Prefer live local state so flipping saved returns reflects immediately.
    if (derivedOpEx > 0 && nf(avgInventoryUnits) > 0 && effectiveGrossPerUnit > 0) return true;
    if (output?.breakevenDays?.hasExpenseData != null)
      return output.breakevenDays.hasExpenseData;
    const totalOpEx =
      (latest?.input?.annualTotalOperatingExpenses ?? 0) ||
      (latest?.input?.annualTotalExpenses ?? 0);
    return totalOpEx > 0 && nf(avgInventoryUnits) > 0 && effectiveGrossPerUnit > 0;
  })();

  const liveAvgInventoryUnits = nf(avgInventoryUnits);
  // Prefer local derivedOpEx (updated immediately when a saved return is loaded)
  // over latest?.input which only refreshes after a server round-trip.
  const liveTotalOperatingExpenses = derivedOpEx > 0
    ? derivedOpEx
    : (latest?.input?.annualTotalOperatingExpenses ?? latest?.input?.annualTotalExpenses ?? 0);
  const perUnitDailyBase =
    liveAvgInventoryUnits > 0 && liveTotalOperatingExpenses > 0
      ? liveTotalOperatingExpenses / (liveAvgInventoryUnits * 365)
      : liveTotalOperatingExpenses > 0 && (output?.inputsUsed?.avgInventoryUnits ?? 0) > 0
        ? liveTotalOperatingExpenses / ((output!.inputsUsed.avgInventoryUnits) * 365)
        : (output?.costToCarry?.carryingCostPerCarPerDay ?? 0);
  const perUnitDaily = perUnitDailyBase;
  const holdCost = perUnitDaily > 0
    ? (perUnitDaily * nf(holdingDays)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;

  // P&L vs Profile comparison data
  const profileGPU = profile?.avgGrossProfitPerUnit != null ? parseFloat(String(profile.avgGrossProfitPerUnit)) : null;
  const profileMonthlyRevenue = profile?.avgMonthlyRevenue != null
    ? parseFloat(String(profile.avgMonthlyRevenue))
    : (profile?.avgSalesPerMonth != null && profile?.avgVehicleValue != null
        ? parseFloat(String(profile.avgSalesPerMonth)) * parseFloat(String(profile.avgVehicleValue))
        : null);
  const profileMonthlyOpEx = profile?.monthlyOperatingExpenses != null ? parseFloat(String(profile.monthlyOperatingExpenses)) : null;

  const plMonthlyRevenue = nf(manualRevenue) > 0 ? nf(manualRevenue) / 12 : null;
  const plMonthlyOpEx = derivedOpEx > 0 ? derivedOpEx / 12 : null;
  const plGPU = derivedAvgGross ?? null;

  const showPLProfileComparison = profile && (nf(manualRevenue) > 0 || nf(manualGrossProfit) > 0 || derivedOpEx > 0);

  // Cost Recovery Calculator — Revenue Needed = Cost ÷ (Net Profit / Revenue)
  //                            Units Needed   = Cost ÷ Net Profit per Unit
  //                            Revenue Needed = Units Needed × Avg Sale Price
  const crcGrossProfit = nf(manualGrossProfit);
  const crcRevenue = nf(manualRevenue);
  const crcTotalExpenses = derivedOpEx;
  const crcAnnualUnits = nf(annualUnitsSold);
  const crcNetProfit = crcGrossProfit - crcTotalExpenses;
  // Net margin based on revenue (not gross profit)
  const crcNetMargin =
    crcGrossProfit > 0 && crcRevenue > 0 && crcTotalExpenses > 0 && crcNetProfit > 0
      ? crcNetProfit / crcRevenue
      : null;
  const crcGrossPerUnit = derivedAvgGross ?? null;
  const crcNetProfitPerUnit: number | null =
    crcAnnualUnits > 0 && crcNetProfit > 0
      ? crcNetProfit / crcAnnualUnits
      : null;
  // Avg sale price = Revenue ÷ Units (used for Revenue = Units × Avg Sale Price display)
  const crcAvgSalePrice: number | null =
    crcRevenue > 0 && crcAnnualUnits > 0 ? crcRevenue / crcAnnualUnits : null;
  const crcIncurredCostNum = parseFloat(incurredCost.replace(/,/g, "")) || 0;
  // Revenue Needed = Cost ÷ (Net Profit / Revenue)
  const crcSalesNeeded: number | null =
    crcIncurredCostNum > 0 && crcNetMargin != null && crcNetMargin > 0
      ? crcIncurredCostNum / crcNetMargin
      : null;
  // Units Needed = Cost ÷ Net Profit per Unit
  const crcUnitsNeeded: number | null =
    crcIncurredCostNum > 0 && crcNetProfitPerUnit != null && crcNetProfitPerUnit > 0
      ? Math.ceil(crcIncurredCostNum / crcNetProfitPerUnit)
      : null;
  const hasCRC = crcGrossProfit > 0 && crcRevenue > 0 && crcTotalExpenses > 0;

  const saveRecoveryNotice = recoveryUnavailable && taxReturnSaveRequest.isCurrent(taxReturnSaveScope(currentUser)) ? (
    <div role="status" className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm" data-testid="notice-tax-save-recovery">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
      <p>{t("operations:analytics.saveRecoveryUnavailable")}</p>
    </div>
  ) : null;

  return (
    <div className="space-y-5">
      {!showSaveReturnDialog && !showManualEntryDialog && saveRecoveryNotice}

      {latest?.lastSavedAt && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="text-analytics-last-saved">
          <CheckCircle2 className="w-3.5 h-3.5 text-chart-3" />
          {t("operations:step1.lastSaved", { date: fmtDateTime(latest.lastSavedAt) })}
        </div>
      )}

      {/* ── Step 1: P&L Manual Entry ── */}
      <Card className="border-primary/20 bg-gradient-to-br from-primary/3 to-transparent">
        <CardHeader className="pb-2 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-sm font-semibold">{t("operations:step1.title")}</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">{t("operations:step1.subtitle")}</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              {/* Hidden file input for PDF upload */}
              <input
                ref={pdfInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                data-testid="input-tax-return-pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  // Clear current data now that the user has actually selected a file
                  setManualRevenue("");
                  setManualGrossProfit("");
                  setManualTotalIncome("");
                  setManualOpEx("");
                  setDynamicExpenseItems([]);
                  setLoadedSavedReturn(null);
                  if (currentUser?.id) localStorage.removeItem(`lastTaxReturnId:${currentUser.ownerUserId ?? currentUser.id}`);
                  setExtractionSource(null);
                  setTaxReturnSourceFields([]);
                  setAiExtractedValues({});
                  setExtractedOtherIncome(null);
                  setExtractionBannerWarnings([]);
                  setIsEditingTaxReturn(false);
                  setIsPendingNewReturn(true);
                  handlePdfUpload(file);
                }}
              />
              <div className="flex items-center gap-2">
              <div className="flex flex-col items-start gap-0.5">
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2.5 text-xs"
                    disabled={pdfUploading}
                    onClick={() => {
                      setShowAddReturnChoiceDialog(true);
                    }}
                    data-testid="button-add-tax-return"
                  >
                    {pdfUploading ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Upload className="w-3 h-3 mr-1" />
                    )}
                    {pdfUploading ? t("operations:step1.extracting") : t("operations:step1.addReturn")}
                  </Button>
                  {pdfUploading && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => cleanupPolling(true, true)}
                      title={t("operations:step1.cancelExtraction")}
                      data-testid="button-cancel-extraction"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                {pdfUploading && extractionProgressText && (
                  <span className="text-[10px] text-muted-foreground leading-none pl-0.5" data-testid="text-extraction-progress">
                    {extractionProgressText}
                  </span>
                )}
                {extractionError && !pdfUploading && (
                  <span className="text-[10px] text-destructive leading-none pl-0.5 max-w-[200px]" data-testid="text-extraction-error">
                    {extractionError}
                  </span>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2.5 text-xs"
                onClick={() => setShowPastStatements(true)}
                data-testid="button-past-statements"
              >
                <FileText className="w-3 h-3 mr-1" />
                {t("operations:step1.allTaxReturns")}
              </Button>
              {!isEditingTaxReturn ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => {
                    // Load saved values into the fields when entering edit mode
                    // (they are intentionally blank in view mode to avoid looking pre-filled)
                    const inp = latest?.input;
                    const editRevenue = manualRevenue || (inp?.revenue ? String(inp.revenue) : "");
                    const editGrossProfit = manualGrossProfit || (inp?.grossProfit ? String(inp.grossProfit) : "");
                    const editOpEx = manualOpEx || (inp?.annualTotalOperatingExpenses ? String(inp.annualTotalOperatingExpenses) : "");
                    setManualRevenue(editRevenue);
                    setManualGrossProfit(editGrossProfit);
                    setManualOpEx(editOpEx);

                    // Initialize aiExtractedValues from the saved input for any field that
                    // is still provenance-tracked (i.e., was originally AI-extracted and not yet
                    // manually overridden). This lets "Restore AI value" work across sessions.
                    const savedSourceFields: string[] = inp?.taxReturnSourceFields ?? taxReturnSourceFields;
                    const initAiValues: { revenue?: string; grossProfit?: string; opEx?: string } = {};
                    if (savedSourceFields.includes("revenue") && inp?.revenue) initAiValues.revenue = String(inp.revenue);
                    if (savedSourceFields.includes("grossProfit") && inp?.grossProfit) initAiValues.grossProfit = String(inp.grossProfit);
                    if (savedSourceFields.includes("annualTotalOperatingExpenses") && inp?.annualTotalOperatingExpenses) {
                      initAiValues.opEx = String(inp.annualTotalOperatingExpenses);
                    }
                    // Override any stale in-session values with the authoritative saved values.
                    // Saved input is the ground truth for what the AI originally extracted.
                    setAiExtractedValues(prev => ({ ...prev, ...initAiValues }));

                    setTaxReturnSnapshot({
                      annualUnitsSold,
                      manualRevenue: editRevenue,
                      manualGrossProfit: editGrossProfit,
                      manualOpEx: editOpEx,
                      dynamicExpenseItems: dynamicExpenseItems.map(i => ({ ...i })),
                      taxReturnSourceFields: [...taxReturnSourceFields],
                    });
                    setIsEditingTaxReturn(true);
                  }}
                  data-testid="button-edit-tax-return"
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  {t("common:edit")}
                </Button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    className="h-7 px-2.5 text-xs"
                    onClick={handleSaveInputs}
                    disabled={saving}
                    data-testid="button-save-tax-return"
                  >
                    {saving ? t("common:saving") : t("common:save")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2.5 text-xs"
                    onClick={() => {
                      if (taxReturnSnapshot) {
                        // Cancel must be the final writer: drop any queued
                        // auto-save of the abandoned edit and re-persist the
                        // snapshot values, so neither the debounce timer nor
                        // the unmount keepalive flush can resurrect the edit.
                        postBasicInputs(JSON.stringify(buildBasicInputsBody({
                          avgInventoryUnits,
                          annualUnitsSold: taxReturnSnapshot.annualUnitsSold,
                          avgTurnDaysOverride,
                        })));
                        setAnnualUnitsSold(taxReturnSnapshot.annualUnitsSold);
                        setManualRevenue(taxReturnSnapshot.manualRevenue);
                        setManualGrossProfit(taxReturnSnapshot.manualGrossProfit);
                        setManualTotalIncome("");
                        setManualOpEx(taxReturnSnapshot.manualOpEx);
                        setDynamicExpenseItems(taxReturnSnapshot.dynamicExpenseItems.map(i => ({ ...i })));
                        setTaxReturnSourceFields(taxReturnSnapshot.taxReturnSourceFields);
                        setTaxReturnSnapshot(null);
                      }
                      setIsEditingTaxReturn(false);
                    }}
                    disabled={saving}
                    data-testid="button-cancel-tax-return"
                  >
                    {t("common:cancel")}
                  </Button>
                </div>
              )}
              </div>
              {(savedTaxReturns as any[]).length >= 2 && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2.5 text-xs"
                    onClick={() => setShowTrendsDialog(true)}
                    data-testid="button-trends-dialog"
                  >
                    <TrendingUp className="w-3 h-3 mr-1" />
                    {t("operations:step1.trends")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2.5 text-xs"
                    onClick={() => setShowCompareDialog(true)}
                    data-testid="button-compare-dialog"
                  >
                    <BarChart3 className="w-3 h-3 mr-1" />
                    {t("operations:step1.compare")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3 pt-2">
          {/* Extraction warning banners (large doc, skipped pages) */}
          {extractionBannerWarnings.length > 0 && (
            <div className="space-y-2" data-testid="extraction-warnings-banner">
              {extractionBannerWarnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-300/60 bg-amber-50/60 dark:border-amber-700/50 dark:bg-amber-950/30" data-testid={`extraction-warning-${i}`}>
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 dark:text-amber-300 leading-snug">{w}</p>
                  <button
                    className="ml-auto text-amber-500/60 hover:text-amber-600 dark:hover:text-amber-400 transition-colors shrink-0"
                    onClick={() => setExtractionBannerWarnings(prev => prev.filter((_, idx) => idx !== i))}
                    aria-label={t("operations:step1.dismissWarning")}
                    data-testid={`button-dismiss-warning-${i}`}
                  >✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Document on file card — hidden when viewing a saved return or uploading a new one */}
          {!loadedSavedReturn && !pdfUploading && latest?.input?.taxReturnDoc && (
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-primary/20 bg-primary/5" data-testid="card-tax-return-doc">
              <FileText className="w-4 h-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-primary truncate" data-testid="text-tax-return-filename">{latest.input.taxReturnDoc.fileName}</p>
                <p className="text-[10px] text-muted-foreground" data-testid="text-tax-return-uploaddate">
                  {t("operations:step1.uploaded", { date: fmtDate(latest.input.taxReturnDoc.uploadedAt) })}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                onClick={() => handleDeleteTaxReturnDoc()}
                disabled={docDeleting}
                data-testid="button-delete-tax-return-doc"
              >
                {docDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : t("common:delete")}
              </Button>
            </div>
          )}
          {/* Year not detected notice — shown when a doc is on file but no year could be determined */}
          {!loadedSavedReturn && !pdfUploading && !!latest?.input?.taxReturnDoc && !latest?.input?.taxYear && !(/\b(20\d{2})\b/.test(latest?.input?.taxReturnDoc?.fileName ?? "")) && (
            <div className="flex flex-col gap-2 px-3 py-2.5 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30" data-testid="panel-tax-year-notice">
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                <span className="text-xs font-medium text-amber-800 dark:text-amber-300">{t("operations:step1.yearNotDetected")}</span>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={pendingTaxYear}
                  onChange={e => setPendingTaxYear(e.target.value)}
                  className="h-7 text-xs bg-transparent border border-amber-300 dark:border-amber-700 rounded px-1.5 outline-none text-foreground shrink-0"
                  data-testid="select-tax-year-notice"
                >
                  <option value="">{t("operations:step1.selectYear")}</option>
                  {Array.from({ length: new Date().getFullYear() - 2014 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={String(y)}>{y}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  className="h-7 px-3 text-xs"
                  onClick={handleSaveTaxYear}
                  disabled={savingTaxYear || !pendingTaxYear}
                  data-testid="button-confirm-tax-year"
                >
                  {savingTaxYear ? <Loader2 className="w-3 h-3 animate-spin" /> : t("operations:step1.confirmYear")}
                </Button>
              </div>
            </div>
          )}

          {/* Source note after PDF extraction */}
          {isEditingTaxReturn && extractionSource && (
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground bg-muted/40 rounded-md px-3 py-1.5">
              <FileText className="w-3 h-3 flex-shrink-0" />
              {t("operations:step1.sourceExtractedFrom")} <span className="font-medium truncate max-w-[200px]">{extractionSource}</span>
              <button
                className="ml-auto text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                onClick={() => setExtractionSource(null)}
                aria-label={t("operations:step1.dismissSourceNote")}
                data-testid="button-dismiss-source-note"
              >✕</button>
            </div>
          )}

          {/* Save Tax Return UI — only shown when a new return has been uploaded and not yet saved */}
          {isPendingNewReturn && !loadedSavedReturn && (nf(manualRevenue) > 0 || nf(manualGrossProfit) > 0) && (
            <div className="flex flex-col gap-2 px-3 py-2.5 rounded-lg border border-primary/20 bg-primary/5" data-testid="panel-save-tax-return">
              <div className="flex items-center gap-1.5">
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <span className="text-xs font-medium text-foreground">{t("operations:step1.saveTaxReturn")}</span>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={saveTaxReturnYear}
                  onChange={e => setSaveTaxReturnYear(e.target.value)}
                  className="h-7 text-xs bg-transparent border border-border rounded px-1.5 outline-none text-foreground shrink-0"
                  data-testid="select-save-tax-return-year"
                >
                  <option value="">{t("operations:step1.year")}</option>
                  {Array.from({ length: new Date().getFullYear() - 2014 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={String(y)}>{y}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder={t("operations:step1.namePlaceholder")}
                  value={saveTaxReturnName}
                  onChange={e => setSaveTaxReturnName(e.target.value)}
                  className="flex-1 min-w-0 text-xs bg-transparent outline-none placeholder:text-muted-foreground"
                  data-testid="input-save-tax-return-name"
                  onKeyDown={e => { if (e.key === "Enter") handleSaveTaxReturn(); }}
                />
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 flex-1">
                  <label className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                    {t("operations:step1.unitsSoldPerYr")} <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    placeholder={t("operations:step1.unitsPlaceholder")}
                    value={annualUnitsSold}
                    onChange={e => setAnnualUnitsSold(e.target.value)}
                    className="w-24 h-7 text-xs bg-transparent border border-border rounded px-2 outline-none text-foreground placeholder:text-muted-foreground"
                    data-testid="input-save-tax-return-units"
                    onKeyDown={e => { if (e.key === "Enter") handleSaveTaxReturn(); }}
                  />
                </div>
                <Button
                  size="sm"
                  variant="default"
                  className="h-7 px-3 text-xs shrink-0"
                  onClick={handleSaveTaxReturn}
                  disabled={saveTaxReturnMutation.isPending || !saveTaxReturnName.trim() || !saveTaxReturnYear || !annualUnitsSold || nf(annualUnitsSold) <= 0}
                  data-testid="button-save-tax-return"
                >
                  {saveTaxReturnMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  {t("operations:step1.saveTaxReturnBtn")}
                </Button>
              </div>
            </div>
          )}

          {/* Loaded saved return banner */}
          {loadedSavedReturn && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-300/60 bg-blue-50/60 dark:border-blue-700/40 dark:bg-blue-950/20" data-testid="banner-loaded-saved-return">
              <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
              <span className="text-xs text-blue-800 dark:text-blue-300 flex-1 min-w-0 truncate">
                {t("operations:step1.viewing")} <span className="font-semibold">{loadedSavedReturn.name}</span>
              </span>
              {(savedTaxReturns as any[]).find((r: any) => r.id === loadedSavedReturn.id)?.cloudPath && (
                <a
                  href={`/api/saved-tax-returns/${loadedSavedReturn.id}/pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline shrink-0 flex items-center gap-1"
                  data-testid="link-view-saved-return-pdf"
                >
                  <FileText className="w-3 h-3" />
                  {t("operations:step1.openPdf")}
                </a>
              )}
            </div>
          )}

          {/* Past Returns modal */}
          <Dialog
            open={showPastStatements}
            onOpenChange={open => {
              setShowPastStatements(open);
              if (!open) {
                setCompareMode(false);
                setSelectedCompareIds([]);
                setShowCompareView(false);
                setTrendsMode(false);
                setEditingReturnId(null);
                setEditingReturnYear("");
                setEditingReturnName("");
              }
            }}
          >
            <DialogContent className="max-w-2xl w-full max-h-[85vh] flex flex-col p-0 gap-0" data-testid="dialog-past-returns">
              <DialogHeader className="px-5 pt-5 pb-0 shrink-0">
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-base font-semibold">{t("operations:step1.allTaxReturns")}</DialogTitle>
                  {savedTaxReturns.length > 0 && (
                    <div className="flex items-center gap-2 mr-6">
                      <Button
                        size="sm"
                        variant={trendsMode ? "default" : "outline"}
                        className={trendsMode ? "h-7 px-3 text-xs" : "h-7 px-3 text-xs bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900"}
                        onClick={() => {
                          setTrendsMode(v => !v);
                          if (!trendsMode) {
                            setCompareMode(false);
                            setSelectedCompareIds([]);
                            setShowCompareView(false);
                          }
                        }}
                        data-testid="button-trends-mode"
                      >
                        {t("operations:step1.trends")}
                      </Button>
                      <Button
                        size="sm"
                        variant={compareMode ? "default" : "outline"}
                        className={compareMode ? "h-7 px-3 text-xs" : "h-7 px-3 text-xs bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900"}
                        onClick={() => {
                          setCompareMode(v => !v);
                          setSelectedCompareIds([]);
                          setShowCompareView(false);
                          if (!compareMode) setTrendsMode(false);
                        }}
                        data-testid="button-compare-mode"
                      >
                        {t("operations:step1.compare")}
                      </Button>
                    </div>
                  )}
                </div>
                <DialogDescription className="sr-only">{t("operations:pastReturns.dialogDesc")}</DialogDescription>
              </DialogHeader>
              <div className="overflow-y-auto flex-1 mt-4">
                {savedTaxReturns.length === 0 && (
                  <div className="px-4 py-8 text-center" data-testid="empty-state-past-returns">
                    <FileText className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">{t("operations:pastReturns.noSavedReturns")}</p>
                    <p className="text-xs text-muted-foreground/70 mt-1">{t("operations:pastReturns.uploadPrompt")}</p>
                  </div>
                )}
                {compareMode && savedTaxReturns.length > 0 && (
                  <div className="px-5 py-2 text-xs text-muted-foreground bg-muted/10 border-b border-border">
                    {selectedCompareIds.length < 2 ? t("operations:pastReturns.selectTwoToCompare") : t("operations:pastReturns.showingComparison")}
                  </div>
                )}
                {trendsMode && (() => {
                  const parseLabelYear = (entry: any): number => {
                    const match = typeof entry.name === "string" ? entry.name.match(/^(\d{4})\s*[–\-]/) : null;
                    return match ? parseInt(match[1], 10) : new Date(entry.savedAt).getFullYear();
                  };

                  // Include active (unsaved) extraction if it has a year not already in saved snapshots
                  const activeYear = latest?.sourceTaxYear;
                  const activeInp = latest?.input;
                  const activeSynthetic = (activeYear && activeInp && (activeInp.revenue || activeInp.grossProfit) &&
                    !(savedTaxReturns as any[]).some((r: any) => parseLabelYear(r) === activeYear))
                    ? {
                        id: "__active__",
                        name: `${activeYear} – (uploaded)`,
                        snapshotJson: JSON.stringify({
                          revenue: activeInp.revenue,
                          grossProfit: activeInp.grossProfit,
                          annualTotalOperatingExpenses: activeInp.annualTotalOperatingExpenses,
                        }),
                        savedAt: new Date().toISOString(),
                      }
                    : null;
                  const returnsForChart = activeSynthetic
                    ? [...(savedTaxReturns as any[]), activeSynthetic]
                    : (savedTaxReturns as any[]);

                  const allYears = [...new Set(
                    returnsForChart.map((e: any) => parseLabelYear(e))
                  )].sort((a, b) => a - b);

                  const trendData = [...returnsForChart]
                    .filter((entry: any) => {
                      const year = parseLabelYear(entry);
                      if (trendYearFrom !== null && year < trendYearFrom) return false;
                      if (trendYearTo !== null && year > trendYearTo) return false;
                      return true;
                    })
                    .sort((a: any, b: any) => {
                      const yearDiff = parseLabelYear(a) - parseLabelYear(b);
                      if (yearDiff !== 0) return yearDiff;
                      return new Date(a.savedAt).getTime() - new Date(b.savedAt).getTime();
                    })
                    .map((entry: any) => {
                      const snap = (() => { try { return JSON.parse(entry.snapshotJson); } catch { return {}; } })();
                      const gp = snap.grossProfit;
                      const opEx = snap.annualTotalOperatingExpenses;
                      const noi = typeof gp === "number" && typeof opEx === "number" ? gp - opEx : null;
                      const rGross = typeof snap.rental?.rentalGrossIncome === "number" ? snap.rental.rentalGrossIncome : null;
                      const rExp = typeof snap.rental?.rentalTotalExpenses === "number" ? snap.rental.rentalTotalExpenses : null;
                      const rNet = typeof snap.rental?.rentalNetIncome === "number" ? snap.rental.rentalNetIncome : null;
                      return {
                        name: entry.name,
                        revenue: typeof snap.revenue === "number" ? Math.round(snap.revenue / 1000) : null,
                        grossProfit: typeof gp === "number" ? Math.round(gp / 1000) : null,
                        noi: noi != null ? Math.round(noi / 1000) : null,
                        rentalGross: rGross != null ? Math.round(rGross / 1000) : null,
                        rentalExpenses: rExp != null ? Math.round(rExp / 1000) : null,
                        rentalNet: rNet != null ? Math.round(rNet / 1000) : null,
                      };
                    });
                  const hasRentalTrendData = trendData.some(d => d.rentalGross != null || d.rentalExpenses != null || d.rentalNet != null);
                  const hasData = trendData.some(d => d.revenue != null || d.grossProfit != null || d.noi != null || d.rentalGross != null || d.rentalExpenses != null || d.rentalNet != null);
                  return (
                    <div className="px-5 pt-4 pb-5 border-b border-border" data-testid="panel-trends-chart">
                      {allYears.length > 1 && (
                        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="trends-year-filter">
                          <span className="text-[11px] text-muted-foreground shrink-0">{t("operations:pastReturns.showYears")}</span>
                          <select
                            value={trendYearFrom ?? ""}
                            onChange={e => setTrendYearFrom(e.target.value === "" ? null : Number(e.target.value))}
                            className="text-[11px] h-6 px-1 rounded border border-border bg-background text-foreground"
                            data-testid="select-trend-year-from"
                          >
                            <option value="">{t("operations:pastReturns.fromAll")}</option>
                            {allYears.map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                          <span className="text-[11px] text-muted-foreground">–</span>
                          <select
                            value={trendYearTo ?? ""}
                            onChange={e => setTrendYearTo(e.target.value === "" ? null : Number(e.target.value))}
                            className="text-[11px] h-6 px-1 rounded border border-border bg-background text-foreground"
                            data-testid="select-trend-year-to"
                          >
                            <option value="">{t("operations:pastReturns.toAll")}</option>
                            {allYears.map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                          {(trendYearFrom !== null || trendYearTo !== null) && (
                            <button
                              onClick={() => { setTrendYearFrom(null); setTrendYearTo(null); }}
                              className="text-[11px] text-muted-foreground underline hover:text-foreground"
                              data-testid="button-trend-clear-filter"
                            >
                              {t("operations:pastReturns.clear")}
                            </button>
                          )}
                        </div>
                      )}
                      {!hasData ? (
                        <p className="text-sm text-muted-foreground text-center py-6">
                          {trendYearFrom !== null || trendYearTo !== null
                            ? t("operations:pastReturns.noMatchRange")
                            : t("operations:pastReturns.notEnoughData")}
                        </p>
                      ) : (
                        <>
                          <div className="flex flex-wrap gap-2 mb-3">
                            <button
                              onClick={() => updateTrendMetrics(prev => ({ ...prev, revenue: !prev.revenue }))}
                              data-testid="toggle-metric-revenue"
                              className={`px-3 py-1 rounded-full text-[11px] font-medium border transition-colors ${visibleTrendMetrics.revenue ? "bg-[#6366f1] text-white border-[#6366f1]" : "bg-transparent text-muted-foreground border-border hover:border-[#6366f1] hover:text-[#6366f1]"}`}
                            >
                              {t("operations:pastReturns.metricRevenue")}
                            </button>
                            <button
                              onClick={() => updateTrendMetrics(prev => ({ ...prev, grossProfit: !prev.grossProfit }))}
                              data-testid="toggle-metric-gross-profit"
                              className={`px-3 py-1 rounded-full text-[11px] font-medium border transition-colors ${visibleTrendMetrics.grossProfit ? "bg-[#22c55e] text-white border-[#22c55e]" : "bg-transparent text-muted-foreground border-border hover:border-[#22c55e] hover:text-[#22c55e]"}`}
                            >
                              {t("operations:pastReturns.metricGrossProfit")}
                            </button>
                            <button
                              onClick={() => updateTrendMetrics(prev => ({ ...prev, noi: !prev.noi }))}
                              data-testid="toggle-metric-noi"
                              className={`px-3 py-1 rounded-full text-[11px] font-medium border transition-colors ${visibleTrendMetrics.noi ? "bg-[#f59e0b] text-white border-[#f59e0b]" : "bg-transparent text-muted-foreground border-border hover:border-[#f59e0b] hover:text-[#f59e0b]"}`}
                            >
                              {t("operations:pastReturns.metricNoi")}
                            </button>
                            {hasRentalTrendData && (
                              <>
                                <button
                                  onClick={() => updateTrendMetrics(prev => ({ ...prev, rentalGross: !prev.rentalGross }))}
                                  data-testid="toggle-metric-rental-gross"
                                  className={`px-3 py-1 rounded-full text-[11px] font-medium border transition-colors ${visibleTrendMetrics.rentalGross ? "bg-[#06b6d4] text-white border-[#06b6d4]" : "bg-transparent text-muted-foreground border-border hover:border-[#06b6d4] hover:text-[#06b6d4]"}`}
                                >
                                  {t("operations:pastReturns.metricRentalGross")}
                                </button>
                                <button
                                  onClick={() => updateTrendMetrics(prev => ({ ...prev, rentalExpenses: !prev.rentalExpenses }))}
                                  data-testid="toggle-metric-rental-expenses"
                                  className={`px-3 py-1 rounded-full text-[11px] font-medium border transition-colors ${visibleTrendMetrics.rentalExpenses ? "bg-[#ec4899] text-white border-[#ec4899]" : "bg-transparent text-muted-foreground border-border hover:border-[#ec4899] hover:text-[#ec4899]"}`}
                                >
                                  {t("operations:pastReturns.metricRentalExpenses")}
                                </button>
                                <button
                                  onClick={() => updateTrendMetrics(prev => ({ ...prev, rentalNet: !prev.rentalNet }))}
                                  data-testid="toggle-metric-rental-net"
                                  className={`px-3 py-1 rounded-full text-[11px] font-medium border transition-colors ${visibleTrendMetrics.rentalNet ? "bg-[#8b5cf6] text-white border-[#8b5cf6]" : "bg-transparent text-muted-foreground border-border hover:border-[#8b5cf6] hover:text-[#8b5cf6]"}`}
                                >
                                  {t("operations:pastReturns.metricRentalNet")}
                                </button>
                              </>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground mb-3">{t("operations:pastReturns.valuesInThousands")}</p>
                          <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={trendData} margin={{ top: 4, right: 8, left: -8, bottom: 4 }}>
                              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                              <XAxis dataKey="name" tick={{ fontSize: 12, fontWeight: 600 }} />
                              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => {
                                const n = Number(v);
                                if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}M`;
                                return `$${n.toLocaleString()}K`;
                              }} />
                              <RechartsTooltip
                                formatter={(value: number, name: string) => {
                                  if (Math.abs(value) >= 1000) return [`$${(value / 1000).toFixed(2)}M`, name];
                                  return [`$${Number(value).toLocaleString()}K`, name];
                                }}
                                contentStyle={{ fontSize: 11 }}
                              />
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              {visibleTrendMetrics.revenue && (
                                <Line
                                  type="monotone"
                                  dataKey="revenue"
                                  name={t("operations:pastReturns.metricRevenue")}
                                  stroke="#6366f1"
                                  strokeWidth={2}
                                  dot={{ r: 3 }}
                                  connectNulls
                                />
                              )}
                              {visibleTrendMetrics.grossProfit && (
                                <Line
                                  type="monotone"
                                  dataKey="grossProfit"
                                  name={t("operations:pastReturns.metricGrossProfit")}
                                  stroke="#22c55e"
                                  strokeWidth={2}
                                  dot={{ r: 3 }}
                                  connectNulls
                                />
                              )}
                              {visibleTrendMetrics.noi && (
                                <Line
                                  type="monotone"
                                  dataKey="noi"
                                  name={t("operations:pastReturns.metricNoi")}
                                  stroke="#f59e0b"
                                  strokeWidth={2}
                                  dot={{ r: 3 }}
                                  connectNulls
                                />
                              )}
                              {hasRentalTrendData && visibleTrendMetrics.rentalGross && (
                                <Line
                                  type="monotone"
                                  dataKey="rentalGross"
                                  name={t("operations:pastReturns.rentalGrossIncome")}
                                  stroke="#06b6d4"
                                  strokeWidth={2}
                                  strokeDasharray="5 3"
                                  dot={{ r: 3 }}
                                  connectNulls
                                />
                              )}
                              {hasRentalTrendData && visibleTrendMetrics.rentalExpenses && (
                                <Line
                                  type="monotone"
                                  dataKey="rentalExpenses"
                                  name={t("operations:pastReturns.metricRentalExpenses")}
                                  stroke="#ec4899"
                                  strokeWidth={2}
                                  strokeDasharray="5 3"
                                  dot={{ r: 3 }}
                                  connectNulls
                                />
                              )}
                              {hasRentalTrendData && visibleTrendMetrics.rentalNet && (
                                <Line
                                  type="monotone"
                                  dataKey="rentalNet"
                                  name={t("operations:pastReturns.rentalNetIncome")}
                                  stroke="#8b5cf6"
                                  strokeWidth={2}
                                  strokeDasharray="5 3"
                                  dot={{ r: 3 }}
                                  connectNulls
                                />
                              )}
                            </LineChart>
                          </ResponsiveContainer>
                        </>
                      )}
                    </div>
                  );
                })()}
                {/* Compare view inside modal */}
                {showCompareView && selectedCompareIds.length === 2 && (() => {
                  const [a, b] = selectedCompareIds.map(id => savedTaxReturns.find((r: any) => r.id === id)).filter(Boolean);
                  if (!a || !b) return null;
                  const parseSnap = (entry: any) => { try { return JSON.parse(entry.snapshotJson); } catch { return {}; } };
                  const snapA = parseSnap(a);
                  const snapB = parseSnap(b);
                  const gpA = snapA.grossProfit ?? 0;
                  const gpB = snapB.grossProfit ?? 0;
                  const revA = snapA.revenue ?? 0;
                  const revB = snapB.revenue ?? 0;
                  const cogsA = revA > 0 ? revA - gpA : null;
                  const cogsB = revB > 0 ? revB - gpB : null;
                  const gpuA = snapA.annualUnitsSold > 0 ? gpA / snapA.annualUnitsSold : null;
                  const gpuB = snapB.annualUnitsSold > 0 ? gpB / snapB.annualUnitsSold : null;
                  const opExA = snapA.annualTotalOperatingExpenses ?? 0;
                  const opExB = snapB.annualTotalOperatingExpenses ?? 0;
                  const netA = gpA > 0 && opExA > 0 ? gpA - opExA : null;
                  const netB = gpB > 0 && opExB > 0 ? gpB - opExB : null;
                  // Determine which entry is more recent — the newer one gets the +/- % badge
                  // Year from the return name takes priority; savedAt is only a tiebreaker when
                  // years are equal OR either year is unparseable.
                  const _yearMatchA = (a as any).name?.match(/(\d{4})/);
                  const _yearMatchB = (b as any).name?.match(/(\d{4})/);
                  const yearA: number | null = _yearMatchA ? parseInt(_yearMatchA[1], 10) : null;
                  const yearB: number | null = _yearMatchB ? parseInt(_yearMatchB[1], 10) : null;
                  const aIsNewer = (yearA !== null && yearB !== null && yearA !== yearB)
                    ? yearA > yearB
                    : new Date((a as any).savedAt).getTime() >= new Date((b as any).savedAt).getTime();
                  function pctBadge(newVal: number | null, oldVal: number | null) {
                    if (newVal == null || oldVal == null || oldVal === 0) return null;
                    const pct = ((newVal - oldVal) / Math.abs(oldVal)) * 100;
                    if (!Number.isFinite(pct)) return null;
                    const pos = pct >= 0;
                    return (
                      <span className={`ml-1.5 text-[10px] font-semibold px-1 py-0.5 rounded ${pos ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"}`}>
                        {pos ? "+" : ""}{pct.toFixed(1)}%
                      </span>
                    );
                  }
                  type Row = { label: string; valA: string; valB: string; numA: number | null; numB: number | null };
                  const rows: Row[] = [
                    { label: t("operations:pastReturns.rowAnnualRevenue"), valA: fmt$(revA), valB: fmt$(revB), numA: revA, numB: revB },
                    { label: t("operations:pastReturns.rowCogs"), valA: cogsA != null ? fmt$(cogsA) : "—", valB: cogsB != null ? fmt$(cogsB) : "—", numA: cogsA, numB: cogsB },
                    { label: t("operations:pastReturns.metricGrossProfit"), valA: fmt$(gpA), valB: fmt$(gpB), numA: gpA, numB: gpB },
                    { label: t("operations:pastReturns.rowGpPerUnit"), valA: gpuA != null ? fmt$(gpuA) : "—", valB: gpuB != null ? fmt$(gpuB) : "—", numA: gpuA, numB: gpuB },
                    { label: t("operations:pastReturns.rowOperatingExpenses"), valA: opExA > 0 ? fmt$(opExA) : "—", valB: opExB > 0 ? fmt$(opExB) : "—", numA: opExA || null, numB: opExB || null },
                    { label: t("operations:pastReturns.rowNetProfit"), valA: netA != null ? fmt$(netA) : "—", valB: netB != null ? fmt$(netB) : "—", numA: netA, numB: netB },
                  ];
                  const expensesA: { label: string; amount: number }[] = Array.isArray(snapA.dynamicExpenses) ? snapA.dynamicExpenses : [];
                  const expensesB: { label: string; amount: number }[] = Array.isArray(snapB.dynamicExpenses) ? snapB.dynamicExpenses : [];
                  const allLabels = Array.from(new Set([...expensesA.map(e => e.label), ...expensesB.map(e => e.label)]));
                  return (
                    <div className="border-b border-border" data-testid="panel-compare-view">
                      <div className="flex items-center justify-between px-5 py-3 bg-muted/20 border-b border-border">
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("operations:pastReturns.sideBySide")}</span>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => { setShowCompareView(false); setCompareMode(false); setSelectedCompareIds([]); }}
                          aria-label={t("operations:pastReturns.closeComparison")}
                          data-testid="button-close-compare"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 border-b border-border">
                        {([a, b] as any[]).map((entry, idx) => {
                          const isNewer = idx === 0 ? aIsNewer : !aIsNewer;
                          return (
                            <div key={idx} className={`px-5 py-2.5 ${idx === 0 ? "border-r border-border" : ""} bg-muted/10`}>
                              <div className="flex items-center gap-1.5">
                                <p className="text-xs font-semibold truncate">{entry.name}</p>
                                {isNewer && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0">{t("operations:pastReturns.mostRecent")}</span>}
                              </div>
                              <p className="text-[10px] text-muted-foreground truncate">{entry.fileName}</p>
                            </div>
                          );
                        })}
                      </div>
                      <div>
                        {rows.map(({ label, valA, valB, numA, numB }) => {
                          const newerNum = aIsNewer ? numA : numB;
                          const olderNum = aIsNewer ? numB : numA;
                          const badge = pctBadge(newerNum, olderNum);
                          return (
                            <div key={label} className="grid grid-cols-2 border-b border-border last:border-0">
                              <div className="px-5 py-2.5 border-r border-border">
                                <p className="text-[10px] text-muted-foreground">{label}</p>
                                <div className="flex items-center flex-wrap">
                                  <p className="text-sm font-semibold">{valA}</p>
                                  {aIsNewer && badge}
                                </div>
                              </div>
                              <div className="px-5 py-2.5">
                                <p className="text-[10px] text-muted-foreground">{label}</p>
                                <div className="flex items-center flex-wrap">
                                  <p className="text-sm font-semibold">{valB}</p>
                                  {!aIsNewer && badge}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {/* Rental Income section in comparison */}
                        {(() => {
                          const rA = snapA.rental as { rentalGrossIncome?: number | null; rentalTotalExpenses?: number | null; rentalNetIncome?: number | null } | null | undefined;
                          const rB = snapB.rental as { rentalGrossIncome?: number | null; rentalTotalExpenses?: number | null; rentalNetIncome?: number | null } | null | undefined;
                          const hasRental = rA != null || rB != null;
                          if (!hasRental) return null;
                          type RentalRow = { label: string; net?: boolean; numA: number | null; numB: number | null };
                          const rentalRows: RentalRow[] = [
                            { label: t("operations:pastReturns.grossRentsReceived"), numA: rA?.rentalGrossIncome ?? null, numB: rB?.rentalGrossIncome ?? null },
                            { label: t("operations:pastReturns.totalRentalExpenses"), numA: rA?.rentalTotalExpenses ?? null, numB: rB?.rentalTotalExpenses ?? null },
                            { label: t("operations:pastReturns.netRentalIncome"), net: true, numA: rA?.rentalNetIncome ?? null, numB: rB?.rentalNetIncome ?? null },
                          ];
                          return (
                            <>
                              <div className="grid grid-cols-2 bg-muted/20 border-b border-border">
                                <div className="px-5 py-2 border-r border-border">
                                  <p className="text-xs font-semibold text-muted-foreground">{t("operations:pastReturns.rentalIncomeSection")}</p>
                                </div>
                                <div className="px-5 py-2">
                                  <p className="text-xs font-semibold text-muted-foreground">{t("operations:pastReturns.rentalIncomeSection")}</p>
                                </div>
                              </div>
                              {rentalRows.map(({ label, net, numA, numB }) => {
                                const badge = pctBadge(
                                  aIsNewer ? numA : numB,
                                  aIsNewer ? numB : numA,
                                );
                                const isNet = !!net;
                                return (
                                  <div key={label} className="grid grid-cols-2 border-b border-border last:border-0">
                                    <div className="px-5 py-2.5 border-r border-border">
                                      <p className="text-[10px] text-muted-foreground">{label}</p>
                                      <div className="flex items-center flex-wrap">
                                        <p className={`text-sm font-semibold ${isNet && numA != null && numA < 0 ? "text-destructive" : ""}`}>
                                          {numA != null ? fmt$(numA) : "—"}
                                        </p>
                                        {aIsNewer && badge}
                                      </div>
                                    </div>
                                    <div className="px-5 py-2.5">
                                      <p className="text-[10px] text-muted-foreground">{label}</p>
                                      <div className="flex items-center flex-wrap">
                                        <p className={`text-sm font-semibold ${isNet && numB != null && numB < 0 ? "text-destructive" : ""}`}>
                                          {numB != null ? fmt$(numB) : "—"}
                                        </p>
                                        {!aIsNewer && badge}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          );
                        })()}

                        {allLabels.length > 0 && (
                          <>
                            <div className="grid grid-cols-2 bg-muted/20 border-b border-border">
                              <div className="px-5 py-2 border-r border-border">
                                <p className="text-xs font-semibold text-muted-foreground">{t("operations:pastReturns.expenseBreakdown")}</p>
                              </div>
                              <div className="px-5 py-2">
                                <p className="text-xs font-semibold text-muted-foreground">{t("operations:pastReturns.expenseBreakdown")}</p>
                              </div>
                            </div>
                            {allLabels.map(label => {
                              const eA = expensesA.find(e => e.label === label);
                              const eB = expensesB.find(e => e.label === label);
                              const badge = pctBadge(
                                aIsNewer ? (eA?.amount ?? null) : (eB?.amount ?? null),
                                aIsNewer ? (eB?.amount ?? null) : (eA?.amount ?? null),
                              );
                              return (
                                <div key={label} className="grid grid-cols-2 border-b border-border last:border-0">
                                  <div className="px-5 py-2 border-r border-border">
                                    <p className="text-[10px] text-muted-foreground">{label}</p>
                                    <div className="flex items-center flex-wrap">
                                      <p className="text-sm">{eA ? fmt$(eA.amount) : "—"}</p>
                                      {aIsNewer && badge}
                                    </div>
                                  </div>
                                  <div className="px-5 py-2">
                                    <p className="text-[10px] text-muted-foreground">{label}</p>
                                    <div className="flex items-center flex-wrap">
                                      <p className="text-sm">{eB ? fmt$(eB.amount) : "—"}</p>
                                      {!aIsNewer && badge}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
                <div className="divide-y divide-border" data-testid="panel-past-statements">
                  {savedTaxReturns.map((entry: any) => {
                    const isSelected = selectedCompareIds.includes(entry.id);
                    const snap = (() => { try { return JSON.parse(entry.snapshotJson); } catch { return {}; } })();
                    const isEditing = editingReturnId === entry.id;
                    return (
                      <div
                        key={entry.id}
                        className={`flex items-center gap-3 px-5 py-3 transition-colors ${!compareMode && !isEditing ? "cursor-pointer hover:bg-muted/30" : "hover:bg-muted/20"} ${compareMode && isSelected ? "bg-primary/5" : ""}`}
                        onClick={!compareMode && !isEditing ? () => { loadSavedReturn(entry); setShowPastStatements(false); } : undefined}
                        data-testid={`row-saved-return-${entry.id}`}
                      >
                        {compareMode && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={e => {
                              if (e.target.checked) {
                                if (selectedCompareIds.length < 2) {
                                  const next = [...selectedCompareIds, entry.id];
                                  setSelectedCompareIds(next);
                                  if (next.length === 2) setShowCompareView(true);
                                }
                              } else {
                                setSelectedCompareIds(prev => prev.filter(id => id !== entry.id));
                                setShowCompareView(false);
                              }
                            }}
                            className="w-4 h-4 shrink-0 accent-primary"
                            data-testid={`checkbox-compare-${entry.id}`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                              <select
                                value={editingReturnYear}
                                onChange={e => setEditingReturnYear(e.target.value)}
                                className="h-7 text-xs bg-transparent border border-border rounded px-1.5 outline-none text-foreground shrink-0"
                                data-testid={`select-rename-year-${entry.id}`}
                              >
                                <option value="">{t("operations:step1.year")}</option>
                                {Array.from({ length: new Date().getFullYear() - 2014 }, (_, i) => new Date().getFullYear() - i).map(y => (
                                  <option key={y} value={String(y)}>{y}</option>
                                ))}
                              </select>
                              <Input
                                className="h-7 text-xs flex-1 min-w-0"
                                value={editingReturnName}
                                onChange={e => setEditingReturnName(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === "Enter" && editingReturnName.trim() && editingReturnYear) {
                                    renameTaxReturnMutation.mutate({ id: entry.id, name: `${editingReturnYear} – ${editingReturnName.trim()}` });
                                  } else if (e.key === "Escape") {
                                    setEditingReturnId(null);
                                    setEditingReturnYear("");
                                    setEditingReturnName("");
                                  }
                                }}
                                autoFocus
                                data-testid={`input-rename-return-${entry.id}`}
                              />
                            </div>
                          ) : (
                            <p className="text-sm font-semibold truncate" data-testid={`text-return-name-${entry.id}`}>{entry.name}</p>
                          )}
                          <p className="text-[11px] text-muted-foreground truncate">
                            {entry.fileName} · {new Date(entry.savedAt).toLocaleDateString()}
                            {snap.revenue ? ` · ${fmt$(snap.revenue)}` : ""}
                          </p>
                        </div>
                        {isEditing ? (
                          <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="default"
                              className="h-7 px-2 text-xs"
                              disabled={!editingReturnName.trim() || !editingReturnYear || renameTaxReturnMutation.isPending}
                              onClick={() => renameTaxReturnMutation.mutate({ id: entry.id, name: `${editingReturnYear} – ${editingReturnName.trim()}` })}
                              data-testid={`button-save-rename-${entry.id}`}
                            >
                              {t("common:save")}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs"
                              onClick={() => { setEditingReturnId(null); setEditingReturnYear(""); setEditingReturnName(""); }}
                              data-testid={`button-cancel-rename-${entry.id}`}
                            >
                              {t("common:cancel")}
                            </Button>
                          </div>
                        ) : !compareMode && (
                          <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                setEditingReturnId(entry.id);
                                const separatorIdx = entry.name.indexOf(" – ");
                                if (separatorIdx !== -1) {
                                  setEditingReturnYear(entry.name.slice(0, separatorIdx));
                                  setEditingReturnName(entry.name.slice(separatorIdx + 3));
                                } else {
                                  setEditingReturnYear("");
                                  setEditingReturnName(entry.name);
                                }
                              }}
                              data-testid={`button-rename-return-${entry.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            {(entry.cloudPath || entry.fileName?.toLowerCase().endsWith(".pdf")) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={async (ev) => {
                                  ev.stopPropagation();
                                  try {
                                    const r = await fetch(`/api/saved-tax-returns/${entry.id}/pdf`, { credentials: "include" });
                                    if (!r.ok) {
                                      const data = await r.json().catch(() => ({}));
                                      toast({ title: t("operations:pastReturns.pdfUnavailable"), description: data.message || t("operations:pastReturns.pdfNotRetrieved"), variant: "destructive" });
                                      return;
                                    }
                                    const blob = await r.blob();
                                    const blobUrl = URL.createObjectURL(blob);
                                    window.open(blobUrl, "_blank");
                                    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                                  } catch {
                                    toast({ title: t("operations:pastReturns.pdfUnavailable"), description: t("operations:pastReturns.pdfCouldNotRetrieve"), variant: "destructive" });
                                  }
                                }}
                                data-testid={`button-view-pdf-${entry.id}`}
                                title={t("operations:pastReturns.viewOriginalPdf")}
                              >
                                <FileText className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                              onClick={() => setDeletingReturnId(entry.id)}
                              data-testid={`button-delete-return-${entry.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Trends Dialog */}
          <Dialog open={showTrendsDialog} onOpenChange={setShowTrendsDialog}>
            <DialogContent className="max-w-2xl w-full max-h-[90vh] flex flex-col p-0 gap-0" data-testid="dialog-trends">
              <DialogHeader className="px-5 pt-5 pb-4 shrink-0 border-b border-border">
                <DialogTitle className="text-base font-semibold">{t("operations:pastReturns.yearOverYearTrends")}</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4">
                {(() => {
                  const parseLabelYear = (entry: any): number => {
                    const match = typeof entry.name === "string" ? entry.name.match(/^(\d{4})\s*[–\-]/) : null;
                    return match ? parseInt(match[1], 10) : new Date(entry.savedAt).getFullYear();
                  };

                  // Include active (unsaved) extraction if it has a year not already in saved snapshots
                  const activeYear = latest?.sourceTaxYear;
                  const activeInp = latest?.input;
                  const activeSynthetic = (activeYear && activeInp && (activeInp.revenue || activeInp.grossProfit) &&
                    !(savedTaxReturns as any[]).some((r: any) => parseLabelYear(r) === activeYear))
                    ? {
                        id: "__active__",
                        name: `${activeYear} – (uploaded)`,
                        snapshotJson: JSON.stringify({
                          revenue: activeInp.revenue,
                          grossProfit: activeInp.grossProfit,
                          annualTotalOperatingExpenses: activeInp.annualTotalOperatingExpenses,
                        }),
                        savedAt: new Date().toISOString(),
                      }
                    : null;
                  const returnsForChart = activeSynthetic
                    ? [...(savedTaxReturns as any[]), activeSynthetic]
                    : (savedTaxReturns as any[]);

                  const allYears = [...new Set(returnsForChart.map((e: any) => parseLabelYear(e)))].sort((a, b) => a - b);
                  const trendData = [...returnsForChart]
                    .filter((entry: any) => {
                      const year = parseLabelYear(entry);
                      if (trendYearFrom !== null && year < trendYearFrom) return false;
                      if (trendYearTo !== null && year > trendYearTo) return false;
                      return true;
                    })
                    .sort((a: any, b: any) => {
                      const d = parseLabelYear(a) - parseLabelYear(b);
                      return d !== 0 ? d : new Date(a.savedAt).getTime() - new Date(b.savedAt).getTime();
                    })
                    .map((entry: any) => {
                      const snap = (() => { try { return JSON.parse(entry.snapshotJson); } catch { return {}; } })();
                      const gp = snap.grossProfit;
                      const opEx = snap.annualTotalOperatingExpenses;
                      const noi = typeof gp === "number" && typeof opEx === "number" ? gp - opEx : null;
                      const rGross = typeof snap.rental?.rentalGrossIncome === "number" ? snap.rental.rentalGrossIncome : null;
                      const rExp = typeof snap.rental?.rentalTotalExpenses === "number" ? snap.rental.rentalTotalExpenses : null;
                      const rNet = typeof snap.rental?.rentalNetIncome === "number" ? snap.rental.rentalNetIncome : null;
                      return {
                        name: entry.name,
                        revenue: typeof snap.revenue === "number" ? Math.round(snap.revenue / 1000) : null,
                        grossProfit: typeof gp === "number" ? Math.round(gp / 1000) : null,
                        noi: noi != null ? Math.round(noi / 1000) : null,
                        rentalGross: rGross != null ? Math.round(rGross / 1000) : null,
                        rentalExpenses: rExp != null ? Math.round(rExp / 1000) : null,
                        rentalNet: rNet != null ? Math.round(rNet / 1000) : null,
                      };
                    });
                  const hasRentalData = trendData.some(d => d.rentalGross != null || d.rentalExpenses != null || d.rentalNet != null);
                  const hasData = trendData.some(d => d.revenue != null || d.grossProfit != null || d.noi != null);
                  return (
                    <>
                      {allYears.length > 1 && (
                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                          <span className="text-[11px] text-muted-foreground shrink-0">{t("operations:pastReturns.showYears")}</span>
                          <select value={trendYearFrom ?? ""} onChange={e => setTrendYearFrom(e.target.value === "" ? null : Number(e.target.value))} className="text-[11px] h-6 px-1 rounded border border-border bg-background text-foreground">
                            <option value="">{t("operations:pastReturns.fromAll")}</option>
                            {allYears.map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                          <span className="text-[11px] text-muted-foreground">–</span>
                          <select value={trendYearTo ?? ""} onChange={e => setTrendYearTo(e.target.value === "" ? null : Number(e.target.value))} className="text-[11px] h-6 px-1 rounded border border-border bg-background text-foreground">
                            <option value="">{t("operations:pastReturns.toAll")}</option>
                            {allYears.map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                          {(trendYearFrom !== null || trendYearTo !== null) && (
                            <button onClick={() => { setTrendYearFrom(null); setTrendYearTo(null); }} className="text-[11px] text-muted-foreground underline hover:text-foreground">{t("operations:pastReturns.clear")}</button>
                          )}
                        </div>
                      )}
                      {!hasData ? (
                        <p className="text-sm text-muted-foreground text-center py-8">{trendYearFrom !== null || trendYearTo !== null ? t("operations:pastReturns.noMatchRange") : t("operations:pastReturns.notEnoughNumericData")}</p>
                      ) : (
                        <>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {([
                              { key: "revenue", label: t("operations:pastReturns.metricRevenue"), color: "#6366f1" },
                              { key: "grossProfit", label: t("operations:pastReturns.metricGrossProfit"), color: "#22c55e" },
                              { key: "noi", label: t("operations:pastReturns.metricNoi"), color: "#f59e0b" },
                              ...(hasRentalData ? [
                                { key: "rentalGross", label: t("operations:pastReturns.metricRentalGross"), color: "#06b6d4" },
                                { key: "rentalExpenses", label: t("operations:pastReturns.metricRentalExpenses"), color: "#ec4899" },
                                { key: "rentalNet", label: t("operations:pastReturns.metricRentalNet"), color: "#8b5cf6" },
                              ] : []),
                            ] as { key: keyof typeof visibleTrendMetrics; label: string; color: string }[]).map(({ key, label, color }) => (
                              <button key={key} onClick={() => updateTrendMetrics(prev => ({ ...prev, [key]: !prev[key] }))} className="px-3 py-1 rounded-full text-[11px] font-medium border transition-colors" style={visibleTrendMetrics[key] ? { background: color, color: "#fff", borderColor: color } : { borderColor: "#d1d5db" }}>{label}</button>
                            ))}
                          </div>
                          <p className="text-[11px] text-muted-foreground mb-3">{t("operations:pastReturns.valuesInK")}</p>
                          <ResponsiveContainer width="100%" height={240}>
                            <LineChart data={trendData} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                              <XAxis dataKey="name" tick={{ fontSize: 11, fontWeight: 600 }} interval={0} padding={{ left: 12, right: 12 }} />
                              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => { const n = Number(v); return Math.abs(n) >= 1000 ? `$${(n/1000).toFixed(1)}M` : `$${n.toLocaleString()}K`; }} />
                              <RechartsTooltip formatter={(value: number, name: string) => { if (Math.abs(value) >= 1000) return [`$${(value/1000).toFixed(2)}M`, name]; return [`$${Number(value).toLocaleString()}K`, name]; }} contentStyle={{ fontSize: 11 }} />
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              {visibleTrendMetrics.revenue && <Line type="monotone" dataKey="revenue" name={t("operations:pastReturns.metricRevenue")} stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} connectNulls />}
                              {visibleTrendMetrics.grossProfit && <Line type="monotone" dataKey="grossProfit" name={t("operations:pastReturns.metricGrossProfit")} stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} connectNulls />}
                              {visibleTrendMetrics.noi && <Line type="monotone" dataKey="noi" name={t("operations:pastReturns.metricNoi")} stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} connectNulls />}
                              {hasRentalData && visibleTrendMetrics.rentalGross && <Line type="monotone" dataKey="rentalGross" name={t("operations:pastReturns.metricRentalGross")} stroke="#06b6d4" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} connectNulls />}
                              {hasRentalData && visibleTrendMetrics.rentalExpenses && <Line type="monotone" dataKey="rentalExpenses" name={t("operations:pastReturns.metricRentalExpenses")} stroke="#ec4899" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} connectNulls />}
                              {hasRentalData && visibleTrendMetrics.rentalNet && <Line type="monotone" dataKey="rentalNet" name={t("operations:pastReturns.metricRentalNet")} stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} connectNulls />}
                            </LineChart>
                          </ResponsiveContainer>
                          <div data-trends-has-data="true" style={{ display: "none" }} />
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              <TaxReturnChatbot
                key={showTrendsDialog ? "trends-open" : "trends-closed"}
                type="trends"
                data={(savedTaxReturns as any[]).map((r: any) => {
                  const snap = (() => { try { return JSON.parse(r.snapshotJson); } catch { return {}; } })();
                  return { name: r.name, revenue: snap.revenue, grossProfit: snap.grossProfit, annualUnitsSold: snap.annualUnitsSold, annualTotalOperatingExpenses: snap.annualTotalOperatingExpenses, rental: snap.rental, dynamicExpenses: snap.dynamicExpenses };
                })}
                dataReady={showTrendsDialog && (savedTaxReturns as any[]).length >= 2}
                defaultCollapsed={true}
                resizable={true}
              />
            </DialogContent>
          </Dialog>

          {/* Compare Dialog */}
          <Dialog open={showCompareDialog} onOpenChange={open => { setShowCompareDialog(open); if (!open) { setInlineCompareIdA(""); setInlineCompareIdB(""); } }}>
            <DialogContent className="max-w-2xl w-full max-h-[90vh] flex flex-col p-0 gap-0" data-testid="dialog-compare">
              <DialogHeader className="px-5 pt-5 pb-4 shrink-0 border-b border-border">
                <DialogTitle className="text-base font-semibold">{t("operations:pastReturns.compareTaxReturns")}</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto flex-1 min-h-0">
                <div className="px-5 py-4 grid grid-cols-2 gap-3 border-b border-border">
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1.5 font-medium">{t("operations:pastReturns.returnA")}</p>
                    <select value={inlineCompareIdA} onChange={e => setInlineCompareIdA(e.target.value)} className="w-full text-xs h-7 px-2 rounded border border-border bg-background text-foreground" data-testid="select-compare-a">
                      <option value="">{t("operations:pastReturns.selectReturn")}</option>
                      {(savedTaxReturns as any[]).map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1.5 font-medium">{t("operations:pastReturns.returnB")}</p>
                    <select value={inlineCompareIdB} onChange={e => setInlineCompareIdB(e.target.value)} className="w-full text-xs h-7 px-2 rounded border border-border bg-background text-foreground" data-testid="select-compare-b">
                      <option value="">{t("operations:pastReturns.selectReturn")}</option>
                      {(savedTaxReturns as any[]).map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                </div>
                {(!inlineCompareIdA || !inlineCompareIdB) && (
                  <p className="px-5 py-8 text-sm text-muted-foreground text-center">{t("operations:pastReturns.selectTwoAbove")}</p>
                )}
                {inlineCompareIdA && inlineCompareIdB && inlineCompareIdA === inlineCompareIdB && (
                  <p className="px-5 py-8 text-sm text-muted-foreground text-center">{t("operations:pastReturns.selectTwoDifferent")}</p>
                )}
                {inlineCompareIdA && inlineCompareIdB && inlineCompareIdA !== inlineCompareIdB && (() => {
                  const a = (savedTaxReturns as any[]).find((r: any) => r.id === inlineCompareIdA);
                  const b = (savedTaxReturns as any[]).find((r: any) => r.id === inlineCompareIdB);
                  if (!a || !b) return null;
                  const parseSnap = (entry: any) => { try { return JSON.parse(entry.snapshotJson); } catch { return {}; } };
                  const snapA = parseSnap(a); const snapB = parseSnap(b);
                  const gpA = snapA.grossProfit ?? 0; const gpB = snapB.grossProfit ?? 0;
                  const revA = snapA.revenue ?? 0; const revB = snapB.revenue ?? 0;
                  const cogsA = revA > 0 ? revA - gpA : null; const cogsB = revB > 0 ? revB - gpB : null;
                  const gpuA = snapA.annualUnitsSold > 0 ? gpA / snapA.annualUnitsSold : null;
                  const gpuB = snapB.annualUnitsSold > 0 ? gpB / snapB.annualUnitsSold : null;
                  const opExA = snapA.annualTotalOperatingExpenses ?? 0; const opExB = snapB.annualTotalOperatingExpenses ?? 0;
                  const netA = gpA > 0 && opExA > 0 ? gpA - opExA : null; const netB = gpB > 0 && opExB > 0 ? gpB - opExB : null;
                  const _ymA = (a as any).name?.match(/(\d{4})/); const _ymB = (b as any).name?.match(/(\d{4})/);
                  const yearA: number | null = _ymA ? parseInt(_ymA[1], 10) : null; const yearB: number | null = _ymB ? parseInt(_ymB[1], 10) : null;
                  const aIsNewer = (yearA !== null && yearB !== null && yearA !== yearB) ? yearA > yearB : new Date((a as any).savedAt).getTime() >= new Date((b as any).savedAt).getTime();
                  function cmpBadge(newVal: number | null, oldVal: number | null) {
                    if (newVal == null || oldVal == null || oldVal === 0) return null;
                    const pct = ((newVal - oldVal) / Math.abs(oldVal)) * 100;
                    if (!Number.isFinite(pct)) return null;
                    const pos = pct >= 0;
                    return <span className={`ml-1.5 text-[10px] font-semibold px-1 py-0.5 rounded ${pos ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"}`}>{pos ? "+" : ""}{pct.toFixed(1)}%</span>;
                  }
                  type CRow = { label: string; valA: string; valB: string; numA: number | null; numB: number | null };
                  const rows: CRow[] = [
                    { label: t("operations:pastReturns.rowAnnualRevenue"), valA: fmt$(revA), valB: fmt$(revB), numA: revA, numB: revB },
                    { label: t("operations:pastReturns.rowCogs"), valA: cogsA != null ? fmt$(cogsA) : "—", valB: cogsB != null ? fmt$(cogsB) : "—", numA: cogsA, numB: cogsB },
                    { label: t("operations:pastReturns.metricGrossProfit"), valA: fmt$(gpA), valB: fmt$(gpB), numA: gpA, numB: gpB },
                    { label: t("operations:pastReturns.rowGpPerUnit"), valA: gpuA != null ? fmt$(gpuA) : "—", valB: gpuB != null ? fmt$(gpuB) : "—", numA: gpuA, numB: gpuB },
                    { label: t("operations:pastReturns.rowOperatingExpenses"), valA: opExA > 0 ? fmt$(opExA) : "—", valB: opExB > 0 ? fmt$(opExB) : "—", numA: opExA || null, numB: opExB || null },
                    { label: t("operations:pastReturns.rowNetProfit"), valA: netA != null ? fmt$(netA) : "—", valB: netB != null ? fmt$(netB) : "—", numA: netA, numB: netB },
                  ];
                  const expA: { label: string; amount: number }[] = Array.isArray(snapA.dynamicExpenses) ? snapA.dynamicExpenses : [];
                  const expB: { label: string; amount: number }[] = Array.isArray(snapB.dynamicExpenses) ? snapB.dynamicExpenses : [];
                  const expLabels = Array.from(new Set([...expA.map(e => e.label), ...expB.map(e => e.label)]));
                  return (
                    <>
                      <div className="grid grid-cols-2 border-b border-border bg-muted/10">
                        {([a, b] as any[]).map((entry, idx) => {
                          const isNewer = idx === 0 ? aIsNewer : !aIsNewer;
                          return (
                            <div key={idx} className={`px-5 py-2.5 ${idx === 0 ? "border-r border-border" : ""}`}>
                              <div className="flex items-center gap-1.5">
                                <p className="text-xs font-semibold truncate">{entry.name}</p>
                                {isNewer && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0">{t("operations:pastReturns.mostRecent")}</span>}
                              </div>
                              <p className="text-[10px] text-muted-foreground truncate">{entry.fileName}</p>
                            </div>
                          );
                        })}
                      </div>
                      {rows.map(({ label, valA, valB, numA, numB }) => {
                        const newerNum = aIsNewer ? numA : numB; const olderNum = aIsNewer ? numB : numA;
                        const badge = cmpBadge(newerNum, olderNum);
                        return (
                          <div key={label} className="grid grid-cols-2 border-b border-border last:border-0">
                            <div className="px-5 py-2.5 border-r border-border">
                              <p className="text-[10px] text-muted-foreground">{label}</p>
                              <div className="flex items-center flex-wrap"><p className="text-sm font-semibold">{valA}</p>{aIsNewer && badge}</div>
                            </div>
                            <div className="px-5 py-2.5">
                              <p className="text-[10px] text-muted-foreground">{label}</p>
                              <div className="flex items-center flex-wrap"><p className="text-sm font-semibold">{valB}</p>{!aIsNewer && badge}</div>
                            </div>
                          </div>
                        );
                      })}
                      {expLabels.length > 0 && (
                        <>
                          <div className="grid grid-cols-2 bg-muted/20 border-b border-border">
                            <div className="px-5 py-2 border-r border-border"><p className="text-xs font-semibold text-muted-foreground">{t("operations:pastReturns.expenseBreakdown")}</p></div>
                            <div className="px-5 py-2"><p className="text-xs font-semibold text-muted-foreground">{t("operations:pastReturns.expenseBreakdown")}</p></div>
                          </div>
                          {expLabels.map(label => {
                            const eA = expA.find(e => e.label === label); const eB = expB.find(e => e.label === label);
                            const badge = cmpBadge(aIsNewer ? (eA?.amount ?? null) : (eB?.amount ?? null), aIsNewer ? (eB?.amount ?? null) : (eA?.amount ?? null));
                            return (
                              <div key={label} className="grid grid-cols-2 border-b border-border last:border-0">
                                <div className="px-5 py-2 border-r border-border">
                                  <p className="text-[10px] text-muted-foreground">{label}</p>
                                  <div className="flex items-center flex-wrap"><p className="text-sm">{eA ? fmt$(eA.amount) : "—"}</p>{aIsNewer && badge}</div>
                                </div>
                                <div className="px-5 py-2">
                                  <p className="text-[10px] text-muted-foreground">{label}</p>
                                  <div className="flex items-center flex-wrap"><p className="text-sm">{eB ? fmt$(eB.amount) : "—"}</p>{!aIsNewer && badge}</div>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              {inlineCompareIdA && inlineCompareIdB && inlineCompareIdA !== inlineCompareIdB && (() => {
                const a = (savedTaxReturns as any[]).find((r: any) => r.id === inlineCompareIdA);
                const b = (savedTaxReturns as any[]).find((r: any) => r.id === inlineCompareIdB);
                if (!a || !b) return null;
                const snapA = (() => { try { return JSON.parse(a.snapshotJson); } catch { return {}; } })();
                const snapB = (() => { try { return JSON.parse(b.snapshotJson); } catch { return {}; } })();
                return (
                  <TaxReturnChatbot
                    key={`compare-${inlineCompareIdA}-${inlineCompareIdB}`}
                    type="compare"
                    data={{ returnA: { name: a.name, revenue: snapA.revenue, grossProfit: snapA.grossProfit, annualUnitsSold: snapA.annualUnitsSold, annualTotalOperatingExpenses: snapA.annualTotalOperatingExpenses, rental: snapA.rental, dynamicExpenses: snapA.dynamicExpenses }, returnB: { name: b.name, revenue: snapB.revenue, grossProfit: snapB.grossProfit, annualUnitsSold: snapB.annualUnitsSold, annualTotalOperatingExpenses: snapB.annualTotalOperatingExpenses, rental: snapB.rental, dynamicExpenses: snapB.dynamicExpenses } }}
                    dataReady={true}
                    defaultCollapsed={true}
                    resizable={true}
                  />
                );
              })()}
            </DialogContent>
          </Dialog>

          {/* Locked state — nudge the dealer to upload a tax return */}
          {!taxReturnUnlocked && (
            <div
              className="flex flex-col items-center text-center gap-3 rounded-xl border border-dashed border-primary/30 bg-primary/[0.03] px-6 py-10"
              data-testid="panel-tax-return-locked"
            >
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Lock className="w-5 h-5 text-primary" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold">{t("operations:taxReturnTiles.unlockTitle")}</p>
                <p className="text-xs text-muted-foreground max-w-sm">
                  {t("operations:taxReturnTiles.unlockDesc")}
                </p>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  className="h-8 px-3 text-xs"
                  disabled={pdfUploading}
                  onClick={() => setShowAddReturnChoiceDialog(true)}
                  data-testid="button-unlock-upload-tax-return"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  {t("operations:taxReturnTiles.uploadTaxReturn")}
                </Button>
              </div>
            </div>
          )}

          {/* Input tiles — shown once the tax return is unlocked */}
          {taxReturnUnlocked && (
          <div
            className="grid grid-cols-2 lg:grid-cols-4 gap-4"
            style={!isEditingTaxReturn ? { pointerEvents: "none" } : undefined}
          >
            {/* Units Sold */}
            <div className="rounded-xl border border-amber-200/50 dark:border-amber-800/30 bg-amber-50/40 dark:bg-amber-950/20 p-4 space-y-3">
              <div>
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center mb-2">
                  <Calculator className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                </div>
                <p className="text-sm font-semibold leading-tight">{t("operations:taxReturnTiles.unitsSold")} <span className="font-normal text-muted-foreground">{t("operations:taxReturnTiles.notOnTaxReturn")}</span></p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("operations:taxReturnTiles.unitsSoldDesc")}</p>
              </div>
              <FormattedInput
                placeholder="e.g. 240"
                value={annualUnitsSold}
                onChange={setAnnualUnitsSold}
                readOnly={!isEditingTaxReturn}
                className="h-9 text-sm font-medium bg-background"
                data-testid="input-annual-units-sold-pl"
              />
            </div>

            {/* Annual Revenue */}
            <div className="rounded-xl border border-emerald-200/50 dark:border-emerald-800/30 bg-emerald-50/40 dark:bg-emerald-950/20 p-4 space-y-3">
              <div>
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-2">
                  <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <p className="text-sm font-semibold leading-tight">{t("operations:taxReturnTiles.annualRevenue")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("operations:taxReturnTiles.annualRevenueDesc")}</p>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                <FormattedInput
                  placeholder="e.g. 4,200,000"
                  value={manualRevenue}
                  onChange={setManualRevenueUser}
                  readOnly={!isEditingTaxReturn}
                  className="pl-6 h-9 text-sm font-medium bg-background"
                  data-testid="input-manual-revenue"
                />
              </div>
              {isEditingTaxReturn && aiExtractedValues.revenue && !taxReturnSourceFields.includes("revenue") && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-[10px] text-primary/70 hover:text-primary transition-colors"
                  onClick={() => {
                    setManualRevenue(aiExtractedValues.revenue!);
                    setTaxReturnSourceFields(prev => [...prev.filter(k => k !== "revenue"), "revenue"]);
                  }}
                  data-testid="button-revert-revenue"
                >
                  <RotateCcw className="w-2.5 h-2.5" />
                  {t("operations:taxReturnTiles.restoreAiValue")}
                </button>
              )}
            </div>

            {/* Gross Profit */}
            <div className="rounded-xl border border-blue-200/50 dark:border-blue-800/30 bg-blue-50/40 dark:bg-blue-950/20 p-4 space-y-3">
              <div>
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center mb-2">
                  <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <p className="text-sm font-semibold leading-tight">{t("operations:taxReturnTiles.grossProfit")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("operations:taxReturnTiles.grossProfitDesc")}</p>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                <FormattedInput
                  placeholder="e.g. 840,000"
                  value={manualGrossProfit}
                  onChange={setManualGrossProfitUser}
                  readOnly={!isEditingTaxReturn}
                  className="pl-6 h-9 text-sm font-medium bg-background"
                  data-testid="input-manual-gross-profit"
                />
              </div>
              {isEditingTaxReturn && aiExtractedValues.grossProfit && !taxReturnSourceFields.includes("grossProfit") && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-[10px] text-primary/70 hover:text-primary transition-colors"
                  onClick={() => {
                    setManualGrossProfit(aiExtractedValues.grossProfit!);
                    setTaxReturnSourceFields(prev => [...prev.filter(k => k !== "grossProfit"), "grossProfit"]);
                  }}
                  data-testid="button-revert-gross-profit"
                >
                  <RotateCcw className="w-2.5 h-2.5" />
                  {t("operations:taxReturnTiles.restoreAiValue")}
                </button>
              )}
            </div>

            {/* Other Income — read-only, shown only when a non-zero value was extracted */}
            {extractedOtherIncome != null && extractedOtherIncome !== 0 && (
              <div className="rounded-xl border border-teal-200/50 dark:border-teal-800/30 bg-teal-50/40 dark:bg-teal-950/20 p-4 space-y-3" data-testid="tile-other-income">
                <div>
                  <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center mb-2">
                    <DollarSign className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                  </div>
                  <p className="text-sm font-semibold leading-tight">{t("operations:taxReturnTiles.otherIncome")}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("operations:taxReturnTiles.otherIncomeDesc")}</p>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                  <div
                    className="pl-6 h-9 text-sm font-medium bg-background/60 border border-input rounded-md flex items-center text-teal-700 dark:text-teal-300 tabular-nums select-none"
                    data-testid="value-other-income"
                  >
                    {extractedOtherIncome.toLocaleString("en-US")}
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">{t("operations:taxReturnTiles.readOnlyIncluded")}</p>
              </div>
            )}

            {/* Operating Expenses */}
            <div className="rounded-xl border border-orange-200/50 dark:border-orange-800/30 bg-orange-50/40 dark:bg-orange-950/20 p-4 space-y-3">
              <div>
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center mb-2">
                  <BarChart3 className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                </div>
                <p className="text-sm font-semibold leading-tight">{t("operations:taxReturnTiles.operatingExpenses")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("operations:taxReturnTiles.operatingExpensesDesc")}</p>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                <FormattedInput
                  placeholder="e.g. 160,000"
                  value={manualOpEx}
                  onChange={setManualOpExUser}
                  readOnly={!isEditingTaxReturn}
                  className="pl-6 h-9 text-sm font-medium bg-background"
                  data-testid="input-manual-opex"
                />
              </div>
              {nf(manualGrossProfit) > 0 && manualOpEx !== "" && (
                <p className="text-[11px] text-muted-foreground">
                  {t("operations:taxReturnTiles.netProfitEquals", { value: fmt$(derivedNetProfit ?? 0) })}
                </p>
              )}
              {isEditingTaxReturn && aiExtractedValues.opEx && !taxReturnSourceFields.includes("annualTotalOperatingExpenses") && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-[10px] text-primary/70 hover:text-primary transition-colors"
                  onClick={() => {
                    setManualOpEx(aiExtractedValues.opEx!);
                    setTaxReturnSourceFields(prev => [
                      ...prev.filter(k => k !== "annualTotalOperatingExpenses" && k !== "annualTotalExpenses"),
                      "annualTotalOperatingExpenses",
                      "annualTotalExpenses",
                    ]);
                  }}
                  data-testid="button-revert-opex"
                >
                  <RotateCcw className="w-2.5 h-2.5" />
                  {t("operations:taxReturnTiles.restoreAiValue")}
                </button>
              )}
            </div>
          </div>
          )}

          {/* Live P&L summary — styled stat chips */}
          {nf(manualGrossProfit) > 0 && (() => {
            const rev = nf(manualRevenue);
            const gp = nf(manualGrossProfit);
            const totalInc = nf(manualTotalIncome);
            const otherInc = totalInc > gp ? totalInc - gp : null;  // non-COGS income items (gain/other income)
            const opex = derivedOpEx;
            const net = derivedNetProfit;
            const cogs = rev > 0 ? rev - gp : null;
            const gpMargin = rev > 0 ? (gp / rev) * 100 : null;
            const netMargin = rev > 0 && net != null ? (net / rev) * 100 : null;
            const gpu = nf(annualUnitsSold) > 0 ? gp / nf(annualUnitsSold) : null;
            type Chip = { label: string; value: string; sub?: string; labelColor: string; valueColor: string; bg: string; border: string };
            const chips: Chip[] = [
              ...(rev > 0 ? [{ label: t("operations:plChips.revenue"), value: fmt$(rev) ?? "—", labelColor: "text-slate-400 dark:text-slate-500", valueColor: "text-slate-800 dark:text-slate-100", bg: "bg-slate-50 dark:bg-slate-900/40", border: "border-slate-200/70 dark:border-slate-700/40" }] : []),
              ...(cogs != null ? [{ label: t("operations:plChips.cogs"), value: fmt$(cogs) ?? "—", labelColor: "text-rose-400", valueColor: "text-rose-700 dark:text-rose-300", bg: "bg-rose-50 dark:bg-rose-950/30", border: "border-rose-200/60 dark:border-rose-800/30" }] : []),
              { label: t("operations:plChips.grossProfit"), value: fmt$(gp) ?? "—", sub: gpMargin != null ? t("operations:plChips.marginSub", { pct: gpMargin.toFixed(1) }) : undefined, labelColor: "text-emerald-500", valueColor: "text-emerald-700 dark:text-emerald-300", bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200/60 dark:border-emerald-800/30" },
              ...(otherInc != null ? [{ label: t("operations:plChips.otherIncome"), value: fmt$(otherInc) ?? "—", sub: t("operations:plChips.inclInNetProfit"), labelColor: "text-teal-500", valueColor: "text-teal-700 dark:text-teal-300", bg: "bg-teal-50 dark:bg-teal-950/30", border: "border-teal-200/60 dark:border-teal-800/30" }] : []),
              ...(gpu != null ? [{ label: t("operations:plChips.gpPerUnit"), value: fmt$(gpu) ?? "—", labelColor: "text-blue-400", valueColor: "text-blue-700 dark:text-blue-300", bg: "bg-blue-50 dark:bg-blue-950/30", border: "border-blue-200/60 dark:border-blue-800/30" }] : []),
              ...(opex > 0 ? [{ label: t("operations:plChips.operExpenses"), value: fmt$(opex) ?? "—", labelColor: "text-orange-400", valueColor: "text-orange-700 dark:text-orange-300", bg: "bg-orange-50 dark:bg-orange-950/30", border: "border-orange-200/60 dark:border-orange-800/30" }] : []),
              ...(net != null ? [{ label: t("operations:plChips.netProfit"), value: fmt$(net) ?? "—", sub: netMargin != null ? t("operations:plChips.marginSub", { pct: netMargin.toFixed(1) }) : undefined, labelColor: net >= 0 ? "text-primary/70" : "text-destructive/70", valueColor: net >= 0 ? "text-primary" : "text-destructive", bg: net >= 0 ? "bg-primary/5" : "bg-destructive/5", border: net >= 0 ? "border-primary/20" : "border-destructive/20" }] : []),
            ];
            return (
              <div className="flex flex-wrap gap-2 pt-1 justify-center">
                {chips.map(({ label, value, sub, labelColor, valueColor, bg, border }) => (
                  <div key={label} className={`flex flex-col px-3.5 py-2.5 rounded-xl border ${bg} ${border} flex-1 min-w-[100px] items-center text-center`}>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider ${labelColor}`}>{label}</span>
                    <span className={`text-base font-bold tabular-nums leading-tight mt-0.5 ${valueColor}`}>{value}</span>
                    {sub && <span className={`text-[10px] font-medium mt-0.5 ${labelColor}`}>{sub}</span>}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Rental Income (Schedule E / Form 8825) */}
          {rentalData && (
            rentalData.rentalGrossIncome != null ||
            rentalData.rentalTotalExpenses != null ||
            rentalData.rentalNetIncome != null
          ) && (
            <div className="rounded-xl border border-border overflow-hidden" data-testid="section-rental-income">
              <div className="flex items-center justify-between px-4 py-3 bg-muted/20">
                <span className="font-medium text-sm">{t("operations:pastReturns.rentalIncomeSection")}</span>
              </div>
              <div className="border-t border-border px-4 pb-4 pt-3 space-y-1">
                {rentalData.rentalGrossIncome != null && (
                  <div className="flex justify-between py-1 border-b text-sm" data-testid="text-rental-gross">
                    <span className="text-muted-foreground">{t("operations:pastReturns.grossRentsReceived")}</span>
                    <span className="font-semibold tabular-nums">{fmt$(rentalData.rentalGrossIncome)}</span>
                  </div>
                )}
                {rentalData.rentalTotalExpenses != null && (
                  <div className="flex justify-between py-1 border-b text-sm" data-testid="text-rental-expenses">
                    <span className="text-muted-foreground">{t("operations:pastReturns.totalRentalExpenses")}</span>
                    <span className="font-semibold tabular-nums">{fmt$(rentalData.rentalTotalExpenses)}</span>
                  </div>
                )}
                {rentalData.rentalNetIncome != null && (
                  <div
                    className={`flex justify-between py-1.5 text-sm font-semibold ${rentalData.rentalNetIncome < 0 ? "text-destructive" : "text-emerald-700 dark:text-emerald-400"}`}
                    data-testid="text-rental-net"
                  >
                    <span>{t("operations:pastReturns.netRentalIncome")}</span>
                    <span className="tabular-nums">{fmt$(rentalData.rentalNetIncome)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Expense breakdown (optional, collapsible) — hidden until unlocked */}
          {taxReturnUnlocked && (
          <div className="rounded-xl border border-border overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-muted/30 transition-colors"
              onClick={() => setShowExpenseDetail(v => !v)}
              data-testid="button-toggle-expense-detail"
            >
              <span className="font-medium text-sm">{t("operations:pastReturns.expenseBreakdown")}</span>
              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${showExpenseDetail ? "rotate-180" : ""}`} />
            </button>
            {showExpenseDetail && (
              <div className="border-t border-border px-4 pb-4 pt-3">
                {(dynamicExpenseItems.length > 0 || isEditingTaxReturn) ? (
                  <div className="space-y-1">
                    {!isEditingTaxReturn && (
                      <p className="text-xs text-muted-foreground mb-2">{t("operations:expenseDetail.extractedNote")}</p>
                    )}
                    <table className="w-full text-sm" data-testid="table-dynamic-expenses">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-1.5 text-xs font-semibold text-muted-foreground">{t("operations:expenseDetail.category")}</th>
                          <th className="text-right py-1.5 text-xs font-semibold text-muted-foreground">{t("operations:expenseDetail.annual")}</th>
                          <th className="text-right py-1.5 text-xs font-semibold text-muted-foreground border-l border-border/50 pl-5">{t("operations:expenseDetail.monthly")}</th>
                          {isEditingTaxReturn && <th className="w-6"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {isEditingTaxReturn ? (
                          // Edit mode — show raw labels in inputs (one row per item)
                          dynamicExpenseItems.map((item, i) => (
                            <tr key={i} className="border-b last:border-0" data-testid={`row-exp-${i}`}>
                              <td className="py-1.5 pr-2">
                                <input
                                  type="text"
                                  value={item.label}
                                  onChange={e => {
                                    const updated = dynamicExpenseItems.map((it, idx) => idx === i ? { ...it, label: e.target.value } : it);
                                    setDynamicExpenseItems(updated);
                                  }}
                                  className="w-full border border-border rounded px-2 py-1 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                                  data-testid={`input-exp-label-${i}`}
                                />
                              </td>
                              <td className="py-1.5 pl-2">
                                <input
                                  type="number"
                                  value={item.amount}
                                  onChange={e => {
                                    const val = parseFloat(e.target.value) || 0;
                                    const updated = dynamicExpenseItems.map((it, idx) => idx === i ? { ...it, amount: val } : it);
                                    setDynamicExpenseItems(updated);
                                  }}
                                  className="w-full border border-border rounded px-2 py-1 text-sm bg-background text-right focus:outline-none focus:ring-1 focus:ring-ring"
                                  data-testid={`input-exp-amount-${i}`}
                                />
                              </td>
                              <td className="py-1.5 text-right text-muted-foreground text-xs border-l border-border/50 pl-5 whitespace-nowrap">{fmt$(item.amount / 12)}</td>
                              <td className="py-1.5 pl-1">
                                <button
                                  onClick={() => setDynamicExpenseItems(dynamicExpenseItems.filter((_, idx) => idx !== i))}
                                  className="text-muted-foreground hover:text-destructive transition-colors"
                                  data-testid={`button-delete-exp-${i}`}
                                  title={t("operations:expenseDetail.removeRow")}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (() => {
                          // View mode — group "Other Deductions: …" items under a single
                          // section header; display each without the repeated prefix.
                          const isOdRow = (lbl: string) => /^(other\s+deductions?|other\s+expenses?)(\s*:\s*|\s+)/i.test(lbl);
                          const stripOd  = (lbl: string) => lbl.replace(/^(other\s+deductions?|other\s+expenses?)(\s*:\s*|\s+)/i, "").trim();

                          const regularItems = dynamicExpenseItems.filter(it => !isOdRow(it.label));
                          const odItems      = dynamicExpenseItems.filter(it =>  isOdRow(it.label));

                          return (
                            <>
                              {regularItems.map((item, i) => (
                                <tr key={`reg-${i}`} className="border-b" data-testid={`row-exp-${i}`}>
                                  <td className="py-2 font-medium">{item.label}</td>
                                  <td className="py-2 text-right">{fmt$(item.amount)}</td>
                                  <td className="py-2 text-right text-muted-foreground border-l border-border/50 pl-5">{fmt$(item.amount / 12)}</td>
                                </tr>
                              ))}
                              {odItems.length > 0 && (
                                <>
                                  <tr className="border-b border-t border-border/60">
                                    <td colSpan={3} className="py-2 px-0">
                                      <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{t("operations:expenseDetail.otherDeductions")}</span>
                                    </td>
                                  </tr>
                                  {odItems.map((item, i) => (
                                    <tr key={`od-${i}`} className="border-b last:border-0" data-testid={`row-exp-od-${i}`}>
                                      <td className="py-2 pl-3 text-muted-foreground/90">{stripOd(item.label)}</td>
                                      <td className="py-2 text-right">{fmt$(item.amount)}</td>
                                      <td className="py-2 text-right text-muted-foreground border-l border-border/50 pl-5">{fmt$(item.amount / 12)}</td>
                                    </tr>
                                  ))}
                                </>
                              )}
                            </>
                          );
                        })()}
                      </tbody>
                      {dynamicExpenseItems.length > 0 && (() => {
                        const totalAnnual = dynamicExpenseItems.reduce((sum, it) => sum + (it.amount || 0), 0);
                        return (
                          <tfoot>
                            <tr className="border-t border-border bg-muted/20">
                              <td className="py-2 font-bold text-sm">{t("operations:expenseDetail.totalExpenses")}</td>
                              <td className="py-2 text-right font-bold text-sm" data-testid="text-total-expenses-annual">{fmt$(totalAnnual)}</td>
                              <td className="py-2 text-right font-bold text-sm border-l border-border/50 pl-5" data-testid="text-total-expenses-monthly">{fmt$(totalAnnual / 12)}</td>
                              {isEditingTaxReturn && <td></td>}
                            </tr>
                          </tfoot>
                        );
                      })()}
                    </table>
                    {isEditingTaxReturn && (
                      <button
                        onClick={() => setDynamicExpenseItems([...dynamicExpenseItems, { label: "", amount: 0 }])}
                        className="mt-2 text-xs text-primary hover:underline flex items-center gap-1"
                        data-testid="button-add-expense-row"
                      >
                        {t("operations:expenseDetail.addRow")}
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground py-2">
                    {t("operations:expenseDetail.noneExtracted")}
                  </p>
                )}
              </div>
            )}
          </div>
          )}

        </CardContent>
      </Card>

      {/* ── P&L vs Profile Comparison ── */}
      {showPLProfileComparison && (
        <Card className="border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" />
              {t("operations:plCompare.title")}
            </CardTitle>
            <CardDescription>{t("operations:plCompare.desc")}</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Column headers */}
            <div className="grid grid-cols-3 gap-3 mb-2 px-1">
              <div />
              <p className="text-xs font-semibold text-center text-muted-foreground uppercase tracking-wide">{t("operations:plCompare.plHeader")}</p>
              <p className="text-xs font-semibold text-center text-muted-foreground uppercase tracking-wide">{t("operations:plCompare.statedOnProfile")}</p>
            </div>
            <div className="space-y-2">
              {[
                ...(plGPU != null && profileGPU != null ? [{
                  label: t("operations:plCompare.gpuLabel"),
                  sub: t("operations:plCompare.gpuSub"),
                  plVal: plGPU,
                  profileVal: profileGPU,
                  higherIsBetter: true,
                  buildTooltip: (d: number, good: boolean) =>
                    good
                      ? t("operations:plCompare.gpuTipHigher", { diff: fmt$(d), profile: fmt$(profileGPU) })
                      : t("operations:plCompare.gpuTipLower", { diff: fmt$(-d), profile: fmt$(profileGPU) }),
                }] : []),
                ...(plMonthlyRevenue != null && profileMonthlyRevenue != null ? [{
                  label: t("operations:plCompare.monthlyRevenueLabel"),
                  sub: t("operations:plCompare.annualDiv12"),
                  plVal: plMonthlyRevenue,
                  profileVal: profileMonthlyRevenue,
                  higherIsBetter: true,
                  buildTooltip: (d: number, good: boolean) =>
                    good
                      ? t("operations:plCompare.revenueTipHigher", { diff: fmt$(d), profile: fmt$(profileMonthlyRevenue) })
                      : t("operations:plCompare.revenueTipLower", { diff: fmt$(-d), profile: fmt$(profileMonthlyRevenue) }),
                }] : []),
                ...(plMonthlyOpEx != null && profileMonthlyOpEx != null ? [{
                  label: t("operations:plCompare.monthlyOpExLabel"),
                  sub: t("operations:plCompare.annualDiv12"),
                  plVal: plMonthlyOpEx,
                  profileVal: profileMonthlyOpEx,
                  higherIsBetter: false,
                  buildTooltip: (d: number, good: boolean) =>
                    good
                      ? t("operations:plCompare.opexTipLower", { diff: fmt$(-d), profile: fmt$(profileMonthlyOpEx) })
                      : t("operations:plCompare.opexTipHigher", { diff: fmt$(d), profile: fmt$(profileMonthlyOpEx) }),
                }] : []),
              ].map(({ label, sub, plVal, profileVal, higherIsBetter, buildTooltip }) => {
                const diff = plVal - profileVal;
                const isGood = higherIsBetter ? diff >= 0 : diff <= 0;
                const diffStr = diff >= 0 ? `+${fmt$(diff)}` : `-${fmt$(-diff)}`;
                const tipText = buildTooltip(diff, isGood);
                return (
                  <div key={label} className="grid grid-cols-3 items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/40">
                    <div>
                      <p className="text-xs font-medium text-foreground">{label}</p>
                      <p className="text-[10px] text-muted-foreground/70 mt-0.5">{sub}</p>
                    </div>
                    <div className="flex items-center justify-center gap-1.5">
                      <p className="text-sm font-bold">{fmt$(plVal)}</p>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full cursor-help ${isGood ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-destructive/15 text-destructive"}`}>
                              {diffStr}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs text-xs">
                            {tipText}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-muted-foreground">{fmt$(profileVal)}</p>
                    </div>
                  </div>
                );
              })}
              {plGPU == null && plMonthlyRevenue == null && plMonthlyOpEx == null && (
                <p className="text-xs text-muted-foreground py-2">{t("operations:plCompare.completeProfile")}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Divider ── */}
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-medium">{t("operations:analyticsInputs.dividerLabel")}</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* ── Step 2: Inputs used for cost to carry & breakeven ── */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-muted/30 transition-colors"
          onClick={() => setInputsExpanded((v) => !v)}
          data-testid="button-toggle-analytics-inputs"
        >
          <div className="flex flex-col items-start gap-0.5">
            <span className="font-medium text-sm">{t("operations:analyticsInputs.title")}</span>
            <span className="text-xs text-muted-foreground">{t("operations:analyticsInputs.subtitle")}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground">
              {avgInventoryUnits
                ? <span><span className="font-medium text-foreground">{avgInventoryUnits}</span> {t("operations:analyticsInputs.unitsOnLot")}</span>
                : <span className="text-yellow-600 dark:text-yellow-400">{t("operations:analyticsInputs.lotUnitsMissing")}</span>}
              {annualUnitsSold
                ? <span><span className="font-medium text-foreground">{annualUnitsSold}</span> {t("operations:analyticsInputs.soldPerYr")}</span>
                : <span className="text-yellow-600 dark:text-yellow-400">{t("operations:analyticsInputs.annualUnitsMissing")}</span>}
              <span>
                <span className="font-medium text-foreground">
                  {nf(avgTurnDaysOverride) > 0 ? avgTurnDaysOverride : autoAvgTurnDays ?? "—"}
                </span>{t("operations:analyticsInputs.dTurn")}
              </span>
            </div>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${inputsExpanded ? "rotate-180" : ""}`} />
          </div>
        </button>

        {inputsExpanded && (
          <div className="border-t border-border px-4 pb-4 pt-4 space-y-4">
            <p className="text-xs text-muted-foreground">
              {t("operations:analyticsInputs.autoPopulatedNote")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("operations:analyticsInputs.avgInventoryUnits")}</Label>
                <FormattedInput
                  placeholder="e.g. 25"
                  value={avgInventoryUnits}
                  onChange={setAvgInventoryUnits}
                  disabled={isLocked}
                  data-testid="input-avg-inventory"
                />
                <p className="text-xs text-muted-foreground">{t("operations:analyticsInputs.avgInventoryDesc")}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("operations:analyticsInputs.annualUnitsSold")}</Label>
                <FormattedInput
                  placeholder="e.g. 240"
                  value={annualUnitsSold}
                  onChange={setAnnualUnitsSold}
                  disabled={isLocked}
                  data-testid="input-annual-units-sold"
                />
                <p className="text-xs text-muted-foreground">{t("operations:analyticsInputs.annualUnitsDesc")}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">
                  {t("operations:analyticsInputs.avgTurnTime")}
                  {autoAvgTurnDays != null && !nf(avgTurnDaysOverride) && (
                    <span className="ml-2 text-chart-3 font-normal">{t("operations:analyticsInputs.autoDays", { days: autoAvgTurnDays })}</span>
                  )}
                </Label>
                <FormattedInput
                  placeholder={autoAvgTurnDays != null ? String(autoAvgTurnDays) : "45"}
                  value={avgTurnDaysOverride}
                  onChange={setAvgTurnDaysOverride}
                  disabled={isLocked}
                  data-testid="input-avg-turn"
                />
                <p className="text-xs text-muted-foreground">
                  {nf(avgTurnDaysOverride) > 0
                    ? t("operations:analyticsInputs.manualOverride")
                    : autoAvgTurnDays != null
                      ? t("operations:analyticsInputs.autoFromReceivables")
                      : t("operations:analyticsInputs.uploadToAutoPopulate")}
                </p>
              </div>
            </div>
            {isLocked ? (
              <Button variant="outline" size="sm" onClick={() => {
                setInputsSnapshot({ avgInventoryUnits, annualUnitsSold, avgTurnDaysOverride });
                setIsLocked(false);
              }} data-testid="button-edit-inputs">
                <Pencil className="w-3 h-3 mr-1.5" />{t("operations:analyticsInputs.editInputs")}
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => { handleSaveInputs(); setInputsSnapshot(null); setIsLocked(true); }}
                  disabled={saving}
                  data-testid="button-save-analytics-inputs"
                >
                  {saving ? t("operations:analyticsInputs.saving") : t("common:save")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (inputsSnapshot) {
                      setAvgInventoryUnits(inputsSnapshot.avgInventoryUnits);
                      setAnnualUnitsSold(inputsSnapshot.annualUnitsSold);
                      setAvgTurnDaysOverride(inputsSnapshot.avgTurnDaysOverride);
                      // Edits may have auto-saved while typing — persist the
                      // restored (pre-edit) values so Cancel really reverts.
                      void postBasicInputs(JSON.stringify(buildBasicInputsBody(inputsSnapshot)));
                      setInputsSnapshot(null);
                    }
                    setIsLocked(true);
                  }}
                  disabled={saving}
                  data-testid="button-cancel-analytics-inputs"
                >
                  {t("common:cancel")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Auto-compute status */}
      {computeError && (
        <p className="text-sm text-destructive flex gap-1.5 items-center">
          <AlertTriangle className="w-4 h-4 shrink-0" />{computeError}
        </p>
      )}
      {computing && (
        <p className="text-xs text-muted-foreground flex gap-1.5 items-center">
          <span className="animate-pulse">●</span> {t("operations:costAnalysis.updatingAnalytics")}
        </p>
      )}

      {/* ── Results ── */}
      {output && (
        <>
          {/* Cost to Carry */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-base">{t("operations:costAnalysis.costToCarry")}</CardTitle>
                  <CardDescription className="mt-1">
                    {t("operations:costAnalysis.perCarDailyDesc", { units: fmtNum(output.inputsUsed.avgInventoryUnits) })}
                  </CardDescription>
                  {displayTaxYear && !latestFetching && (
                    <p className="text-xs text-muted-foreground mt-1" data-testid="label-cost-to-carry-tax-year">{t("operations:costAnalysis.fromTaxYear", { year: displayTaxYear })}</p>
                  )}
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs text-xs">
                      {t("operations:costAnalysis.costToCarryTip", { source: displayTaxYear ? t("operations:costAnalysis.theTaxYearReturn", { year: displayTaxYear }) : t("operations:costAnalysis.yourTaxReturn") })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-muted/40 text-center">
                  <p className="text-xs text-muted-foreground">{t("operations:costAnalysis.perCarDay")}</p>
                  <p className="text-xl font-bold mt-1">{fmt$(perUnitDaily > 0 ? perUnitDaily : (output.costToCarry?.carryingCostPerCarPerDay ?? 0), 2)}</p>
                </div>
                <div className="p-4 rounded-xl bg-primary/6 border border-primary/15 text-center">
                  <p className="text-xs text-muted-foreground">{t("operations:costAnalysis.totalMonth")}</p>
                  <p className="text-xl font-bold mt-1 text-primary">
                    {fmt$((perUnitDaily > 0 ? perUnitDaily * 30 : (output.costToCarry?.monthlyCarryingCostPerCar ?? 0)) * output.inputsUsed.avgInventoryUnits)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("operations:costAnalysis.unitsAvg", { units: fmtNum(output.inputsUsed.avgInventoryUnits) })}</p>
                </div>
                <div className="p-4 rounded-xl bg-muted/40 text-center">
                  <p className="text-xs text-muted-foreground">{t("operations:costAnalysis.totalYear")}</p>
                  <p className="text-xl font-bold mt-1">
                    {fmt$((perUnitDaily > 0 ? perUnitDaily * 365 : (output.costToCarry?.yearlyCarryingCostPerCar ?? 0)) * output.inputsUsed.avgInventoryUnits)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("operations:costAnalysis.unitsAvg", { units: fmtNum(output.inputsUsed.avgInventoryUnits) })}</p>
                </div>
              </div>


              {/* Calculator */}
              <div className="flex items-center gap-4 p-4 rounded-xl border bg-muted/20 flex-wrap">
                <Calculator className="w-4 h-4 text-primary shrink-0" />
                <p className="text-sm font-medium flex-1">{t("operations:costAnalysis.costToCarry1Unit")}</p>
                <div className="flex items-center gap-2">
                  <FormattedInput
                    value={holdingDays}
                    onChange={setHoldingDays}
                    className="w-20 text-center h-8 text-sm"
                    data-testid="input-holding-days"
                  />
                  <span className="text-muted-foreground text-sm shrink-0">{t("operations:costAnalysis.daysEquals")}</span>
                  <span className="text-xl font-bold text-primary shrink-0" data-testid="holding-cost-result">
                    {holdCost != null ? `$${holdCost}` : "—"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Estimated Breakeven */}
          {output.breakevenDays && (
            <Card className="border-primary/20 bg-primary/3">
              <CardHeader className="pb-0 pt-4 px-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("operations:costAnalysis.estimatedBreakeven")}</span>
                    {displayTaxYear && !latestFetching && (
                      <span className="text-xs text-muted-foreground" data-testid="label-breakeven-tax-year">{t("operations:costAnalysis.dotFromTaxYear", { year: displayTaxYear })}</span>
                    )}
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4 text-muted-foreground cursor-default" />
                      </TooltipTrigger>
                      <TooltipContent side="bottom" align="end" className="max-w-[220px] text-xs">
                        <p>{t("operations:costAnalysis.breakevenTip")}</p>
                        <p className="text-muted-foreground/70 mt-1">{displayTaxYear ? t("operations:costAnalysis.avgFromTaxYear", { year: displayTaxYear }) : t("operations:costAnalysis.avgFromTaxReturn")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex items-start gap-5 flex-wrap">
                  <div className="text-center shrink-0">
                    {displayBreakevenDays === 0 && displayHasExpenseData && displayDailyCostPerUnit > 0 ? (
                      <div className="w-28 h-28 rounded-full bg-amber-500/10 border-2 border-amber-500/40 flex flex-col items-center justify-center mx-auto">
                        <span className="text-xl font-black text-amber-600 dark:text-amber-400 leading-none text-center" data-testid="hold-recommendation">{t("operations:costAnalysis.lessThan1Day")}</span>
                      </div>
                    ) : (
                      <div className="w-28 h-28 rounded-full bg-primary/10 border-2 border-primary/30 flex flex-col items-center justify-center mx-auto">
                        <span className="text-4xl font-black text-primary leading-none" data-testid="hold-recommendation">
                          {displayBreakevenDays > 0 ? displayBreakevenDays : "—"}
                        </span>
                        <span className="text-xs text-muted-foreground font-medium">{t("operations:costAnalysis.days")}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-2 pt-1">
                    <h3 className="font-semibold text-sm">{t("operations:costAnalysis.howLongTitle")}</h3>
                    {displayBreakevenDays === 0 && displayHasExpenseData && displayDailyCostPerUnit > 0 && (
                      <p className="text-sm text-amber-600 dark:text-amber-400 leading-relaxed">
                        {t("operations:costAnalysis.holdingCostsExceed")}
                      </p>
                    )}
                    {!displayHasExpenseData && (
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {t("operations:costAnalysis.uploadForPrecise")}
                      </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                      <div className="p-3 rounded-lg bg-background/60 border">
                        <p className="text-xs text-muted-foreground">{t("operations:costAnalysis.dailyCostPerUnit")}</p>
                        <p className="font-bold text-sm mt-0.5">{t("operations:costAnalysis.perDay", { value: fmt$(displayDailyCostPerUnit, 2) })}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-background/60 border">
                        <p className="text-xs text-muted-foreground">{t("operations:costAnalysis.avgGrossProfitUnit")}</p>
                        <p className="font-bold text-sm mt-0.5">
                          {effectiveGrossPerUnit > 0
                            ? t("operations:costAnalysis.perUnit", { value: fmt$(effectiveGrossPerUnit) })
                            : "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cost Recovery Calculator */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calculator className="w-4 h-4 text-primary" />{t("operations:crc.title")}
              </CardTitle>
              <CardDescription>
                {hasCRC && crcNetMargin != null
                  ? t("operations:crc.descActive", { margin: fmtPct(crcNetMargin) })
                  : hasCRC
                    ? t("operations:crc.descNegative")
                    : t("operations:crc.descLocked")}
              </CardDescription>
              {displayTaxYear && !latestFetching && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="label-crc-tax-year">{t("operations:costAnalysis.fromTaxYear", { year: displayTaxYear })}</p>
              )}
            </CardHeader>
            <CardContent>
              {hasCRC && crcNetMargin == null ? (
                <div className="py-6 text-center space-y-1">
                  <p className="text-sm text-amber-600 font-medium">{t("operations:crc.opexExceed")}</p>
                  <p className="text-xs text-muted-foreground">{t("operations:crc.requiresPositive")}</p>
                </div>
              ) : hasCRC && crcNetMargin != null ? (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("operations:crc.costIncurred")}</Label>
                    <div className="relative max-w-xs">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">$</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={incurredCost}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/[^0-9]/g, "");
                          const num = parseInt(digits, 10);
                          setIncurredCost(digits === "" ? "" : num.toLocaleString("en-US"));
                        }}
                        placeholder="e.g. 50,000"
                        className="pl-6"
                        data-testid="input-incurred-cost"
                      />
                    </div>
                  </div>

                  {crcSalesNeeded != null || crcUnitsNeeded != null ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="p-4 rounded-xl bg-primary/8 border border-primary/20">
                          <p className="text-xs text-muted-foreground mb-1">{t("operations:crc.revenueNeeded")}</p>
                          <p className="text-xl font-bold text-primary tabular-nums" data-testid="text-sales-needed">
                            {crcSalesNeeded != null ? fmt$(crcSalesNeeded) : "—"}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">{t("operations:crc.basedOnCostMargin")}</p>
                        </div>
                        {crcUnitsNeeded != null && (
                          <div className="p-4 rounded-xl bg-muted/40">
                            <p className="text-xs text-muted-foreground mb-1">{t("operations:crc.unitsToSell")}</p>
                            <p className="text-xl font-bold tabular-nums" data-testid="text-units-needed">
                              ~{fmtNum(crcUnitsNeeded)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {t("operations:crc.basedOnNetPerUnit")}
                            </p>
                          </div>
                        )}
                      </div>
                      {crcUnitsNeeded != null && crcAvgSalePrice != null && (
                        <div className="px-3 py-2 rounded-lg bg-muted/30 border text-xs text-muted-foreground">
                          {t("operations:crc.unitsAtPricePrefix", { units: fmtNum(crcUnitsNeeded), price: fmt$(crcAvgSalePrice) })}<span className="font-semibold text-foreground">{fmt$(crcUnitsNeeded * crcAvgSalePrice)}</span>{t("operations:crc.revenueSuffix")}
                        </div>
                      )}
                    </div>
                  ) : (
                    crcIncurredCostNum === 0 && (
                      <p className="text-xs text-muted-foreground italic">{t("operations:crc.enterCostAbove")}</p>
                    )
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1 border-t text-center">
                    {[
                      { label: t("operations:crc.netMarginOnRev"), value: fmtPct(crcNetMargin) },
                      { label: t("operations:crc.grossPerUnit"), value: fmt$(crcGrossPerUnit) },
                      { label: t("operations:crc.netPerUnit"), value: fmt$(crcNetProfitPerUnit) },
                    ].map(({ label, value }) => (
                      <div key={label} className="pt-2">
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <p className="text-sm font-semibold tabular-nums mt-0.5">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <Calculator className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">{t("operations:crc.noPlData")}</p>
                  <p className="text-xs text-muted-foreground/60 mt-0.5">{t("operations:crc.enterAnnualFinancials")}</p>
                </div>
              )}
            </CardContent>
          </Card>

        </>
      )}

      <AlertDialog open={deletingReturnId !== null} onOpenChange={(open) => { if (!open) setDeletingReturnId(null); }}>
        <AlertDialogContent data-testid="dialog-delete-tax-return">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("operations:deleteReturn.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("operations:deleteReturn.desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-delete-return-cancel" onClick={() => setDeletingReturnId(null)}>
              {t("common:cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-delete-return-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (deletingReturnId !== null) deleteTaxReturnMutation.mutate(deletingReturnId); }}
            >
              {t("operations:deleteReturn.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={noFormsDialogPending !== null} onOpenChange={(open) => { if (!open) setNoFormsDialogPending(null); }}>
        <AlertDialogContent data-testid="dialog-no-forms-found">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("operations:noForms.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("operations:noForms.desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-no-forms-cancel" onClick={() => setNoFormsDialogPending(null)}>
              {t("common:cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-no-forms-proceed"
              onClick={async () => {
                if (noFormsDialogPending) {
                  applyResult(noFormsDialogPending, undefined, true);
                  try {
                    const clearRes = await fetch("/api/op-analytics/clear-financial-fields", { method: "POST", credentials: "include" });
                    if (!clearRes.ok) throw new Error("Clear failed");
                    qc.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
                  } catch {
                    toast({ title: t("operations:noForms.warningTitle"), description: t("operations:noForms.warningDesc"), variant: "destructive" });
                  }
                }
                setNoFormsDialogPending(null);
              }}
            >
              {t("operations:noForms.proceed")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Save Tax Return Dialog (appears immediately after successful parse) ── */}
      <Dialog
        open={showSaveReturnDialog}
        onOpenChange={() => {}}
      >
        <DialogContent
          className="max-w-sm"
          data-testid="dialog-save-tax-return"
          hideClose
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{t("operations:saveReturn.title")}</DialogTitle>
            <DialogDescription>
              {t("operations:saveReturn.desc")}
            </DialogDescription>
          </DialogHeader>
          {showSaveReturnDialog && saveRecoveryNotice}
          <div className="flex flex-col gap-3 py-1">
            <div className="flex flex-col gap-1">
              <Label htmlFor="dialog-tax-year" className="text-xs font-medium">
                {t("operations:saveReturn.taxYear")}
              </Label>
              <select
                id="dialog-tax-year"
                ref={saveReturnYearRef}
                value={saveTaxReturnYear}
                onChange={(e) => setSaveTaxReturnYear(e.target.value)}
                className="h-9 text-sm bg-background border border-input rounded-md px-2.5 outline-none text-foreground"
                data-testid="select-dialog-tax-return-year"
              >
                <option value="">{t("operations:saveReturn.selectYear")}</option>
                {Array.from({ length: new Date().getFullYear() - 2014 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="dialog-tax-name" className="text-xs font-medium">
                {t("operations:saveReturn.name")}
              </Label>
              <Input
                id="dialog-tax-name"
                ref={saveReturnNameRef}
                placeholder={t("operations:saveReturn.namePlaceholder")}
                value={saveTaxReturnName}
                onChange={(e) => setSaveTaxReturnName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSaveTaxReturn(); }}
                data-testid="input-dialog-tax-return-name"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="dialog-tax-units" className="text-xs font-medium">
                {t("operations:saveReturn.unitsSoldPerYear")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="dialog-tax-units"
                type="number"
                min="1"
                step="1"
                placeholder="e.g. 120"
                value={annualUnitsSold}
                onChange={(e) => setAnnualUnitsSold(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSaveTaxReturn(); }}
                data-testid="input-dialog-tax-return-units"
              />
              <p className="text-[11px] text-muted-foreground">{t("operations:saveReturn.unitsRequiredNote")}</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              size="sm"
              onClick={handleSaveTaxReturn}
              disabled={saveTaxReturnMutation.isPending || !saveTaxReturnName.trim() || !saveTaxReturnYear || !annualUnitsSold || nf(annualUnitsSold) <= 0}
              data-testid="button-dialog-tax-return-save"
            >
              {saveTaxReturnMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              {t("common:save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add Return Choice Dialog ── */}
      <Dialog open={showAddReturnChoiceDialog} onOpenChange={(v) => !v && setShowAddReturnChoiceDialog(false)}>
        <DialogContent className="max-w-sm" data-testid="dialog-add-return-choice">
          <DialogHeader>
            <DialogTitle className="text-base">{t("operations:addReturnChoice.title")}</DialogTitle>
            <DialogDescription className="text-xs">
              {t("operations:addReturnChoice.desc")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-1">
            <button
              className="flex flex-col items-center gap-2 rounded-lg border border-border bg-muted/40 p-4 text-center hover:bg-muted hover:border-primary/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="button-choice-upload-pdf"
              onClick={() => {
                pdfInputRef.current?.click();
                setShowAddReturnChoiceDialog(false);
              }}
            >
              <Upload className="w-6 h-6 text-primary" />
              <div>
                <p className="text-sm font-medium">{t("operations:addReturnChoice.uploadPdf")}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{t("operations:addReturnChoice.uploadPdfDesc")}</p>
              </div>
            </button>
            <button
              className="flex flex-col items-center gap-2 rounded-lg border border-border bg-muted/40 p-4 text-center hover:bg-muted hover:border-primary/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="button-choice-add-manually"
              onClick={() => {
                setShowAddReturnChoiceDialog(false);
                setManualEntryYear("");
                setManualEntryRevenue("");
                setManualEntryGrossProfit("");
                setManualEntryTotalExpenses("");
                setShowManualEntryDialog(true);
              }}
            >
              <Calculator className="w-6 h-6 text-primary" />
              <div>
                <p className="text-sm font-medium">{t("operations:addReturnChoice.addManually")}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{t("operations:addReturnChoice.addManuallyDesc")}</p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Manual Tax Return Entry Dialog ── */}
      <Dialog open={showManualEntryDialog} onOpenChange={(v) => !v && setShowManualEntryDialog(false)}>
        <DialogContent className="max-w-sm" data-testid="dialog-manual-tax-entry">
          <DialogHeader>
            <DialogTitle className="text-base">{t("operations:manualEntry.title")}</DialogTitle>
            <DialogDescription className="text-xs">
              {t("operations:manualEntry.desc")}
            </DialogDescription>
          </DialogHeader>
          {showManualEntryDialog && saveRecoveryNotice}
          <div className="space-y-3 pt-1">
            <div className="space-y-1">
              <Label htmlFor="manual-entry-year" className="text-xs font-medium">{t("operations:manualEntry.taxYear")}</Label>
              <Input
                id="manual-entry-year"
                placeholder="e.g. 2023"
                value={manualEntryYear}
                onChange={(e) => setManualEntryYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className="h-8 text-sm"
                maxLength={4}
                data-testid="input-manual-entry-year"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="manual-entry-revenue" className="text-xs font-medium">{t("operations:manualEntry.revenue")}</Label>
              <Input
                id="manual-entry-revenue"
                placeholder="e.g. 5000000"
                value={manualEntryRevenue}
                onChange={(e) => setManualEntryRevenue(e.target.value)}
                className="h-8 text-sm"
                type="number"
                min={0}
                data-testid="input-manual-entry-revenue"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="manual-entry-gross-profit" className="text-xs font-medium">{t("operations:manualEntry.grossProfit")}</Label>
              <Input
                id="manual-entry-gross-profit"
                placeholder="e.g. 1200000"
                value={manualEntryGrossProfit}
                onChange={(e) => setManualEntryGrossProfit(e.target.value)}
                className="h-8 text-sm"
                type="number"
                min={0}
                data-testid="input-manual-entry-gross-profit"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="manual-entry-total-expenses" className="text-xs font-medium">{t("operations:manualEntry.totalExpenses")}</Label>
              <Input
                id="manual-entry-total-expenses"
                placeholder="e.g. 800000"
                value={manualEntryTotalExpenses}
                onChange={(e) => setManualEntryTotalExpenses(e.target.value)}
                className="h-8 text-sm"
                type="number"
                min={0}
                data-testid="input-manual-entry-total-expenses"
              />
            </div>
          </div>
          {(() => {
            const yr = manualEntryYear.trim();
            const rev = nf(manualEntryRevenue);
            const gp = nf(manualEntryGrossProfit);
            const exp = nf(manualEntryTotalExpenses);
            const yearValid = /^\d{4}$/.test(yr);
            const errors: string[] = [];
            if (yr && !yearValid) errors.push(t("operations:manualEntry.errYear"));
            if (manualEntryRevenue && rev < 0) errors.push(t("operations:manualEntry.errRevenue"));
            if (manualEntryGrossProfit && gp < 0) errors.push(t("operations:manualEntry.errGrossProfit"));
            if (manualEntryTotalExpenses && exp < 0) errors.push(t("operations:manualEntry.errTotalExpenses"));
            const canSubmit = yearValid && rev >= 0 && gp >= 0 && exp >= 0 && (rev > 0 || gp > 0);
            return (
              <>
                {errors.length > 0 && (
                  <p className="text-[11px] text-destructive pt-1" data-testid="text-manual-entry-errors">{errors[0]}</p>
                )}
                <DialogFooter className="pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => setShowManualEntryDialog(false)}
                    data-testid="button-manual-entry-cancel"
                  >
                    {t("common:cancel")}
                  </Button>
                  <Button
                    size="sm"
                    className="h-8 text-xs"
                    disabled={!canSubmit || saveTaxReturnMutation.isPending}
                    onClick={handleManualEntrySubmit}
                    data-testid="button-manual-entry-save"
                  >
                    {saveTaxReturnMutation.isPending ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" />{t("operations:manualEntry.saving")}</>
                    ) : (
                      t("operations:manualEntry.saveReturn")
                    )}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Expense helpers (shared with Expenses tab) ───────────────────────────────

const EXPENSE_CHART_COLORS = [
  "hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))",
  "hsl(var(--chart-4))", "hsl(var(--chart-5))", "hsl(221,83%,53%)",
  "hsl(142,71%,45%)", "hsl(346,87%,43%)",
];

interface ExpenseCat { label: string; annual: number; color: string; }

function cleanExpLabel(label: string): string {
  const cleaned = label.replace(/^(other\s+deductions|other\s+expenses|other)(\s*:\s*|\s+)(?=\S)/i, "").trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function buildExpCats(inp: any): ExpenseCat[] {
  const dynamicItems: { label: string; amount: number }[] = inp?.expenses?.dynamicItems ?? [];
  return dynamicItems
    .filter((item) => item.amount > 0)
    .sort((a, b) => b.amount - a.amount)
    .map((item, i) => ({
      label: cleanExpLabel(item.label),
      annual: item.amount,
      color: EXPENSE_CHART_COLORS[i % EXPENSE_CHART_COLORS.length],
    }));
}

function computeTotalOpEx(inp: any): number {
  if ((inp?.annualTotalOperatingExpenses ?? 0) > 0) return inp.annualTotalOperatingExpenses;
  return buildExpCats(inp).reduce((s, c) => s + c.annual, 0);
}

function ExpBreakdownChart({ categories, annualUnitsSold }: { categories: ExpenseCat[]; annualUnitsSold: number }) {
  const { t } = useTranslation(["operations", "common"]);
  const [mode, setMode] = useState<"$" | "%">("$");
  const total = categories.reduce((s, c) => s + c.annual, 0);
  const barData = categories.map((c) => ({ label: c.label, value: Math.round(c.annual / annualUnitsSold), color: c.color, annual: c.annual })).sort((a, b) => b.value - a.value).slice(0, 10);
  const pieData = categories.map((c) => ({ name: c.label, value: parseFloat(((c.annual / total) * 100).toFixed(1)), color: c.color, annual: c.annual })).sort((a, b) => b.value - a.value).slice(0, 10);
  const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }: any) => {
    if (value < 5) return null;
    const R = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    return <text x={cx + radius * Math.cos(-midAngle * R)} y={cy + radius * Math.sin(-midAngle * R)} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>{value}%</text>;
  };
  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-3 pt-4 px-5">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" />{t("operations:expChart.title")}</CardTitle>
            <CardDescription className="text-xs mt-0.5">{mode === "$" ? t("operations:expChart.subtitleDollar") : t("operations:expChart.subtitlePct")}</CardDescription>
          </div>
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            {(["$", "%"] as const).map((m) => (
              <button key={m} onClick={() => setMode(m)} className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${mode === m ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`button-cost-mode-${m}`}>{m}</button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        {mode === "$" ? (
          <ResponsiveContainer width="100%" height={Math.max(140, barData.length * 30)}>
            <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 20, left: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={(v) => `$${v.toLocaleString()}`} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={148} />
              <RechartsTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }} formatter={(value: number, _: any, props: any) => [`$${value.toLocaleString()}/unit · $${props.payload.annual.toLocaleString()}/yr`, props.payload.label]} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={14}>{barData.map((_, i) => <Cell key={i} fill={barData[i].color} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" outerRadius={110} dataKey="value" labelLine={false} label={renderPieLabel}>
                {pieData.map((_, i) => <Cell key={i} fill={pieData[i].color} />)}
              </Pie>
              <RechartsTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }} formatter={(value: number, name: string, props: any) => [`${value}% · $${props.payload.annual.toLocaleString()}/yr`, name]} />
              <Legend formatter={(v) => <span style={{ fontSize: "11px" }}>{v}</span>} iconType="circle" iconSize={8} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Expenses Tab ─────────────────────────────────────────────────────────────

function ExpensesTab() {
  const { t } = useTranslation(["operations", "common"]);
  const { data: saved, isFetching } = useOpAnalytics();
  const { liveInp, displayTaxYear, loadedSavedReturn, isExtractionPending } = useContext(OperationsLiveCtx);
  const sourceTaxYear: number | null = (isExtractionPending || isFetching) ? null : (displayTaxYear ?? saved?.sourceTaxYear ?? null);

  // Merge live context inputs with saved data — live takes priority.
  // liveInp is always a non-null object (useMemo in OperationsPage), so we
  // rely on undefined checks for individual fields rather than a null guard.
  const inp = useMemo(() => {
    const base = saved?.input ?? null;
    if (!liveInp) return base;
    const liveItems = liveInp.expenses?.dynamicItems;
    // Override saved dynamicItems with live items only when a return has been
    // explicitly loaded (loadedSavedReturn is set) OR live items are populated.
    // This prevents the initial empty-array from wiping saved items when the
    // user navigates directly to this tab before the Analytics tab has loaded.
    const resolvedItems = (loadedSavedReturn || (liveItems && liveItems.length > 0))
      ? (liveItems ?? [])
      : base?.expenses?.dynamicItems ?? [];
    return {
      ...base,
      annualUnitsSold: liveInp.annualUnitsSold ?? base?.annualUnitsSold,
      grossProfit: liveInp.grossProfit ?? base?.grossProfit,
      totalIncome: liveInp.totalIncome ?? base?.totalIncome,
      revenue: liveInp.revenue ?? base?.revenue,
      annualTotalOperatingExpenses: liveInp.annualTotalOperatingExpenses ?? base?.annualTotalOperatingExpenses,
      avgGrossProfitPerUnit: liveInp.avgGrossProfitPerUnit ?? base?.avgGrossProfitPerUnit,
      expenses: {
        ...(base?.expenses ?? {}),
        dynamicItems: resolvedItems,
      },
    };
  }, [liveInp, saved, loadedSavedReturn]);

  const [showExpenseDetail, setShowExpenseDetail] = useState(false);

  const expCats = useMemo(() => inp ? buildExpCats(inp) : [], [inp]);
  const totalOpEx: number | null = inp ? computeTotalOpEx(inp) : null;
  const annualUnitsSold: number = inp?.annualUnitsSold ?? 0;
  const grossPerUnit: number | null = inp?.avgGrossProfitPerUnit ?? null;
  const expPerUnit: number | null = totalOpEx != null && annualUnitsSold > 0 ? totalOpEx / annualUnitsSold : null;
  const topLine: number | null = inp?.totalIncome ?? inp?.grossProfit ?? null;
  const hasOpEx: boolean =
    (inp?.annualTotalOperatingExpenses != null) ||
    (inp?.expenses?.dynamicItems?.some((i: { label: string; amount: number }) => i.amount > 0) ?? false);
  const netPerUnit: number | null =
    topLine != null && hasOpEx && totalOpEx != null && annualUnitsSold > 0
      ? (topLine - totalOpEx) / annualUnitsSold
      : null;

  if (isFetching) return null;

  if (!inp) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <BarChart3 className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-30" />
          <h3 className="font-semibold text-sm mb-1">{t("operations:expensesTab.noDataTitle")}</h3>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            <Trans i18nKey="operations:expensesTab.noDataBody" components={{ strong: <strong /> }} />
          </p>
        </CardContent>
      </Card>
    );
  }

  const dynamicItems: { label: string; amount: number }[] = inp.expenses?.dynamicItems ?? [];
  const isOdItem = (label: string) => /^other\s+(deductions|expenses)/i.test(label);
  const coreRows = dynamicItems
    .filter((item) => item.amount > 0 && !isOdItem(item.label))
    .sort((a, b) => b.amount - a.amount)
    .map((item) => ({ label: item.label, value: item.amount }));
  const odRows = dynamicItems
    .filter((item) => item.amount > 0 && isOdItem(item.label))
    .sort((a, b) => b.amount - a.amount)
    .map((item) => ({ label: cleanExpLabel(item.label), value: item.amount }));
  const expRows = [...coreRows, ...odRows];

  if (isExtractionPending) {
    return (
      <Card data-testid="card-expense-extraction-pending">
        <CardContent className="py-12 flex flex-col items-center justify-center gap-3 text-center">
          <Loader2 className="w-7 h-7 text-primary animate-spin" />
          <p className="font-semibold text-sm">{t("operations:expensesTab.extracting")}</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            {t("operations:expensesTab.extractingBody")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* Total OpEx summary */}
      <Card className="border-primary/20 bg-gradient-to-br from-primary/3 to-transparent">
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-primary" />{t("operations:expensesTab.totalOpExTitle")}
          </CardTitle>
          <CardDescription data-testid="label-expenses-tax-year">
            {sourceTaxYear
              ? t("operations:expensesTab.annualFiguresYear", { year: sourceTaxYear })
              : t("operations:expensesTab.annualFiguresLast")}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-1">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: t("operations:expensesTab.annualOpEx"), value: fmt$(totalOpEx) },
              { label: t("operations:expensesTab.monthlyOpEx"), value: fmt$(totalOpEx ? totalOpEx / 12 : null) },
              { label: t("operations:expensesTab.expensesPerUnit"), value: fmt$(expPerUnit) },
              { label: t("operations:expensesTab.netProfitPerUnit"), value: fmt$(netPerUnit), highlight: true },
            ].map(({ label, value, highlight }) => (
              <div key={label} className={`p-3 rounded-xl text-center ${highlight ? "bg-primary/10 border border-primary/20" : "bg-muted/40"}`}>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className={`text-base font-bold mt-0.5 ${highlight ? "text-primary" : ""}`}>{value}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Expense breakdown — collapsible */}
      <div className="rounded-xl border border-border overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-muted/30 transition-colors"
          onClick={() => setShowExpenseDetail(v => !v)}
          data-testid="button-toggle-expense-breakdown"
        >
          <span className="font-medium text-sm">{t("operations:expensesTab.viewBreakdown")}</span>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${showExpenseDetail ? "rotate-180" : ""}`} />
        </button>
        {showExpenseDetail && (
          <div className="border-t border-border">
            {expRows.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-muted-foreground">{t("operations:expensesTab.colCategory")}</th>
                      <th className="text-right py-2.5 px-4 text-xs font-semibold text-muted-foreground">{t("operations:expensesTab.colAnnual")}</th>
                      <th className="text-right py-2.5 px-4 text-xs font-semibold text-muted-foreground">{t("operations:expensesTab.colMonthly")}</th>
                      {annualUnitsSold > 0 && <th className="text-right py-2.5 px-4 text-xs font-semibold text-muted-foreground">{t("operations:expensesTab.colPerUnit")}</th>}
                      {totalOpEx && totalOpEx > 0 && <th className="text-right py-2.5 px-4 text-xs font-semibold text-muted-foreground">{t("operations:expensesTab.colPctOpEx")}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {coreRows.map(({ label, value }) => (
                      <tr key={label} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="py-3 px-4 font-medium text-sm text-left">{label}</td>
                        <td className="py-3 px-4 text-right tabular-nums">{fmt$(value)}</td>
                        <td className="py-3 px-4 text-right tabular-nums text-muted-foreground">{fmt$(value ? value / 12 : null)}</td>
                        {annualUnitsSold > 0 && <td className="py-3 px-4 text-right tabular-nums text-muted-foreground">{fmt$(value && annualUnitsSold > 0 ? value / annualUnitsSold : null)}</td>}
                        {totalOpEx && totalOpEx > 0 && (
                          <td className="py-3 px-4 text-right tabular-nums text-muted-foreground">
                            {value && totalOpEx > 0 ? `${((value / totalOpEx) * 100).toFixed(1)}%` : "—"}
                          </td>
                        )}
                      </tr>
                    ))}
                    {odRows.length > 0 && (
                      <tr className="bg-muted/40 border-b border-t border-border">
                        <td colSpan={2 + (annualUnitsSold > 0 ? 1 : 0) + (totalOpEx && totalOpEx > 0 ? 1 : 0) + 1}
                          className="py-2 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {t("operations:expensesTab.otherDeductions")}
                        </td>
                      </tr>
                    )}
                    {odRows.map(({ label, value }) => (
                      <tr key={label} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="py-3 px-4 text-sm text-left pl-6">{label}</td>
                        <td className="py-3 px-4 text-right tabular-nums">{fmt$(value)}</td>
                        <td className="py-3 px-4 text-right tabular-nums text-muted-foreground">{fmt$(value ? value / 12 : null)}</td>
                        {annualUnitsSold > 0 && <td className="py-3 px-4 text-right tabular-nums text-muted-foreground">{fmt$(value && annualUnitsSold > 0 ? value / annualUnitsSold : null)}</td>}
                        {totalOpEx && totalOpEx > 0 && (
                          <td className="py-3 px-4 text-right tabular-nums text-muted-foreground">
                            {value && totalOpEx > 0 ? `${((value / totalOpEx) * 100).toFixed(1)}%` : "—"}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  {expRows.length > 0 && (() => {
                    const totalAnnual = expRows.reduce((sum, r) => sum + (r.value || 0), 0);
                    return (
                      <tfoot>
                        <tr className="border-t border-border bg-muted/20">
                          <td className="py-3 px-4 font-bold text-sm text-left">{t("operations:expensesTab.totalExpenses")}</td>
                          <td className="py-3 px-4 text-right font-bold text-sm tabular-nums" data-testid="text-exp-analysis-total-annual">{fmt$(totalAnnual)}</td>
                          <td className="py-3 px-4 text-right font-bold text-sm tabular-nums" data-testid="text-exp-analysis-total-monthly">{fmt$(totalAnnual / 12)}</td>
                          {annualUnitsSold > 0 && (
                            <td className="py-3 px-4 text-right font-bold text-sm tabular-nums" data-testid="text-exp-analysis-total-per-unit">{fmt$(totalAnnual / annualUnitsSold)}</td>
                          )}
                          {totalOpEx && totalOpEx > 0 && (
                            <td className="py-3 px-4 text-right font-bold text-sm tabular-nums" data-testid="text-exp-analysis-total-pct">
                              {totalOpEx > 0 ? `${((totalAnnual / totalOpEx) * 100).toFixed(1)}%` : "—"}
                            </td>
                          )}
                        </tr>
                      </tfoot>
                    );
                  })()}
                </table>
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {t("operations:expensesTab.noCategories")}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Per-unit stat cards — only shown when the figures actually come from
          an uploaded tax return (sourceTaxYear present); onboarding-entered
          values must not be presented as tax-return data. */}
      {sourceTaxYear != null && (
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold mb-3">{t("operations:expensesTab.perUnitFromYear", { year: sourceTaxYear })}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: t("operations:expensesTab.grossProfitPerUnit"), value: fmt$(grossPerUnit), color: grossPerUnit != null && grossPerUnit >= 0 ? "text-chart-3" : "text-destructive", sub: annualUnitsSold > 0 ? t("operations:expensesTab.acrossUnits", { count: annualUnitsSold, units: annualUnitsSold.toLocaleString() }) : undefined },
            { label: t("operations:expensesTab.totalExpensesPerUnit"), value: fmt$(expPerUnit), color: "" },
            { label: t("operations:expensesTab.netProfitPerUnit"), value: fmt$(netPerUnit), color: netPerUnit != null && netPerUnit >= 0 ? "text-chart-3" : "text-destructive", sub: netPerUnit != null && grossPerUnit != null && grossPerUnit > 0 ? t("operations:expensesTab.ofGross", { pct: fmtPct(netPerUnit / grossPerUnit) }) : undefined },
          ].map(({ label, value, color, sub }) => (
            <Card key={label}>
              <CardContent className="pt-4 pb-4 px-5">
                <p className="text-xs text-muted-foreground leading-tight mb-1">{label}</p>
                <p className={`text-2xl font-bold tabular-nums ${color}`}>{value}</p>
                {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      )}

      {/* Expense breakdown chart */}
      {expCats.length > 0 && annualUnitsSold > 0 && (
        <ExpBreakdownChart categories={expCats} annualUnitsSold={annualUnitsSold} />
      )}

    </div>
  );
}

// ─── P&L Data Tab ─────────────────────────────────────────────────────────────

function TaxReturnSection({
  heading, rows, editKeys,
}: {
  heading: string;
  rows: [string, number | undefined][];
  editKeys: (string | null)[];
}) {
  const qc = useQueryClient();
  const { t } = useTranslation(["operations", "common"]);
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const { isFetching } = useOpAnalytics();

  const headingLabels: Record<string, string> = {
    "Revenue & Profit": t("operations:taxSection.revenueProfit"),
    "Expense Categories": t("operations:taxSection.expenseCategories"),
    "Operational Inputs Used": t("operations:taxSection.operationalInputs"),
  };
  const displayHeading = headingLabels[heading] ?? heading;

  const startEdit = () => {
    const init: Record<string, string> = {};
    rows.forEach(([, val], i) => {
      const key = editKeys[i];
      if (key) init[key] = val != null ? String(val) : "";
    });
    setDrafts(init);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updates: Record<string, any> = {};
      Object.entries(drafts).forEach(([k, v]) => {
        updates[k] = v === "" ? null : parseFloat(v) || 0;
      });
      const res = await fetch("/api/op-analytics/input", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      await qc.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
      setEditing(false);
      toast({ title: t("operations:taxSection.updatedTitle"), description: t("operations:taxSection.updatedBody") });
    } catch (err: any) {
      toast({ title: t("operations:taxSection.saveFailed"), description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const visible = rows.filter(([, v]) => v != null && v !== 0);
  if (!visible.length && !editing) return null;

  return (
    <Card>
      <CardHeader className="pb-2 pt-4 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-muted-foreground font-medium">{displayHeading}</CardTitle>
          {!editing ? (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={startEdit} data-testid={`btn-edit-${heading.replace(/\s+/g, "-").toLowerCase()}`}>
              <Pencil className="w-3 h-3 mr-1" /> {t("common:edit")}
            </Button>
          ) : (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setEditing(false)} disabled={saving} data-testid={`button-cancel-${heading.replace(/\s+/g, "-").toLowerCase()}`}>{t("common:cancel")}</Button>
              <Button size="sm" className="h-6 px-2 text-xs" onClick={save} disabled={saving} data-testid={`button-save-${heading.replace(/\s+/g, "-").toLowerCase()}`}>
                {saving ? "…" : t("common:save")}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-4 space-y-2">
        {rows.map(([label, val], i) => {
          if (!editing && (val == null || val === 0)) return null;
          const key = editKeys[i];
          return (
            <div key={label} className="flex justify-between items-center text-sm gap-3">
              <span className="text-muted-foreground flex-1 min-w-0">{label}</span>
              {editing && key ? (
                <input
                  type="number"
                  className="w-28 text-right text-sm font-semibold tabular-nums border rounded px-2 py-0.5 bg-background shrink-0"
                  value={drafts[key] ?? ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                  data-testid={`input-tax-${key.replace(/\./g, "-")}`}
                />
              ) : (
                <span className="font-semibold tabular-nums shrink-0 text-right">
                  {isFetching ? "…" : typeof val === "number" && val >= 1000
                    ? val.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
                    : val != null ? val.toLocaleString() : "—"}
                </span>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function PLDataTab() {
  const { t } = useTranslation(["operations", "common"]);
  const { data: saved, isFetching } = useOpAnalytics();
  const inp = (saved as any)?.input ?? null;

  if (isFetching) return null;

  if (!inp) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-30" />
          <h3 className="font-semibold text-sm mb-1">{t("operations:plDataTab.noDataTitle")}</h3>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            <Trans i18nKey="operations:plDataTab.noDataBody" components={{ strong: <strong /> }} />
          </p>
        </CardContent>
      </Card>
    );
  }

  const dynamicItemsLocked: { label: string; amount: number }[] = inp.expenses?.dynamicItems ?? [];

  return (
    <div className="space-y-5">
      {(saved as any)?.lastSavedAt && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <CheckCircle2 className="w-3.5 h-3.5 text-chart-3" />
          {t("operations:plDataTab.lastSaved", { value: new Date((saved as any).lastSavedAt).toLocaleString(intlLocale()) })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <TaxReturnSection
          heading="Revenue & Profit"
          rows={[
            [t("operations:plDataTab.annualRevenue"), inp.revenue],
            [t("operations:plDataTab.grossProfit"), inp.grossProfit],
            [
              t("operations:plDataTab.avgGrossPerUnit"),
              inp.grossProfit != null && inp.grossProfit > 0 && inp.annualUnitsSold != null && inp.annualUnitsSold > 0
                ? inp.grossProfit / inp.annualUnitsSold
                : inp.avgGrossProfitPerUnit,
            ],
          ]}
          editKeys={["revenue", "grossProfit", "avgGrossProfitPerUnit"]}
        />
        <TaxReturnSection
          heading="Expense Categories"
          rows={[
            [t("operations:plDataTab.totalOperatingExpenses"), inp.annualTotalOperatingExpenses],
            ...dynamicItemsLocked.map((item) => [item.label, item.amount] as [string, number | undefined]),
          ]}
          editKeys={["annualTotalOperatingExpenses", "expenses.dynamicItems"]}
        />
        <TaxReturnSection
          heading="Operational Inputs Used"
          rows={[
            [t("operations:plDataTab.avgInventoryUnits"), inp.avgInventoryUnits],
            [t("operations:plDataTab.annualUnitsSold"), inp.annualUnitsSold],
            [t("operations:plDataTab.avgTurnDays"), inp.avgTurnDays],
          ]}
          editKeys={["avgInventoryUnits", "annualUnitsSold", "avgTurnDays"]}
        />
      </div>
    </div>
  );
}

// ─── What-If Banner ───────────────────────────────────────────────────────────

function WhatIfBanner({ active, onReset, resetTestId = "btn-reset-whatif" }: { active: boolean; onReset: () => void; resetTestId?: string }) {
  const { t } = useTranslation(["operations", "common"]);
  if (!active) return null;
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-sm">
      <Lightbulb className="w-4 h-4 shrink-0" />
      <span className="flex-1 font-medium">{t("operations:whatIf.banner")}</span>
      <Button size="sm" variant="outline" className="h-7 px-3 text-xs border-amber-500/40 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10" onClick={onReset} data-testid={resetTestId}>
        <RotateCcw className="w-3 h-3 mr-1" />{t("operations:whatIf.reset")}
      </Button>
    </div>
  );
}

// ─── Editable metric row ───────────────────────────────────────────────────────

function EditableRow({
  label, value, onChange, unit = "", min = 0, max = 100, step = 1, readOnly = false, testId,
}: {
  label: string; value: number; onChange: (v: number) => void;
  unit?: string; min?: number; max?: number; step?: number; readOnly?: boolean; testId?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm text-muted-foreground flex-1">{label}</span>
      {readOnly ? (
        <span className="font-semibold tabular-nums text-sm">{value}{unit}</span>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="range"
            min={min} max={max} step={step}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            className="w-28 accent-primary"
            data-testid={testId}
          />
          <span className="font-semibold tabular-nums text-sm w-16 text-right">{value}{unit}</span>
        </div>
      )}
    </div>
  );
}

// ─── Marketing Tab ────────────────────────────────────────────────────────────

// Marketing cost benchmarks
const COST_PER_LEAD_LOW = 35;
const COST_PER_LEAD_HIGH = 75;
const COST_PER_SALE_LOW = 800;
const COST_PER_SALE_HIGH = 1200;
const MARKETING_PCT_GROSS_LOW = 8;
const MARKETING_PCT_GROSS_HIGH = 12;
const ROAS_LOW = 4;
const ROAS_HIGH = 6;

// Performance tier thresholds (relative to benchmark midpoints)
// Best: CPL <= 50% of benchmark high; Good: <= benchmark high; Watch: <= 150%; Worst: > 150%
type PerformanceTier = "Best" | "Good" | "Watch" | "Worst";

function getCplTier(cpl: number): PerformanceTier {
  if (cpl <= COST_PER_LEAD_LOW) return "Best";
  if (cpl <= COST_PER_LEAD_HIGH) return "Good";
  if (cpl <= COST_PER_LEAD_HIGH * 1.5) return "Watch";
  return "Worst";
}

function getCpsTier(cps: number): PerformanceTier {
  if (cps <= COST_PER_SALE_LOW) return "Best";
  if (cps <= COST_PER_SALE_HIGH) return "Good";
  if (cps <= COST_PER_SALE_HIGH * 1.5) return "Watch";
  return "Worst";
}

function combineTier(cplTier: PerformanceTier, cpsTier: PerformanceTier): PerformanceTier {
  const order: PerformanceTier[] = ["Best", "Good", "Watch", "Worst"];
  const i = Math.max(order.indexOf(cplTier), order.indexOf(cpsTier));
  return order[i];
}

const CONV_RATE_BENCHMARK = 18;
function getConvRateTier(convRatePct: number): PerformanceTier {
  if (convRatePct >= 25) return "Best";
  if (convRatePct >= CONV_RATE_BENCHMARK) return "Good";
  if (convRatePct >= 10) return "Watch";
  return "Worst";
}

function tierBadge(tier: PerformanceTier, t: (k: string) => string) {
  if (tier === "Best") return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" data-testid={`badge-tier-best`}>{t("operations:marketing.tierBest")}</span>;
  if (tier === "Good") return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-700 dark:text-blue-400" data-testid={`badge-tier-good`}>{t("operations:marketing.tierGood")}</span>;
  if (tier === "Watch") return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400" data-testid={`badge-tier-watch`}>{t("operations:marketing.tierWatch")}</span>;
  return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-destructive/15 text-destructive" data-testid={`badge-tier-worst`}>{t("operations:marketing.tierWorst")}</span>;
}

function tierColor(tier: PerformanceTier): string {
  if (tier === "Best") return "text-emerald-600 dark:text-emerald-400";
  if (tier === "Good") return "text-blue-600 dark:text-blue-400";
  if (tier === "Watch") return "text-amber-600 dark:text-amber-400";
  return "text-destructive";
}

// Benchmark indicator for summary bar
type BenchmarkStatus = "above" | "at" | "below";
function benchmarkIndicator(status: BenchmarkStatus, lowerIsBetter: boolean, t: (k: string) => string) {
  // For lower-is-better (CPL, CPS): "below" benchmark is good, "above" is bad.
  // For higher-is-better (ROAS): "above" benchmark is good, "below" is bad.
  const isGood = lowerIsBetter ? status === "below" : status === "above";
  if (status === "at") return <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium">{t("operations:marketing.onTarget")}</span>;
  if (isGood) return <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium">{lowerIsBetter ? t("operations:marketing.belowGood") : t("operations:marketing.aboveGood")}</span>;
  return <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium">{lowerIsBetter ? t("operations:marketing.aboveHigh") : t("operations:marketing.belowLow")}</span>;
}

type ChannelData = {
  id: string;
  name: string;
  spend: number;
  leads: number;
  sales: number;
  spendOverridden: boolean;
  leadsOverridden: boolean;
  salesOverridden: boolean;
};

// Onboarding progress API shape (only the fields we need)
type OnboardingProgressPayload = {
  vendors?: Array<{ id?: string; name: string; category: string; monthlyCost: number | string; leadsPerMonth?: number | string; salesPerMonth?: number | string }>;
};

type SortKey = "cpl" | "cps" | "spend";

// Tooltip wrapper for consistent styling
function MktTooltip({ content, children }: { content: string; children: React.ReactElement }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">{content}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function MarketingTab() {
  const { t } = useTranslation(["operations", "common"]);
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: profile } = useQuery<any>({ queryKey: ["/api/dealer-profile"] });
  const { data: onboardingProgress } = useQuery<OnboardingProgressPayload>({ queryKey: ["/api/onboarding/progress"] });
  const { liveInp } = useContext(OperationsLiveCtx);
  const { data: latestAnalytics, isFetching } = useOpAnalytics();

  const baseLeads = profile?.monthlyLeadVolume != null ? Number(profile.monthlyLeadVolume) : 0;
  const baseConvPct = profile?.leadToSaleConversionPct != null ? Number(profile.leadToSaleConversionPct) : 0;

  const avgGross = liveInp?.avgGrossProfitPerUnit ?? (profile?.avgGrossProfitPerUnit != null ? Number(profile.avgGrossProfitPerUnit) : 0);
  const annualGrossProfit = liveInp?.grossProfit ?? 0;
  const monthlyGrossProfit = annualGrossProfit > 0 ? annualGrossProfit / 12 : (avgGross > 0 && baseLeads > 0 ? avgGross * Math.round((baseLeads * baseConvPct) / 100) : 0);


  // ── Resolved base channels from onboarding vendors only ─────────────────
  const { resolvedChannels, spendSource } = useMemo(() => {
    const allVendors = onboardingProgress?.vendors ?? [];
    const mktVendors = allVendors.filter(v => {
      const cat = v.category?.toLowerCase() ?? '';
      return cat.includes('marketing') || cat.includes('advertising') || !cat;
    });

    if (mktVendors.some(v => (Number(v.monthlyCost) || 0) > 0 || v.leadsPerMonth != null)) {
      return {
        resolvedChannels: mktVendors.map(v => ({
          id: v.id ?? v.name,
          name: v.name,
          spend: Number(v.monthlyCost) || 0,
          leads: v.leadsPerMonth != null ? Number(v.leadsPerMonth) : 0,
          sales: v.salesPerMonth != null ? Number(v.salesPerMonth) : 0,
        })),
        spendSource: 'From your onboarding profile' as 'From your onboarding profile' | 'Previously saved' | '',
      };
    }

    return {
      resolvedChannels: [] as Array<{ id: string; name: string; spend: number; leads: number; sales: number }>,
      spendSource: '' as 'From your onboarding profile' | 'Previously saved' | '',
    };
  }, [onboardingProgress]);

  // ── Local edit state (optimistic UI only — authoritative data lives in dealer_vendors) ──
  // Keyed by vendor id to avoid name-collision bugs.
  const [localOverrides, setLocalOverrides] = useState<Record<string, { name?: string; spend?: number; leads?: number; sales?: number }>>({});
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [rowEditForm, setRowEditForm] = useState<{ name: string; spend: string; leads: string; sales: string }>({ name: '', spend: '', leads: '', sales: '' });
  const [sortKey, setSortKey] = useState<SortKey>('cpl');
  const [isSaving, setIsSaving] = useState(false);
  const [hasBeenCleared, setHasBeenCleared] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [optimisticDeleted, setOptimisticDeleted] = useState<Set<string>>(new Set());
  const [addChannelOpen, setAddChannelOpen] = useState(false);
  const [addChannelForm, setAddChannelForm] = useState<{ name: string; spend: string; leads: string; sales: string }>({ name: '', spend: '', leads: '', sales: '' });
  const [isAddingChannel, setIsAddingChannel] = useState(false);

  // ── Derived source label ───────────────────────────────────────────────────
  const effectiveSpendSource: string = spendSource || '';

  // ── Clear all local overrides ──────────────────────────────────────────────
  function clearAllChannels() {
    setLocalOverrides({});
    setHasBeenCleared(true);
    toast({ title: t("operations:marketing.editsClearedTitle"), description: t("operations:marketing.editsClearedBody") });
  }

  // ── Add a new marketing channel ────────────────────────────────────────────
  function openAddChannel() {
    setAddChannelForm({ name: '', spend: '', leads: '', sales: '' });
    setAddChannelOpen(true);
  }

  async function submitAddChannel() {
    const name = addChannelForm.name.trim();
    if (!name) {
      toast({ title: t("operations:marketing.nameRequiredTitle"), description: t("operations:marketing.nameRequiredBody"), variant: 'destructive' });
      return;
    }
    const spend = Math.max(0, nf(addChannelForm.spend));
    const leads = Math.max(0, nf(addChannelForm.leads));
    const sales = Math.max(0, nf(addChannelForm.sales));
    setIsAddingChannel(true);
    try {
      await apiRequest('POST', '/api/dealer-vendors', {
        name,
        monthlyCost: spend,
        leadsPerMonth: leads,
        salesPerMonth: sales,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['/api/onboarding/progress'] }),
        qc.invalidateQueries({ queryKey: ['/api/dealer-profile'] }),
      ]);
      setHasBeenCleared(false);
      setAddChannelOpen(false);
      toast({ title: t("operations:marketing.channelAddedTitle"), description: t("operations:marketing.channelAddedBody", { name }) });
    } catch {
      toast({ title: t("operations:marketing.addFailedTitle"), description: t("operations:marketing.addFailedBody"), variant: 'destructive' });
    } finally {
      setIsAddingChannel(false);
    }
  }

  // ── Delete a channel (with confirmation + optimistic removal) ─────────────
  async function deleteChannel(id: string) {
    setConfirmDeleteId(null);
    setOptimisticDeleted(prev => new Set(prev).add(id));
    try {
      await apiRequest('DELETE', `/api/dealer-vendors/${id}`);
      qc.invalidateQueries({ queryKey: ['/api/onboarding/progress'] });
      qc.invalidateQueries({ queryKey: ['/api/dealer-profile'] });
      toast({ title: t("operations:marketing.channelRemovedTitle"), description: t("operations:marketing.channelRemovedBody") });
    } catch {
      setOptimisticDeleted(prev => { const next = new Set(prev); next.delete(id); return next; });
      toast({ title: t("operations:marketing.deleteFailedTitle"), description: t("operations:marketing.deleteFailedBody"), variant: 'destructive' });
    }
  }

  // Merge: localOverrides (optimistic, keyed by vendor id) > resolvedChannels (dealer_vendors — authoritative)
  // Channels pending optimistic deletion are excluded immediately.
  const channels: ChannelData[] = useMemo(() => {
    return resolvedChannels
      .filter(base => !optimisticDeleted.has(base.id))
      .map(base => {
        const local = localOverrides[base.id] ?? {};
        return {
          id: base.id,
          name: local.name !== undefined ? local.name : base.name,
          spend: local.spend !== undefined ? local.spend : base.spend,
          leads: local.leads !== undefined ? local.leads : base.leads,
          sales: local.sales !== undefined ? local.sales : base.sales,
          spendOverridden: local.spend !== undefined,
          leadsOverridden: local.leads !== undefined,
          salesOverridden: local.sales !== undefined,
        };
      });
  }, [resolvedChannels, localOverrides, optimisticDeleted]);

  const totalMonthlySpend = channels.reduce((s, c) => s + c.spend, 0);
  const totalEstLeads = channels.reduce((s, c) => s + c.leads, 0);
  const totalVendorSales = channels.reduce((s, c) => s + c.sales, 0);
  // When leads exist, derive conversion directly from vendor data (even when sales=0 → 0%).
  // Only fall back to stored profile value when there are no leads at all.
  const liveConvPct = totalEstLeads > 0
    ? (totalVendorSales / totalEstLeads) * 100
    : (baseConvPct || 12);
  // Use channel-summed leads for blended CPS so channel edits are responsive.
  // Fall back to profile lead volume only when no channel data exists.
  const blendedLeads = totalEstLeads > 0 ? totalEstLeads : (baseLeads || 100);
  const blendedUnits = totalEstLeads > 0 ? totalVendorSales : Math.max(1, Math.round((blendedLeads * (baseConvPct || 12)) / 100));

  // ── Per-channel derived metrics ───────────────────────────────────────────
  type ChannelMetrics = ChannelData & {
    cpl: number | null;
    cps: number | null;
    tier: PerformanceTier | null;
    convRateTier: PerformanceTier | null;
    shareOfSpend: number;
  };

  const channelMetrics: ChannelMetrics[] = useMemo(() => {
    const raw = channels.map(ch => {
      const cpl = ch.leads > 0 && ch.spend > 0 ? ch.spend / ch.leads : null;
      // Use actual salesPerMonth from vendor data if available, otherwise estimate from conversion.
      // When liveConvPct is 0 (true zero from vendor data), estimated units are also 0.
      const estimatedUnits = liveConvPct > 0 ? Math.max(1, Math.round(ch.leads * liveConvPct / 100)) : 0;
      const units = ch.sales > 0 ? ch.sales : (ch.leads > 0 ? estimatedUnits : 0);
      const cps = units > 0 && ch.spend > 0 ? ch.spend / units : null;
      const tier = cpl != null && cps != null ? combineTier(getCplTier(cpl), getCpsTier(cps))
        : cpl != null ? getCplTier(cpl)
        : cps != null ? getCpsTier(cps)
        : null;
      // Conversion-rate fallback tier: only when spend is missing but both leads and sales exist.
      const convRateTier: PerformanceTier | null = (tier == null && ch.leads > 0 && ch.sales > 0 && ch.spend === 0)
        ? getConvRateTier((ch.sales / ch.leads) * 100)
        : null;
      const shareOfSpend = totalMonthlySpend > 0 ? (ch.spend / totalMonthlySpend) * 100 : 0;
      return { ...ch, cpl, cps, tier, convRateTier, shareOfSpend };
    });

    // Relative Best: only the single top-performing channel earns "Best".
    // Winner is the lowest CPS channel when the CPS view is active, otherwise lowest CPL.
    // All other channels that beat the absolute threshold are demoted to "Good".
    const bestChannels = raw.filter(c => c.tier === 'Best');
    if (bestChannels.length > 1) {
      const useCps = sortKey === 'cps';
      const winner = bestChannels.reduce((a, b) => {
        const aVal = useCps ? a.cps : a.cpl;
        const bVal = useCps ? b.cps : b.cpl;
        if (aVal == null) return b;
        if (bVal == null) return a;
        return aVal <= bVal ? a : b;
      });
      return raw.map(ch =>
        ch.tier === 'Best' && ch.id !== winner.id
          ? { ...ch, tier: 'Good' as PerformanceTier }
          : ch
      );
    }
    return raw;
  }, [channels, liveConvPct, totalMonthlySpend, sortKey]);

  // ── Sorted channels ───────────────────────────────────────────────────────
  const sortedMetrics: ChannelMetrics[] = useMemo(() => {
    return [...channelMetrics].sort((a, b) => {
      if (sortKey === 'cpl') {
        if (a.cpl == null && b.cpl == null) return 0;
        if (a.cpl == null) return 1;
        if (b.cpl == null) return -1;
        return a.cpl - b.cpl;
      }
      if (sortKey === 'cps') {
        if (a.cps == null && b.cps == null) return 0;
        if (a.cps == null) return 1;
        if (b.cps == null) return -1;
        return a.cps - b.cps;
      }
      return b.spend - a.spend;
    });
  }, [channelMetrics, sortKey]);

  // ── Best / Worst callout ──────────────────────────────────────────────────
  const rankedByCpl = [...channelMetrics].filter(c => c.cpl != null && c.spend > 0).sort((a, b) => (a.cpl ?? 0) - (b.cpl ?? 0));
  // Fallback: no-spend channels ranked by conversion rate (highest first = best)
  const rankedByConvRate = [...channelMetrics]
    .filter(c => c.spend === 0 && c.leads > 0 && c.sales > 0)
    .sort((a, b) => (b.sales / b.leads) - (a.sales / a.leads));
  const useCplRanking = rankedByCpl.length > 0;
  const bestChannel = useCplRanking ? (rankedByCpl[0] ?? null) : (rankedByConvRate[0] ?? null);
  const worstChannel = useCplRanking
    ? (rankedByCpl.length > 1 ? rankedByCpl[rankedByCpl.length - 1] : null)
    : (rankedByConvRate.length > 1 ? rankedByConvRate[rankedByConvRate.length - 1] : null);

  // ── Blended summary metrics ───────────────────────────────────────────────
  const blendedCpl = totalMonthlySpend > 0 && totalEstLeads > 0 ? totalMonthlySpend / totalEstLeads : null;
  const blendedCps = totalMonthlySpend > 0 && blendedUnits > 0 ? totalMonthlySpend / blendedUnits : null;
  const roas = totalMonthlySpend > 0 && monthlyGrossProfit > 0 ? monthlyGrossProfit / totalMonthlySpend : null;

  function getCplBenchmarkStatus(v: number): BenchmarkStatus {
    if (v <= COST_PER_LEAD_LOW) return 'below';
    if (v <= COST_PER_LEAD_HIGH) return 'at';
    return 'above';
  }
  function getCpsBenchmarkStatus(v: number): BenchmarkStatus {
    if (v <= COST_PER_SALE_LOW) return 'below';
    if (v <= COST_PER_SALE_HIGH) return 'at';
    return 'above';
  }
  function getRoasBenchmarkStatus(v: number): BenchmarkStatus {
    if (v >= ROAS_HIGH) return 'above';
    if (v >= ROAS_LOW) return 'at';
    return 'below';
  }
  // Marketing spend as % of monthly gross profit — healthy is 8–12%
  function getSpendBenchmarkStatus(spend: number, grossProfit: number): BenchmarkStatus | null {
    if (grossProfit <= 0) return null;
    const pct = (spend / grossProfit) * 100;
    if (pct < 8) return 'below';
    if (pct <= 12) return 'at';
    return 'above';
  }

  // ── Row-level editing — pencil button opens all four fields at once ──────
  function startRowEdit(id: string) {
    const base = resolvedChannels.find(r => r.id === id);
    if (!base) return;
    setEditingRowId(id);
    setRowEditForm({
      name: base.name,
      spend: String(base.spend),
      leads: String(base.leads),
      sales: String(base.sales),
    });
  }

  function cancelRowEdit() {
    setEditingRowId(null);
    setRowEditForm({ name: '', spend: '', leads: '', sales: '' });
  }

  async function saveRowEdit(id: string) {
    const spend = Math.max(0, nf(rowEditForm.spend));
    const leads = Math.max(0, nf(rowEditForm.leads));
    const sales = Math.max(0, nf(rowEditForm.sales));
    const nameVal = rowEditForm.name.trim();
    // Optimistic local update for instant UI feedback
    setLocalOverrides(prev => ({
      ...prev,
      [id]: { name: nameVal || undefined, spend, leads, sales },
    }));
    setEditingRowId(null);
    setIsSaving(true);
    try {
      const vendorPatch: Record<string, any> = {
        monthlyCost: spend,
        leadsPerMonth: leads,
        salesPerMonth: sales,
      };
      if (nameVal) vendorPatch.name = nameVal;
      await apiRequest('PATCH', `/api/dealer-vendors/${id}`, vendorPatch);
      // Refresh vendor data and onboarding profile
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['/api/onboarding/progress'] }),
        qc.invalidateQueries({ queryKey: ['/api/dealer-profile'] }),
      ]);
      // Clear local override now that server data is fresh
      setLocalOverrides(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch {
      toast({ title: t("operations:marketing.saveFailedTitle"), description: t("operations:marketing.saveFailedBody"), variant: 'destructive' });
      // Roll back optimistic update on failure
      setLocalOverrides(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } finally {
      setIsSaving(false);
    }
  }

  const hasOnboardingData = channels.length > 0;

  return (
    <div className="space-y-5">

      {/* ── Empty State ───────────────────────────────────────────────────── */}
      {!hasOnboardingData && !hasBeenCleared && (
        <Card className="border-dashed border-muted-foreground/30" data-testid="marketing-empty-state">
          <CardContent className="pt-10 pb-10 px-6 flex flex-col items-center text-center gap-4">
            <div className="p-4 rounded-full bg-muted/60">
              <Megaphone className="w-8 h-8 text-muted-foreground" />
            </div>
            <div className="space-y-1.5 max-w-sm">
              <p className="text-base font-semibold" data-testid="text-empty-state-title">{t("operations:marketing.emptyTitle")}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("operations:marketing.emptyBody")}
              </p>
            </div>
            <div className="w-full max-w-sm space-y-2 text-left">
              <div className="flex items-start gap-2.5 rounded-lg border border-border/60 bg-muted/30 px-4 py-3">
                <span className="shrink-0 mt-0.5 text-xs font-bold text-primary bg-primary/10 rounded-full w-5 h-5 flex items-center justify-center">3</span>
                <div>
                  <p className="text-sm font-medium">{t("operations:marketing.phase3Label")}</p>
                  <p className="text-xs text-muted-foreground">{t("operations:marketing.phase3Desc")}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Button
                variant="default"
                size="sm"
                onClick={() => window.location.href = '/onboarding'}
                data-testid="btn-go-to-onboarding"
              >
                {t("operations:marketing.completeOnboarding")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={openAddChannel}
                data-testid="button-add-channel-empty"
                className="gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                {t("operations:marketing.addChannelManually")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Best vs Worst Callout ─────────────────────────────────────────── */}
      {hasOnboardingData && <Card className="border-primary/20 bg-gradient-to-br from-primary/3 to-transparent">
        <CardContent className="pt-4 pb-4 px-5">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10 shrink-0 mt-0.5">
              <TrendingUp className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold mb-1" data-testid="text-best-worst-callout">
                {bestChannel && worstChannel && bestChannel.cpl != null && worstChannel.cpl != null && bestChannel.cps != null && worstChannel.cps != null ? (
                  <Trans
                    i18nKey="operations:marketing.calloutFull"
                    values={{ best: bestChannel.name, worst: worstChannel.name, cpl: fmt$(bestChannel.cpl, 0), cps: fmt$(bestChannel.cps, 0), leadX: (worstChannel.cpl / bestChannel.cpl).toFixed(1), saleX: (worstChannel.cps / bestChannel.cps).toFixed(1) }}
                    components={{ best: <span className="text-emerald-600 dark:text-emerald-400" />, worst: <span className="text-destructive" /> }}
                  />
                ) : bestChannel && worstChannel && bestChannel.cpl != null && worstChannel.cpl != null ? (
                  <Trans
                    i18nKey="operations:marketing.calloutCplPair"
                    values={{ best: bestChannel.name, worst: worstChannel.name, cpl: fmt$(bestChannel.cpl, 0), leadX: (worstChannel.cpl / bestChannel.cpl).toFixed(1) }}
                    components={{ best: <span className="text-emerald-600 dark:text-emerald-400" />, worst: <span className="text-destructive" /> }}
                  />
                ) : bestChannel && bestChannel.cpl != null ? (
                  <Trans
                    i18nKey="operations:marketing.calloutBestOnly"
                    values={{ best: bestChannel.name, cpl: fmt$(bestChannel.cpl, 0) }}
                    components={{ best: <span className="text-emerald-600 dark:text-emerald-400" /> }}
                  />
                ) : !useCplRanking && bestChannel && worstChannel && bestChannel.leads > 0 && bestChannel.sales > 0 && worstChannel.leads > 0 && worstChannel.sales > 0 ? (
                  <Trans
                    i18nKey="operations:marketing.calloutConvPair"
                    values={{ best: bestChannel.name, worst: worstChannel.name, bestRate: ((bestChannel.sales / bestChannel.leads) * 100).toFixed(1), worstRate: ((worstChannel.sales / worstChannel.leads) * 100).toFixed(1) }}
                    components={{ best: <span className="text-emerald-600 dark:text-emerald-400" />, worst: <span className="text-destructive" /> }}
                  />
                ) : !useCplRanking && bestChannel && bestChannel.leads > 0 && bestChannel.sales > 0 ? (
                  <Trans
                    i18nKey="operations:marketing.calloutConvBest"
                    values={{ best: bestChannel.name, bestRate: ((bestChannel.sales / bestChannel.leads) * 100).toFixed(1) }}
                    components={{ best: <span className="text-emerald-600 dark:text-emerald-400" /> }}
                  />
                ) : (
                  t("operations:marketing.calloutEnterData")
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {bestChannel && worstChannel && bestChannel.cps != null && worstChannel.cps != null
                  ? t("operations:marketing.subCplRanked", { worst: worstChannel.name, worstCps: fmt$(worstChannel.cps, 0), best: bestChannel.name, bestCps: fmt$(bestChannel.cps, 0) })
                  : !useCplRanking && bestChannel
                  ? t("operations:marketing.subConvNoSpend")
                  : t("operations:marketing.subEditHint")}
              </p>
            </div>
            {isSaving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground shrink-0 mt-1" />}
          </div>
        </CardContent>
      </Card>}

      {/* ── Summary Bar ───────────────────────────────────────────────────── */}
      {hasOnboardingData && <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: t("operations:marketing.totalMonthlySpend"),
            value: totalMonthlySpend > 0 ? fmt$(totalMonthlySpend) : '—',
            tooltip: t("operations:marketing.tipTotalSpend", { benchmark: !isFetching && monthlyGrossProfit > 0 ? t("operations:marketing.tipTotalSpendBenchmark", { low: fmt$(monthlyGrossProfit * 0.08), high: fmt$(monthlyGrossProfit * 0.12) }) : '' }),
            indicator: (() => {
              if (isFetching) return null;
              const st = getSpendBenchmarkStatus(totalMonthlySpend, monthlyGrossProfit);
              if (st == null || totalMonthlySpend === 0) return null;
              // For spend: "at" is good, "below" is low (under-investing), "above" is overspending
              if (st === 'at') return <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium">{t("operations:marketing.onTarget")}</span>;
              if (st === 'below') return <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium">{t("operations:marketing.belowLow")}</span>;
              return <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium">{t("operations:marketing.aboveHigh")}</span>;
            })() as React.ReactNode,
            testId: 'text-total-spend',
          },
          {
            label: t("operations:marketing.blendedCpl"),
            value: blendedCpl != null ? fmt$(blendedCpl, 0) : '—',
            tooltip: t("operations:marketing.tipBlendedCpl", { low: fmt$(COST_PER_LEAD_LOW), high: fmt$(COST_PER_LEAD_HIGH), redFlag: fmt$(COST_PER_LEAD_HIGH * 1.5) }),
            indicator: blendedCpl != null ? benchmarkIndicator(getCplBenchmarkStatus(blendedCpl), true, t) : null,
            testId: 'text-blended-cpl',
          },
          {
            label: t("operations:marketing.blendedCps"),
            value: blendedCps != null ? fmt$(blendedCps, 0) : '—',
            tooltip: t("operations:marketing.tipBlendedCps", { low: fmt$(COST_PER_SALE_LOW), high: fmt$(COST_PER_SALE_HIGH) }),
            indicator: blendedCps != null ? benchmarkIndicator(getCpsBenchmarkStatus(blendedCps), true, t) : null,
            testId: 'text-blended-cps',
          },
          {
            label: t("operations:marketing.roas"),
            value: !isFetching && roas != null ? `${roas.toFixed(1)}x` : '—',
            tooltip: t("operations:marketing.tipRoas", { low: ROAS_LOW, high: ROAS_HIGH }),
            indicator: !isFetching && roas != null ? benchmarkIndicator(getRoasBenchmarkStatus(roas), false, t) : null,
            testId: 'text-roas',
          },
        ].map(({ label, value, tooltip, indicator, testId }) => (
          <Card key={label}>
            <CardContent className="pt-3 pb-3 px-4">
              <MktTooltip content={tooltip}>
                <button className="flex items-center gap-1 group mb-0.5 w-full text-left">
                  <p className="text-xs text-muted-foreground flex-1">{label}</p>
                  <Info className="w-3 h-3 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors shrink-0" />
                </button>
              </MktTooltip>
              <p className="text-lg font-bold tabular-nums" data-testid={testId}>{value}</p>
              {indicator && <div className="mt-1">{indicator}</div>}
            </CardContent>
          </Card>
        ))}
      </div>}

      {/* ── Channel Comparison Table ──────────────────────────────────────── */}
      {(hasOnboardingData || hasBeenCleared) && <Card>
        <CardHeader className="pb-2 pt-4">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-sm flex items-center gap-2">
                <Megaphone className="w-4 h-4 text-primary" />{t("operations:marketing.channelComparison")}
              </CardTitle>
              <CardDescription className="flex items-center gap-1.5 mt-1">
                <Info className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                {effectiveSpendSource ? t("operations:marketing.editRowSource", { source: effectiveSpendSource }) : t("operations:marketing.editRowDefault")}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={openAddChannel}
                data-testid="button-add-channel"
                className="h-7 gap-1.5 text-xs"
                title={t("operations:marketing.addChannelTitleAttr")}
              >
                <Plus className="w-3.5 h-3.5" />
                {t("operations:marketing.addChannelBtn")}
              </Button>
              {Object.keys(localOverrides).length > 0 && (
                <button
                  onClick={clearAllChannels}
                  disabled={isSaving}
                  data-testid="btn-clear-channels"
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                  title={t("operations:marketing.clearEditsTitle")}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t("operations:marketing.clearEdits")}
                </button>
              )}
              <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5">
                {(['cpl', 'cps', 'spend'] as SortKey[]).map(k => (
                  <button
                    key={k}
                    onClick={() => setSortKey(k)}
                    data-testid={`btn-sort-${k}`}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${sortKey === k ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {k === 'cpl' ? t("operations:marketing.sortCpl") : k === 'cps' ? t("operations:marketing.sortCps") : t("operations:marketing.sortSpend")}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left text-xs text-muted-foreground font-medium py-2 pr-3 w-[150px]">
                    <MktTooltip content={t("operations:marketing.tipChannel")}>
                      <button className="flex items-center gap-0.5 group">
                        {t("operations:marketing.colChannel")} <Info className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </MktTooltip>
                  </th>
                  <th className="text-right text-xs text-muted-foreground font-medium py-2 pr-3 w-[110px]">
                    <MktTooltip content={t("operations:marketing.tipMonthlySpend")}>
                      <button className="flex items-center gap-0.5 ml-auto group">
                        {t("operations:marketing.colMonthlySpend")} <Info className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </MktTooltip>
                  </th>
                  <th className="text-right text-xs text-muted-foreground font-medium py-2 pr-3 w-[90px]">
                    <MktTooltip content={t("operations:marketing.tipEstLeads")}>
                      <button className="flex items-center gap-0.5 ml-auto group">
                        {t("operations:marketing.colEstLeads")} <Info className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </MktTooltip>
                  </th>
                  <th className="text-right text-xs text-muted-foreground font-medium py-2 pr-3 w-[80px]">
                    <MktTooltip content={t("operations:marketing.tipSales")}>
                      <button className="flex items-center gap-0.5 ml-auto group">
                        {t("operations:marketing.colSales")} <Info className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </MktTooltip>
                  </th>
                  <th className="text-right text-xs text-muted-foreground font-medium py-2 pr-3 w-[80px]">
                    <MktTooltip content={t("operations:marketing.tipCpl", { low: fmt$(COST_PER_LEAD_LOW), high: fmt$(COST_PER_LEAD_HIGH) })}>
                      <button className="flex items-center gap-0.5 ml-auto group">
                        {t("operations:marketing.colCpl")} <Info className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </MktTooltip>
                  </th>
                  <th className="text-right text-xs text-muted-foreground font-medium py-2 pr-3 w-[80px]">
                    <MktTooltip content={t("operations:marketing.tipCps", { low: fmt$(COST_PER_SALE_LOW), high: fmt$(COST_PER_SALE_HIGH) })}>
                      <button className="flex items-center gap-0.5 ml-auto group">
                        {t("operations:marketing.colCps")} <Info className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </MktTooltip>
                  </th>
                  <th className="text-right text-xs text-muted-foreground font-medium py-2 pr-3 w-[80px]">
                    <MktTooltip content={t("operations:marketing.tipRating", { metric: sortKey === 'cps' ? 'CPS' : 'CPL', cplHigh: COST_PER_LEAD_HIGH, cpsHigh: COST_PER_SALE_HIGH })}>
                      <button className="flex items-center gap-0.5 ml-auto group">
                        {t("operations:marketing.colRating")} <Info className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </MktTooltip>
                  </th>
                  <th className="text-right text-xs text-muted-foreground font-medium py-2 w-16" />
                </tr>
              </thead>
              <tbody>
                {sortedMetrics.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-10 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground" data-testid="text-channel-table-empty">
                        <Megaphone className="w-6 h-6 opacity-40" />
                        <p className="text-sm font-medium">{t("operations:marketing.emptyChannelsTitle")}</p>
                        <p className="text-xs max-w-xs leading-relaxed">
                          {t("operations:marketing.emptyChannelsBody")}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
                {sortedMetrics.map((ch, i) => {
                  const isEditingRow = editingRowId === ch.id;
                  const rowBg = i === 0 && ch.tier === 'Best' ? 'bg-emerald-500/5' : i === sortedMetrics.length - 1 && ch.tier === 'Worst' ? 'bg-destructive/5' : '';
                  const displayedSalesUnits = ch.sales > 0 ? ch.sales : (ch.leads > 0 && liveConvPct > 0 ? Math.max(1, Math.round(ch.leads * liveConvPct / 100)) : 0);
                  if (isEditingRow) {
                    return (
                      <tr key={ch.id} className="border-b border-border/30 bg-primary/5" data-testid={`channel-row-${i}`}>
                        <td className="py-2 pr-2">
                          <input
                            type="text"
                            className="w-full text-sm border border-primary rounded px-1.5 py-0.5 bg-background focus:outline-none font-medium"
                            value={rowEditForm.name}
                            onChange={e => setRowEditForm(f => ({ ...f, name: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') saveRowEdit(ch.id); if (e.key === 'Escape') cancelRowEdit(); }}
                            autoFocus
                            data-testid={`input-name-${i}`}
                          />
                        </td>
                        <td className="py-2 pr-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-muted-foreground text-xs shrink-0">$</span>
                            <input
                              type="number"
                              className="w-full min-w-0 text-right text-sm border border-primary rounded px-1.5 py-0.5 bg-background focus:outline-none"
                              value={rowEditForm.spend}
                              onChange={e => setRowEditForm(f => ({ ...f, spend: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') saveRowEdit(ch.id); if (e.key === 'Escape') cancelRowEdit(); }}
                              data-testid={`input-spend-${i}`}
                            />
                          </div>
                        </td>
                        <td className="py-2 pr-2 text-right">
                          <input
                            type="number"
                            className="w-full text-right text-sm border border-primary rounded px-1.5 py-0.5 bg-background focus:outline-none"
                            value={rowEditForm.leads}
                            onChange={e => setRowEditForm(f => ({ ...f, leads: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') saveRowEdit(ch.id); if (e.key === 'Escape') cancelRowEdit(); }}
                            data-testid={`input-leads-${i}`}
                          />
                        </td>
                        <td className="py-2 pr-2 text-right">
                          <input
                            type="number"
                            className="w-full text-right text-sm border border-primary rounded px-1.5 py-0.5 bg-background focus:outline-none"
                            value={rowEditForm.sales}
                            onChange={e => setRowEditForm(f => ({ ...f, sales: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') saveRowEdit(ch.id); if (e.key === 'Escape') cancelRowEdit(); }}
                            data-testid={`input-sales-${i}`}
                          />
                        </td>
                        <td colSpan={3} />
                        <td className="py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => saveRowEdit(ch.id)}
                              className="p-1 rounded hover:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 transition-colors"
                              title={t("common:save")}
                              data-testid={`btn-save-row-${i}`}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={cancelRowEdit}
                              className="p-1 rounded hover:bg-muted text-muted-foreground transition-colors"
                              title={t("common:cancel")}
                              data-testid={`btn-cancel-row-${i}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={ch.id} className={`border-b border-border/30 hover:bg-muted/20 transition-colors ${rowBg}`} data-testid={`channel-row-${i}`}>
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-1.5">
                          {i === 0 && <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold">#1</span>}
                          <span className="font-medium">{ch.name}</span>
                        </div>
                        {(ch.spendOverridden || ch.leadsOverridden || ch.salesOverridden) && (
                          <span className="text-[10px] text-muted-foreground">{t("operations:marketing.edited")}</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-right">
                        <span className="font-medium tabular-nums">{fmt$(ch.spend)}</span>
                      </td>
                      <td className="py-2.5 pr-3 text-right text-muted-foreground tabular-nums">
                        {fmtNum(ch.leads)}
                      </td>
                      <td className="py-2.5 pr-3 text-right text-muted-foreground tabular-nums">
                        {fmtNum(ch.sales > 0 ? ch.sales : 0)}
                      </td>
                      <td className={`py-2.5 pr-3 text-right font-semibold tabular-nums ${ch.cpl != null ? tierColor(getCplTier(ch.cpl)) : 'text-muted-foreground'}`} data-testid={`text-cpl-${i}`}>
                        {ch.cpl != null ? (
                          <MktTooltip content={t("operations:marketing.rowCplTip", { name: ch.name, cpl: fmt$(ch.cpl, 0), spend: fmt$(ch.spend), leads: fmtNum(ch.leads), low: fmt$(COST_PER_LEAD_LOW), high: fmt$(COST_PER_LEAD_HIGH) })}>
                            <button className="ml-auto">{fmt$(ch.cpl, 0)}</button>
                          </MktTooltip>
                        ) : ch.leads > 0 && ch.spend === 0 ? (
                          <MktTooltip content={t("operations:marketing.cplNoSpend")}>
                            <button
                              className="ml-auto text-xs text-primary/70 hover:text-primary transition-colors font-normal"
                              onClick={() => startRowEdit(ch.id)}
                              data-testid={`btn-add-spend-cpl-${i}`}
                            >
                              {t("operations:marketing.addSpend")}
                            </button>
                          </MktTooltip>
                        ) : (
                          <MktTooltip content={t("operations:marketing.cplEnter")}>
                            <button className="ml-auto">—</button>
                          </MktTooltip>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-right text-muted-foreground tabular-nums" data-testid={`text-cps-${i}`}>
                        {ch.cps != null ? (
                          <MktTooltip content={t("operations:marketing.rowCpsTip", { name: ch.name, cps: fmt$(ch.cps, 0), spend: fmt$(ch.spend), sales: fmtNum(displayedSalesUnits), low: fmt$(COST_PER_SALE_LOW), high: fmt$(COST_PER_SALE_HIGH) })}>
                            <button className="ml-auto">{fmt$(ch.cps, 0)}</button>
                          </MktTooltip>
                        ) : (ch.sales > 0 || ch.leads > 0) && ch.spend === 0 ? (
                          <MktTooltip content={t("operations:marketing.cpsNoSpend")}>
                            <button
                              className="ml-auto text-xs text-primary/70 hover:text-primary transition-colors font-normal"
                              onClick={() => startRowEdit(ch.id)}
                              data-testid={`btn-add-spend-cps-${i}`}
                            >
                              {t("operations:marketing.addSpend")}
                            </button>
                          </MktTooltip>
                        ) : (
                          <MktTooltip content={t("operations:marketing.cpsEnter")}>
                            <button className="ml-auto">—</button>
                          </MktTooltip>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-right">
                        {ch.tier != null ? (
                          <MktTooltip content={t("operations:marketing.rowRatingTip", { name: ch.name, tier: ch.tier, metric: sortKey === 'cps' ? 'CPS' : 'CPL', cplHigh: COST_PER_LEAD_HIGH, cpsHigh: COST_PER_SALE_HIGH })}>
                            <button>{tierBadge(ch.tier, t)}</button>
                          </MktTooltip>
                        ) : ch.convRateTier != null ? (
                          <MktTooltip content={t("operations:marketing.rowConvTip", { name: ch.name, rate: ((ch.sales / ch.leads) * 100).toFixed(0), sales: ch.sales, leads: ch.leads, benchmark: CONV_RATE_BENCHMARK })}>
                            <button className="flex items-center gap-0.5 ml-auto">
                              {tierBadge(ch.convRateTier, t)}
                              <span className="text-[10px] text-muted-foreground">*</span>
                            </button>
                          </MktTooltip>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td className="py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {confirmDeleteId === ch.id ? (
                            <>
                              <button
                                type="button"
                                onClick={() => deleteChannel(ch.id)}
                                disabled={isSaving}
                                className="px-1.5 py-0.5 rounded text-xs font-medium bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors disabled:opacity-50"
                                title={t("operations:marketing.confirmDeleteTitle")}
                                data-testid={`btn-confirm-delete-row-${i}`}
                              >
                                {t("operations:marketing.deleteBtn")}
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteId(null)}
                                disabled={isSaving}
                                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                                title={t("common:cancel")}
                                data-testid={`btn-cancel-delete-row-${i}`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => startRowEdit(ch.id)}
                                disabled={isSaving}
                                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                                title={t("operations:marketing.editChannelTitle")}
                                data-testid={`btn-edit-row-${i}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              {/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ch.id) && (
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteId(ch.id)}
                                  disabled={isSaving}
                                  className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                                  title={t("operations:marketing.removeChannelTitle")}
                                  data-testid={`btn-delete-row-${i}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-border">
                  <td className="py-2 pr-3 font-semibold text-sm">{t("operations:marketing.total")}</td>
                  <td className="py-2 pr-3 text-right font-bold tabular-nums" data-testid="text-channel-total">{fmt$(totalMonthlySpend)}</td>
                  <td className="py-2 pr-3 text-right text-muted-foreground tabular-nums">{fmtNum(totalEstLeads)}</td>
                  <td className="py-2 pr-3 text-right text-muted-foreground tabular-nums">{fmtNum(totalVendorSales)}</td>
                  <td className="py-2 pr-3 text-right font-bold tabular-nums">{blendedCpl != null ? fmt$(blendedCpl, 0) : '—'}</td>
                  <td className="py-2 pr-3 text-right text-muted-foreground">{blendedCps != null ? fmt$(blendedCps, 0) : '—'}</td>
                  <td />
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2 px-0.5 flex items-center gap-1">
            <Info className="w-3 h-3 shrink-0" />
            {t("operations:marketing.footerHint", { sortLabel: sortKey === 'cpl' ? t("operations:marketing.sortLabelCpl") : sortKey === 'cps' ? t("operations:marketing.sortLabelCps") : t("operations:marketing.sortLabelSpend") })}
          </p>
        </CardContent>
      </Card>}

      {/* ── Website Tracking (self-service embeddable widget) ────────────── */}
      <WebsiteTrackingSection />

      {/* ── Add Channel Dialog ────────────────────────────────────────────── */}
      <Dialog open={addChannelOpen} onOpenChange={(open) => { if (!isAddingChannel) setAddChannelOpen(open); }}>
        <DialogContent data-testid="dialog-add-channel">
          <DialogHeader>
            <DialogTitle>{t("operations:marketing.addDialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("operations:marketing.addDialogDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="add-channel-name">{t("operations:marketing.channelName")}</Label>
              <Input
                id="add-channel-name"
                placeholder={t("operations:marketing.channelNamePlaceholder")}
                value={addChannelForm.name}
                onChange={(e) => setAddChannelForm(f => ({ ...f, name: e.target.value }))}
                data-testid="input-add-channel-name"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="add-channel-spend">{t("operations:marketing.monthlySpendLabel")}</Label>
                <FormattedInput
                  id="add-channel-spend"
                  inputMode="decimal"
                  liveFormat
                  placeholder="0"
                  value={addChannelForm.spend}
                  onChange={(val) => setAddChannelForm(f => ({ ...f, spend: val }))}
                  data-testid="input-add-channel-spend"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="add-channel-leads">{t("operations:marketing.estLeadsLabel")}</Label>
                <Input
                  id="add-channel-leads"
                  inputMode="numeric"
                  placeholder="0"
                  value={addChannelForm.leads}
                  onChange={(e) => setAddChannelForm(f => ({ ...f, leads: e.target.value }))}
                  data-testid="input-add-channel-leads"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="add-channel-sales">{t("operations:marketing.salesLabel")}</Label>
                <Input
                  id="add-channel-sales"
                  inputMode="numeric"
                  placeholder="0"
                  value={addChannelForm.sales}
                  onChange={(e) => setAddChannelForm(f => ({ ...f, sales: e.target.value }))}
                  data-testid="input-add-channel-sales"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddChannelOpen(false)} disabled={isAddingChannel} data-testid="button-cancel-add-channel">
              {t("common:cancel")}
            </Button>
            <Button onClick={submitAddChannel} disabled={isAddingChannel} data-testid="button-save-add-channel">
              {isAddingChannel ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />{t("operations:marketing.adding")}</> : t("operations:marketing.addChannelBtn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Cash Flow Tab ────────────────────────────────────────────────────────────

const BHPH_RANGE_LABELS: Record<string, { display: string; midpoint: number }> = {
  "<100k":    { display: "< $100k",          midpoint: 50000 },
  "100-500k": { display: "$100k – $500k",    midpoint: 300000 },
  "500k-2M":  { display: "$500k – $2M",      midpoint: 1250000 },
  "2M+":      { display: "$2M+",             midpoint: 3000000 },
};

const LOC_RANGE_LABELS: Record<string, { display: string; midpoint: number }> = {
  "<100k":    { display: "< $100k",          midpoint: 50000 },
  "100-250k": { display: "$100k – $250k",    midpoint: 175000 },
  "250-500k": { display: "$250k – $500k",    midpoint: 375000 },
  "500k+":    { display: "$500k+",           midpoint: 750000 },
};

function CashFlowTab() {
  const { t } = useTranslation(["operations", "common"]);
  const { data: profile } = useQuery<any>({ queryKey: ["/api/dealer-profile"] });
  const { liveInp } = useContext(OperationsLiveCtx);

  const pn = (v: any) => (v != null && v !== "" ? parseFloat(String(v)) : 0);

  const avgReconDays          = pn(profile?.avgReconDays);
  const avgDaysPurchaseToFrontline = pn(profile?.avgDaysPurchaseToFrontline);
  const avgFloorplanRatePct   = pn(profile?.avgFloorplanRate);   // stored as percent, e.g. 18.5 for 18.5% APR
  const avgVehicleValue       = pn(profile?.avgVehicleValue);
  const avgUnitsOnLot         = pn(profile?.avgUnitsOnLot);
  const bhphReceivablesRange  = profile?.bhphReceivablesRange as string | undefined;
  const hasLineOfCredit       = profile?.hasLineOfCredit as boolean | undefined;
  const lineOfCreditRange     = profile?.lineOfCreditRange as string | undefined;

  // Phase 7 data completeness
  const phase7Complete = bhphReceivablesRange != null || hasLineOfCredit != null;

  // ── Recon Pipeline Delay Cost ──────────────────────────────────────────────
  // We use avgDaysPurchaseToFrontline if available, otherwise avgReconDays.
  // Carrying cost per day = (avgFloorplanRatePct/100 / 365) * avgVehicleValue,
  // falling back to 18.5% if no rate entered.
  const reconDelayDays    = avgDaysPurchaseToFrontline > 0 ? avgDaysPurchaseToFrontline
                          : avgReconDays > 0 ? avgReconDays : null;

  // Convert percent to decimal; default to 18.5% (typical sub-prime floor rate)
  const floorplanRate     = avgFloorplanRatePct > 0 ? avgFloorplanRatePct / 100 : 0.185;
  const vehicleValue      = avgVehicleValue > 0 ? avgVehicleValue : null;
  const inventoryUnits    = avgUnitsOnLot > 0 ? avgUnitsOnLot
                          : (liveInp?.annualUnitsSold ? liveInp.annualUnitsSold / 12 * 2 : null);

  const dailyCarryPerUnit = vehicleValue != null ? (floorplanRate / 365) * vehicleValue : null;

  const reconDelayCost = (reconDelayDays != null && dailyCarryPerUnit != null && inventoryUnits != null)
    ? reconDelayDays * dailyCarryPerUnit * inventoryUnits
    : null;

  const reconDataReady = reconDelayCost != null;

  // ── BHPH Receivables ──────────────────────────────────────────────────────
  const bhphInfo = bhphReceivablesRange ? BHPH_RANGE_LABELS[bhphReceivablesRange] : null;

  // ── Credit Line Utilization Gap ───────────────────────────────────────────
  // We show the full credit line range as potentially deployable capital
  // (conservative: the dealer may be using some of it, so whole range = opportunity range)
  const locInfo = (hasLineOfCredit && lineOfCreditRange) ? LOC_RANGE_LABELS[lineOfCreditRange] : null;

  const DISCLAIMER = t("operations:cashFlow.disclaimer");

  return (
    <div className="space-y-5">
      {/* Intro Banner */}
      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl px-4 py-3 flex items-start gap-3">
        <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-800 dark:text-blue-300">
          <Trans t={t} i18nKey="operations:cashFlow.intro" components={{ strong: <strong /> }} />
        </p>
      </div>

      {/* Card 1 — Recon Pipeline Delay Cost */}
      <Card data-testid="card-recon-delay">
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-primary" />
            {t("operations:cashFlow.recon.title")}
          </CardTitle>
          <CardDescription>{t("operations:cashFlow.recon.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {reconDataReady ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-xl bg-muted/40 text-center">
                  <p className="text-xs text-muted-foreground">{t("operations:cashFlow.recon.reconFrontlineDays")}</p>
                  <p className="text-lg font-bold tabular-nums" data-testid="text-recon-days">{reconDelayDays}</p>
                </div>
                <div className="p-3 rounded-xl bg-muted/40 text-center">
                  <p className="text-xs text-muted-foreground">{t("operations:cashFlow.recon.avgVehicleValue")}</p>
                  <p className="text-lg font-bold tabular-nums" data-testid="text-recon-vehicle-value">{vehicleValue != null ? fmt$(vehicleValue) : "—"}</p>
                </div>
                <div className="p-3 rounded-xl bg-muted/40 text-center">
                  <p className="text-xs text-muted-foreground">{t("operations:cashFlow.recon.dailyCarryPerUnit")}</p>
                  <p className="text-lg font-bold tabular-nums" data-testid="text-recon-daily-carry">{dailyCarryPerUnit != null ? fmt$(dailyCarryPerUnit, 2) : "—"}</p>
                </div>
                <div className="p-3 rounded-xl bg-primary/10 border border-primary/20 text-center">
                  <p className="text-xs text-muted-foreground">{t("operations:cashFlow.recon.estCapitalAtRisk")}</p>
                  <p className="text-lg font-bold tabular-nums text-primary" data-testid="text-recon-frozen-capital">{fmt$(reconDelayCost!)}</p>
                </div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2.5 text-sm text-amber-800 dark:text-amber-300">
                <Lightbulb className="w-3.5 h-3.5 inline mr-1.5" />
                <Trans t={t} i18nKey="operations:cashFlow.recon.action" components={{ strong: <strong /> }} values={{ amount: dailyCarryPerUnit != null && inventoryUnits != null ? fmt$(dailyCarryPerUnit * inventoryUnits) : "—" }} />
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="disclaimer-recon">
                <Info className="w-3 h-3 shrink-0" />{DISCLAIMER}
              </p>
            </>
          ) : (
            <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg px-4 py-5 text-center space-y-1" data-testid="prompt-recon-missing">
              <p className="font-medium">{t("operations:cashFlow.recon.missingTitle")}</p>
              <p><Trans t={t} i18nKey="operations:cashFlow.recon.missingBody" components={{ strong: <strong /> }} /></p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card 2 — BHPH Receivables */}
      <Card data-testid="card-bhph-receivables">
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <Wallet className="w-4 h-4 text-primary" />
            {t("operations:cashFlow.bhph.title")}
          </CardTitle>
          <CardDescription>{t("operations:cashFlow.bhph.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!phase7Complete ? (
            <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg px-4 py-5 text-center space-y-1" data-testid="prompt-bhph-missing">
              <p className="font-medium">{t("operations:cashFlow.bhph.missingTitle")}</p>
              <p><Trans t={t} i18nKey="operations:cashFlow.bhph.missingBody" components={{ strong: <strong /> }} /></p>
            </div>
          ) : bhphInfo ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-muted/40 text-center">
                  <p className="text-xs text-muted-foreground">{t("operations:cashFlow.bhph.receivablesRange")}</p>
                  <p className="text-lg font-bold tabular-nums" data-testid="text-bhph-range">{bhphInfo.display}</p>
                </div>
                <div className="p-3 rounded-xl bg-primary/10 border border-primary/20 text-center">
                  <p className="text-xs text-muted-foreground">{t("operations:cashFlow.bhph.estMidpoint")}</p>
                  <p className="text-lg font-bold tabular-nums text-primary" data-testid="text-bhph-midpoint">{fmt$(bhphInfo.midpoint)}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                <Trans t={t} i18nKey="operations:cashFlow.bhph.summary" components={{ strong: <strong /> }} values={{ range: bhphInfo.display }} />
              </p>
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2.5 text-sm text-amber-800 dark:text-amber-300">
                <Lightbulb className="w-3.5 h-3.5 inline mr-1.5" />
                <Trans t={t} i18nKey="operations:cashFlow.bhph.action" components={{ strong: <strong /> }} />
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="disclaimer-bhph">
                <Info className="w-3 h-3 shrink-0" />{DISCLAIMER}
              </p>
            </>
          ) : (
            <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg px-4 py-5 text-center space-y-1" data-testid="prompt-bhph-no-range">
              <p className="font-medium">{t("operations:cashFlow.bhph.noRangeTitle")}</p>
              <p><Trans t={t} i18nKey="operations:cashFlow.bhph.noRangeBody" components={{ strong: <strong /> }} /></p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card 3 — Credit Line Utilization Gap */}
      <Card data-testid="card-credit-line">
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            {t("operations:cashFlow.loc.title")}
          </CardTitle>
          <CardDescription>{t("operations:cashFlow.loc.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!phase7Complete ? (
            <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg px-4 py-5 text-center space-y-1" data-testid="prompt-loc-missing">
              <p className="font-medium">{t("operations:cashFlow.loc.missingTitle")}</p>
              <p><Trans t={t} i18nKey="operations:cashFlow.loc.missingBody" components={{ strong: <strong /> }} /></p>
            </div>
          ) : !hasLineOfCredit ? (
            <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg px-4 py-5 text-center space-y-1" data-testid="prompt-no-loc">
              <p className="font-medium">{t("operations:cashFlow.loc.noLocTitle")}</p>
              <p>{t("operations:cashFlow.loc.noLocBody")}</p>
            </div>
          ) : locInfo ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-muted/40 text-center">
                  <p className="text-xs text-muted-foreground">{t("operations:cashFlow.loc.creditLineRange")}</p>
                  <p className="text-lg font-bold tabular-nums" data-testid="text-loc-range">{locInfo.display}</p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center">
                  <p className="text-xs text-muted-foreground">{t("operations:cashFlow.loc.estAvailableCapital")}</p>
                  <p className="text-lg font-bold tabular-nums text-emerald-600 dark:text-emerald-400" data-testid="text-loc-available">{locInfo.display}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                <Trans t={t} i18nKey="operations:cashFlow.loc.summary" components={{ strong: <strong /> }} values={{ range: locInfo.display }} />
              </p>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2.5 text-sm text-blue-800 dark:text-blue-300">
                <Lightbulb className="w-3.5 h-3.5 inline mr-1.5" />
                <Trans t={t} i18nKey="operations:cashFlow.loc.action" components={{ strong: <strong /> }} />
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="disclaimer-loc">
                <Info className="w-3 h-3 shrink-0" />{DISCLAIMER}
              </p>
            </>
          ) : (
            <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg px-4 py-5 text-center space-y-1" data-testid="prompt-loc-no-range">
              <p className="font-medium">{t("operations:cashFlow.loc.noRangeTitle")}</p>
              <p><Trans t={t} i18nKey="operations:cashFlow.loc.noRangeBody" components={{ strong: <strong /> }} /></p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Team Tab ──────────────────────────────────────────────────────────────────

type TeamDataSource = "tax_return" | "financial_statement" | "op_analytics" | "profile" | "onboarding" | "manual" | "derived";

type TeamMetricsData = {
  hasEmployeeData: boolean;
  isRoleDataComplete: boolean;
  inventoryType: string;
  headcounts: { total: number; sales: number; fi: number; serviceRecon?: number; admin?: number };
  metrics: {
    revenuePerEmployee: number | null;
    unitsPerSalesperson: number | null;
    grossPerFI: number | null;
    costPerEmployee: number | null;
  };
  inputs: {
    avgMonthlyRevenue: number | null;
    avgSalesPerMonth: number | null;
    avgBackEndGross: number | null;
    monthlyOperatingExpenses: number | null;
    monthlyGrossProfit: number | null;
    avgGrossProfitPerUnit: number | null;
  };
  dataSources: Record<string, TeamDataSource>;
  taxYear: number | null;
  payroll: number | null;
  financialPeriod: string | null;
};

function getStatusInfo(value: number, benchmark: { low: number; median: number; high: number }, higherIsBetter: boolean) {
  if (higherIsBetter) {
    if (value >= benchmark.median) return { label: "onTrack", color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" };
    if (value >= benchmark.low) return { label: "needsAttention", color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" };
    return { label: "belowBenchmark", color: "text-destructive", bg: "bg-destructive/10 border-destructive/20" };
  } else {
    if (value <= benchmark.median) return { label: "onTrack", color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" };
    if (value <= benchmark.high) return { label: "needsAttention", color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" };
    return { label: "aboveBenchmark", color: "text-destructive", bg: "bg-destructive/10 border-destructive/20" };
  }
}

function formatMetricValue(value: number, unit: string, t: TFunction): string {
  if (unit === "$") return fmt$(value);
  if (unit === "units") return t("operations:team.unitsValue", { value: value.toFixed(1) });
  return String(value);
}

function TeamSourceBadge({ source, taxYear }: { source: TeamDataSource | undefined; taxYear?: number | null }) {
  const { t } = useTranslation(["operations", "common"]);
  if (!source) return null;
  if (source === "manual") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-500/10 border border-slate-500/20 text-slate-700 dark:text-slate-300" data-testid="badge-source-manual">
        {t("operations:team.source.manuallySet")}
      </span>
    );
  }
  if (source === "tax_return") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400" data-testid="badge-source-tax-return">
        {t("operations:team.source.taxReturn")}{taxYear != null ? ` · ${taxYear}` : ""}
      </span>
    );
  }
  if (source === "financial_statement") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400" data-testid="badge-source-financial-statement">
        {t("operations:team.source.fromPlUpload")}
      </span>
    );
  }
  if (source === "op_analytics") {
    return (
      <Link href="/operations?tab=analytics">
        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-500/10 border border-violet-500/20 text-violet-600 dark:text-violet-400 cursor-pointer hover:bg-violet-500/20 hover:border-violet-500/40 transition-colors" data-testid="badge-source-op-analytics">
          {t("operations:team.source.fromPlDataTab")}
        </span>
      </Link>
    );
  }
  if (source === "onboarding") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400" data-testid="badge-source-onboarding">
        {t("operations:team.source.onboarding")}
      </span>
    );
  }
  if (source === "derived") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-500/10 border border-violet-500/20 text-violet-600 dark:text-violet-400" data-testid="badge-source-derived">
        {t("operations:team.source.derived")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted border text-muted-foreground" data-testid="badge-source-estimated">
      {t("operations:team.source.estimated")}
    </span>
  );
}

const METRIC_DATA_SOURCE_KEY: Record<string, string> = {
  revenuePerEmployee: "avgMonthlyRevenue",
  unitsPerSalesperson: "avgSalesPerMonth",
  grossPerFI: "grossPerFI",
  costPerEmployee: "monthlyOpEx",
};

function TeamMetricCard({
  metricKey,
  value,
  projectedValue,
  hasChange,
  dealerType,
  dataSource,
  taxYear,
  projectedTotal,
  origTotal,
}: {
  metricKey: "revenuePerEmployee" | "unitsPerSalesperson" | "grossPerFI" | "costPerEmployee";
  value: number | null;
  projectedValue?: number | null;
  hasChange?: boolean;
  dealerType: DealerInventoryType;
  dataSource?: TeamDataSource;
  taxYear?: number | null;
  projectedTotal?: number;
  origTotal?: number;
}) {
  const { t } = useTranslation(["operations", "common"]);
  const benchmark = STAFFING_BENCHMARKS[metricKey][dealerType];
  const status = value != null ? getStatusInfo(value, benchmark, benchmark.higherIsBetter) : null;
  const showProjected = hasChange && projectedValue != null && value != null && Math.abs(projectedValue - value) > 0.01;
  const headcountIncreased = metricKey === "costPerEmployee" && projectedTotal != null && origTotal != null && projectedTotal > origTotal;

  return (
    <Card data-testid={`card-team-metric-${metricKey}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">{benchmark.label}</CardTitle>
          <TeamSourceBadge source={dataSource} taxYear={taxYear} />
        </div>
        <p className="text-xs text-muted-foreground">{benchmark.description}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <p className="text-3xl font-bold" data-testid={`value-team-${metricKey}`}>
              {value != null ? formatMetricValue(value, benchmark.unit, t) : "—"}
            </p>
            {value != null && status && (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${status.bg} ${status.color}`} data-testid={`status-team-${metricKey}`}>
                {t(`operations:team.status.${status.label}`)}
              </span>
            )}
          </div>
          {showProjected && (
            <div className="flex flex-col items-start">
              <p className="text-xs text-muted-foreground mb-0.5">{t("operations:team.projected")}</p>
              <div className="flex items-center gap-1">
                <p className="text-xl font-bold text-amber-600 dark:text-amber-400" data-testid={`value-team-projected-${metricKey}`}>
                  {formatMetricValue(projectedValue!, benchmark.unit, t)}
                </p>
                <MetricArrow cur={value} proj={projectedValue!} higherIsBetter={benchmark.higherIsBetter} forceRed={headcountIncreased} />
              </div>
              {headcountIncreased && (
                <p className="text-xs italic text-muted-foreground mt-0.5" data-testid="note-cost-per-employee-opex">{t("operations:team.opexNote")}</p>
              )}
            </div>
          )}
        </div>
        <div className="bg-muted/40 rounded-lg p-3 space-y-1.5 text-xs">
          <p className="font-semibold text-muted-foreground mb-1">{t("operations:team.industryBenchmark")}</p>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center">
              <p className="text-muted-foreground">{t("operations:team.low")}</p>
              <p className="font-semibold">{formatMetricValue(benchmark.low, benchmark.unit, t)}</p>
            </div>
            <div className="text-center bg-background rounded px-1 py-0.5">
              <p className="text-muted-foreground">{t("operations:team.median")}</p>
              <p className="font-bold">{formatMetricValue(benchmark.median, benchmark.unit, t)}</p>
            </div>
            <div className="text-center">
              <p className="text-muted-foreground">{t("operations:team.high")}</p>
              <p className="font-semibold">{formatMetricValue(benchmark.high, benchmark.unit, t)}</p>
            </div>
          </div>
          {(showProjected ? projectedValue : value) != null && (
            <div className="mt-2">
              <div className="relative h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="absolute inset-0 flex">
                  <div className="flex-1 bg-red-400/40 rounded-l-full" />
                  <div className="flex-1 bg-amber-400/40" />
                  <div className="flex-1 bg-emerald-400/40 rounded-r-full" />
                </div>
                {value != null && (() => {
                  const { low, median: _m, high } = benchmark;
                  const range = high - low;
                  const clamped = Math.max(low, Math.min(high, value));
                  const pct = range > 0 ? ((clamped - low) / range) * 100 : 50;
                  return (
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2 border-background bg-foreground shadow"
                      style={{ left: `calc(${pct}% - 5px)` }}
                    />
                  );
                })()}
                {showProjected && projectedValue != null && (() => {
                  const { low, median: _m, high } = benchmark;
                  const range = high - low;
                  const clamped = Math.max(low, Math.min(high, projectedValue));
                  const pct = range > 0 ? ((clamped - low) / range) * 100 : 50;
                  return (
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2 border-amber-400 bg-amber-500 shadow"
                      style={{ left: `calc(${pct}% - 5px)` }}
                    />
                  );
                })()}
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
                <span>{t("operations:team.low")}</span>
                <span>{t("operations:team.median")}</span>
                <span>{t("operations:team.high")}</span>
              </div>
              {showProjected && (
                <div className="flex gap-3 mt-1 text-[10px]">
                  <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-foreground inline-block" /> {t("operations:team.current")}</span>
                  <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> {t("operations:team.projected")}</span>
                </div>
              )}
            </div>
          )}
        </div>
        {value == null && (
          <p className="text-xs text-muted-foreground italic">{t("operations:team.insufficientData")}</p>
        )}
      </CardContent>
    </Card>
  );
}


function TeamHeadcountForm() {
  const { t } = useTranslation(["operations", "common"]);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [sales, setSales] = useState("");
  const [fi, setFi] = useState("");
  const [serviceRecon, setServiceRecon] = useState("");
  const [admin, setAdmin] = useState("");

  const mutation = useMutation({
    mutationFn: (data: { sales: number; fi: number; serviceRecon: number; admin: number }) =>
      apiRequest("POST", "/api/team-headcount", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-metrics"] });
      toast({ title: t("operations:team.toast.headcountSavedTitle"), description: t("operations:team.toast.headcountSavedDesc") });
    },
    onError: (err: any) => {
      toast({ title: t("operations:team.toast.saveFailedTitle"), description: err?.message ?? t("operations:team.toast.saveFailedDesc"), variant: "destructive" });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const salesNum = parseInt(sales, 10);
    const fiNum = parseInt(fi, 10);
    const serviceReconNum = parseInt(serviceRecon, 10) || 0;
    const adminNum = parseInt(admin, 10) || 0;
    if (!salesNum || salesNum < 1 || !fiNum || fiNum < 1) {
      toast({ title: t("operations:team.toast.validationTitle"), description: t("operations:team.toast.validationDesc"), variant: "destructive" });
      return;
    }
    mutation.mutate({ sales: salesNum, fi: fiNum, serviceRecon: serviceReconNum, admin: adminNum });
  }

  return (
    <Card className="max-w-lg mx-auto mt-8" data-testid="card-team-onboarding-prompt">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" />{t("operations:team.cardTitle")}</CardTitle>
        <CardDescription>
          {t("operations:team.cardDescription")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="form-team-headcount">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="thf-sales">{t("operations:team.salesStaff")} <span className="text-destructive">*</span></Label>
              <Input
                id="thf-sales"
                type="number"
                min={1}
                placeholder={t("operations:team.egSales")}
                value={sales}
                onChange={(e) => setSales(e.target.value)}
                data-testid="input-team-sales"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="thf-fi">{t("operations:team.fiManagers")} <span className="text-destructive">*</span></Label>
              <Input
                id="thf-fi"
                type="number"
                min={1}
                placeholder={t("operations:team.egFi")}
                value={fi}
                onChange={(e) => setFi(e.target.value)}
                data-testid="input-team-fi"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="thf-service">{t("operations:team.serviceRecon")}</Label>
              <Input
                id="thf-service"
                type="number"
                min={0}
                placeholder={t("operations:team.egService")}
                value={serviceRecon}
                onChange={(e) => setServiceRecon(e.target.value)}
                data-testid="input-team-service"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="thf-admin">{t("operations:team.admin")}</Label>
              <Input
                id="thf-admin"
                type="number"
                min={0}
                placeholder={t("operations:team.egAdmin")}
                value={admin}
                onChange={(e) => setAdmin(e.target.value)}
                data-testid="input-team-admin"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground"><Trans t={t} i18nKey="operations:team.requiredNote" components={{ req: <span className="text-destructive" /> }} /></p>
          <Button type="submit" disabled={mutation.isPending} data-testid="button-team-headcount-save" className="w-full">
            {mutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("operations:team.saving")}</> : t("operations:team.saveHeadcount")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function computeProjectedMetrics(
  inputs: TeamMetricsData["inputs"],
  origCounts: TeamMetricsData["headcounts"],
  adjSales: number,
  adjFI: number,
): {
  revenuePerEmployee: number | null;
  unitsPerSalesperson: number | null;
  grossPerFI: number | null;
  costPerEmployee: number | null;
  adjTotal: number;
} {
  const adjTotal = origCounts.total - origCounts.sales - origCounts.fi + adjSales + adjFI;
  const revenuePerEmployee = inputs.avgMonthlyRevenue != null && adjTotal > 0 ? inputs.avgMonthlyRevenue / adjTotal : null;
  const unitsPerSalesperson = inputs.avgSalesPerMonth != null && adjSales > 0 ? inputs.avgSalesPerMonth / adjSales : null;
  const grossPerFI = inputs.monthlyGrossProfit != null && adjFI > 0 ? inputs.monthlyGrossProfit / adjFI : null;
  const costPerEmployee = inputs.monthlyOperatingExpenses != null && adjTotal > 0 ? inputs.monthlyOperatingExpenses / adjTotal : null;
  return { revenuePerEmployee, unitsPerSalesperson, grossPerFI, costPerEmployee, adjTotal };
}

function MetricArrow({ cur, proj, higherIsBetter, forceRed }: { cur: number | null; proj: number | null; higherIsBetter: boolean; forceRed?: boolean }) {
  if (cur == null || proj == null || Math.abs(proj - cur) < 0.01) return null;
  const improved = !forceRed && (higherIsBetter ? proj > cur : proj < cur);
  return (
    <span className={`inline-flex items-center ml-1 text-xs font-semibold ${improved ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
      {improved ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
    </span>
  );
}

function TeamHeadcountEditDialog({
  open,
  onOpenChange,
  headcounts,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  headcounts: TeamMetricsData["headcounts"];
}) {
  const { t } = useTranslation(["operations", "common"]);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [sales, setSales] = useState("");
  const [fi, setFi] = useState("");
  const [serviceRecon, setServiceRecon] = useState("");
  const [admin, setAdmin] = useState("");

  useEffect(() => {
    if (open) {
      setSales(headcounts.sales > 0 ? String(headcounts.sales) : "");
      setFi(headcounts.fi > 0 ? String(headcounts.fi) : "");
      setServiceRecon(headcounts.serviceRecon ? String(headcounts.serviceRecon) : "");
      setAdmin(headcounts.admin ? String(headcounts.admin) : "");
    }
  }, [open, headcounts]);

  const mutation = useMutation({
    mutationFn: (data: { sales: number; fi: number; serviceRecon: number; admin: number }) =>
      apiRequest("POST", "/api/team-headcount", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-metrics"] });
      toast({ title: t("operations:team.toast.headcountUpdatedTitle"), description: t("operations:team.toast.headcountUpdatedDesc") });
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({ title: t("operations:team.toast.saveFailedTitle"), description: err?.message ?? t("operations:team.toast.saveFailedDesc"), variant: "destructive" });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const salesNum = parseInt(sales, 10);
    const fiNum = parseInt(fi, 10);
    const serviceReconNum = parseInt(serviceRecon, 10) || 0;
    const adminNum = parseInt(admin, 10) || 0;
    if (!salesNum || salesNum < 1 || !fiNum || fiNum < 1) {
      toast({ title: t("operations:team.toast.validationTitle"), description: t("operations:team.toast.validationDesc"), variant: "destructive" });
      return;
    }
    mutation.mutate({ sales: salesNum, fi: fiNum, serviceRecon: serviceReconNum, admin: adminNum });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialog-edit-headcount">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5" />{t("operations:team.editTitle")}</DialogTitle>
          <DialogDescription>{t("operations:team.editDescription")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="form-edit-headcount">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ehc-sales">{t("operations:team.salesStaff")} <span className="text-destructive">*</span></Label>
              <Input id="ehc-sales" type="number" min={1} placeholder={t("operations:team.egSales")} value={sales} onChange={(e) => setSales(e.target.value)} data-testid="input-edit-sales" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ehc-fi">{t("operations:team.fiManagers")} <span className="text-destructive">*</span></Label>
              <Input id="ehc-fi" type="number" min={1} placeholder={t("operations:team.egFi")} value={fi} onChange={(e) => setFi(e.target.value)} data-testid="input-edit-fi" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ehc-service">{t("operations:team.serviceRecon")}</Label>
              <Input id="ehc-service" type="number" min={0} placeholder={t("operations:team.egService")} value={serviceRecon} onChange={(e) => setServiceRecon(e.target.value)} data-testid="input-edit-service" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ehc-admin">{t("operations:team.admin")}</Label>
              <Input id="ehc-admin" type="number" min={0} placeholder={t("operations:team.egAdmin")} value={admin} onChange={(e) => setAdmin(e.target.value)} data-testid="input-edit-admin" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground"><Trans t={t} i18nKey="operations:team.requiredNote" components={{ req: <span className="text-destructive" /> }} /></p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="button-edit-headcount-cancel">{t("common:cancel")}</Button>
            <Button type="submit" disabled={mutation.isPending} data-testid="button-edit-headcount-save">
              {mutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("operations:team.saving")}</> : t("operations:team.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TeamRevenueEditDialog({
  open,
  onOpenChange,
  currentValue,
  isManual,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  currentValue: number | null;
  isManual: boolean;
}) {
  const { t } = useTranslation(["operations", "common"]);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [revenue, setRevenue] = useState("");

  useEffect(() => {
    if (open) {
      setRevenue(isManual && currentValue != null ? String(Math.round(currentValue)) : "");
    }
  }, [open, currentValue, isManual]);

  const mutation = useMutation({
    mutationFn: (monthlyRevenue: number | null) =>
      apiRequest("POST", "/api/team-revenue-override", { monthlyRevenue }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-metrics"] });
      toast({
        title: variables == null ? t("operations:team.toast.overrideClearedTitle") : t("operations:team.toast.revenueUpdatedTitle"),
        description: variables == null ? t("operations:team.toast.overrideClearedDesc") : t("operations:team.toast.revenueUpdatedDesc"),
      });
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({ title: t("operations:team.toast.saveFailedTitle"), description: err?.message ?? t("operations:team.toast.saveFailedDesc"), variant: "destructive" });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const num = parseFloat(revenue);
    if (isNaN(num) || num <= 0) {
      toast({ title: t("operations:team.toast.validationTitle"), description: t("operations:team.toast.revenueValidationDesc"), variant: "destructive" });
      return;
    }
    mutation.mutate(num);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialog-edit-revenue">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5" />{t("operations:team.editRevenueTitle")}</DialogTitle>
          <DialogDescription>
            {t("operations:team.editRevenueDescription")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="form-edit-revenue">
          <div className="space-y-1.5">
            <Label htmlFor="erev-amount">{t("operations:team.monthlyRevenue")}</Label>
            <Input id="erev-amount" type="number" min={1} step="any" placeholder={t("operations:team.egRevenue")} value={revenue} onChange={(e) => setRevenue(e.target.value)} data-testid="input-edit-revenue" />
          </div>
          <div className="flex items-center justify-between gap-2">
            <div>
              {isManual && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate(null)}
                  data-testid="button-edit-revenue-clear"
                >
                  {t("operations:team.revertToAuto")}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="button-edit-revenue-cancel">{t("common:cancel")}</Button>
              <Button type="submit" disabled={mutation.isPending} data-testid="button-edit-revenue-save">
                {mutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("operations:team.saving")}</> : t("operations:team.save")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TeamTab() {
  const { t } = useTranslation(["operations", "common"]);
  const { data: teamData, isLoading } = useQuery<TeamMetricsData>({ queryKey: ["/api/team-metrics"] });
  const [headcountEditOpen, setHeadcountEditOpen] = useState(false);
  const [revenueEditOpen, setRevenueEditOpen] = useState(false);

  const [simOpen, setSimOpen] = useState(false);
  const [adjSales, setAdjSales] = useState<number>(0);
  const [adjFI, setAdjFI] = useState<number>(0);
  const [unitTargetInput, setUnitTargetInput] = useState<string>("");
  const [targetUnits, setTargetUnits] = useState<number | null>(null);
  const [reduceCostsInput, setReduceCostsInput] = useState<string>("");
  const [activeQuickStart, setActiveQuickStart] = useState<string | null>(null);
  const [quickStartFeedback, setQuickStartFeedback] = useState<Record<string, string>>({});
  const [aiNarrative, setAiNarrative] = useState<{ narrative: string; headline: string; recommendations: string[] } | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("operations:team.loading")}
      </div>
    );
  }

  if (!teamData?.isRoleDataComplete) {
    return <TeamHeadcountForm />;
  }

  const dealerType = (["used_retail", "retail_wholesale", "wholesale", "bhph", "franchise"].includes(teamData.inventoryType)
    ? teamData.inventoryType
    : ["used", "mixed", "retail"].includes(teamData.inventoryType)
    ? "used_retail"
    : "used_retail") as DealerInventoryType;

  const { headcounts, metrics, inputs, dataSources, payroll, financialPeriod } = teamData;

  const hasRealFinancials = Object.values(dataSources || {}).some(
    (s) => s === "tax_return" || s === "financial_statement" || s === "op_analytics"
  );
  const poweredByFs = Object.values(dataSources || {}).some((s) => s === "financial_statement");

  const bm = STAFFING_BENCHMARKS;

  const curSales = headcounts.sales;
  const curFI = headcounts.fi;
  const newSales = Math.max(1, curSales + adjSales);
  const newFI = Math.max(1, curFI + adjFI);
  // For the "hit a unit target" scenario, project productivity against the TARGET
  // volume (the whole point is selling more units with the bigger team) — not the
  // current volume, which would make every added salesperson look like dead weight.
  const projectionInputs = activeQuickStart === "hit-unit-target" && targetUnits != null
    ? { ...inputs, avgSalesPerMonth: targetUnits }
    : inputs;
  const projected = computeProjectedMetrics(projectionInputs, headcounts, newSales, newFI);

  const hasChange = adjSales !== 0 || adjFI !== 0;

  // Industry-average all-in monthly cost per role (base/draw + taxes/benefits)
  const SALESPERSON_MONTHLY_COST = 3500;
  const FI_MANAGER_MONTHLY_COST = 6000;
  const salesDelta = newSales - curSales;
  const fiDelta = newFI - curFI;
  const totalDelta = salesDelta + fiDelta;
  const monthlyCostImpact = salesDelta * SALESPERSON_MONTHLY_COST + fiDelta * FI_MANAGER_MONTHLY_COST;
  // Keep costPerHead for the reduce-costs quick-start fallback
  const costPerHead = inputs.monthlyOperatingExpenses != null && headcounts.total > 0
    ? inputs.monthlyOperatingExpenses / headcounts.total
    : SALESPERSON_MONTHLY_COST;

  const avgGross = inputs.avgGrossProfitPerUnit ?? 0;
  const breakevenUnits = monthlyCostImpact != null && monthlyCostImpact > 0 && avgGross > 0
    ? monthlyCostImpact / avgGross
    : null;

  const optimalSalesCount = inputs.avgSalesPerMonth != null && bm.unitsPerSalesperson[dealerType].median > 0
    ? Math.ceil(inputs.avgSalesPerMonth / bm.unitsPerSalesperson[dealerType].median)
    : null;

  const productivityShiftDir = projected.unitsPerSalesperson != null && metrics.unitsPerSalesperson != null
    ? projected.unitsPerSalesperson > metrics.unitsPerSalesperson ? "up" : projected.unitsPerSalesperson < metrics.unitsPerSalesperson ? "down" : "same"
    : null;
  const productivityTowardBenchmark = projected.unitsPerSalesperson != null
    ? Math.abs(projected.unitsPerSalesperson - bm.unitsPerSalesperson[dealerType].median) <
      Math.abs((metrics.unitsPerSalesperson ?? projected.unitsPerSalesperson) - bm.unitsPerSalesperson[dealerType].median)
    : false;

  function resetSimulator() {
    setAdjSales(0);
    setAdjFI(0);
    setActiveQuickStart(null);
    setUnitTargetInput("");
    setTargetUnits(null);
    setReduceCostsInput("");
    setAiNarrative(null);
    setAiError(null);
    setQuickStartFeedback({});
  }

  function applyQuickStart(type: string) {
    setAiNarrative(null);
    setAiError(null);
    setQuickStartFeedback((prev) => ({ ...prev, [type]: "" }));
    if (type === "hit-unit-target") {
      const target = parseFloat(unitTargetInput);
      // Base required headcount on the industry median units per salesperson —
      // sizing the team to a sustainable industry pace rather than assuming the
      // dealer's current (possibly unusually high or low) rate scales forever.
      const perSalesperson = bm.unitsPerSalesperson[dealerType].median;
      if (!isNaN(target) && target > 0 && perSalesperson > 0) {
        const needed = Math.ceil(target / perSalesperson);
        const delta = needed - curSales;
        if (delta <= 0) {
          setQuickStartFeedback((prev) => ({ ...prev, [type]: t("operations:team.quickStart.feedbackTargetMet") }));
        } else {
          setAdjSales(delta);
          setTargetUnits(target);
          setActiveQuickStart(type);
        }
      }
    } else if (type === "reduce-costs") {
      const savings = parseFloat(reduceCostsInput);
      if (!isNaN(savings) && savings > 0) {
        const reduction = Math.floor(savings / SALESPERSON_MONTHLY_COST);
        const delta = Math.max(-curSales + 1, -reduction);
        if (delta === 0) {
          setQuickStartFeedback((prev) => ({ ...prev, [type]: t("operations:team.quickStart.feedbackSavingsTooSmall") }));
        } else {
          setAdjSales(delta);
          setActiveQuickStart(type);
        }
      }
    } else if (type === "maximize-throughput") {
      const targetUPS = bm.unitsPerSalesperson[dealerType].high;
      if (inputs.avgSalesPerMonth != null && targetUPS > 0) {
        // Team size that handles current volume at the top-performer pace. Works
        // both ways: adds staff if the team is stretched past the high benchmark,
        // trims staff (never below 1) if the team is larger than needed.
        const needed = Math.max(1, Math.ceil(inputs.avgSalesPerMonth / targetUPS));
        const delta = needed - curSales;
        if (delta === 0) {
          setQuickStartFeedback((prev) => ({ ...prev, [type]: t("operations:team.quickStart.feedbackAlreadySized") }));
        } else {
          setAdjSales(delta);
          setActiveQuickStart(type);
        }
      }
    }
  }

  async function handleAssessMyStaffing() {
    setAiLoading(true);
    setAiNarrative(null);
    setAiError(null);
    try {
      const body = {
        currentMetrics: {
          headcounts,
          metrics,
          inputs,
          inventoryType: dealerType,
        },
        scenario: {
          salesDelta: newSales - curSales,
          fiDelta: newFI - curFI,
          projectedMetrics: {
            revenuePerEmployee: projected.revenuePerEmployee,
            unitsPerSalesperson: projected.unitsPerSalesperson,
            grossPerFI: projected.grossPerFI,
            costPerEmployee: projected.costPerEmployee,
          },
          projectedHeadcounts: { total: projected.adjTotal, sales: newSales, fi: newFI },
          monthlyCostImpact: monthlyCostImpact ?? 0,
          breakevenUnits: breakevenUnits,
          optimalSalesCount: optimalSalesCount,
          scenarioLabel: activeQuickStart
            ? activeQuickStart === "hit-unit-target" ? `Hit unit target (${unitTargetInput} units/mo)`
              : activeQuickStart === "reduce-costs" ? `Reduce costs by $${reduceCostsInput}/mo`
              : "Maximize throughput (benchmark high)"
            : undefined,
        },
      };
      const res = await apiRequest("POST", "/api/assessment/staffing-scenario", body);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setAiNarrative(data);
    } catch (e: any) {
      setAiError(e.message ?? t("operations:team.assessmentFailed"));
    } finally {
      setAiLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t("operations:team.heading")}</h2>
          <p className="text-sm text-muted-foreground">{t("operations:team.benchmarkSubtitle", { type: INVENTORY_TYPE_LABELS[dealerType] ?? dealerType })}</p>
        </div>
        {hasRealFinancials ? (
          poweredByFs && financialPeriod ? (
            <div className="flex items-start gap-1.5 text-xs bg-blue-500/10 border border-blue-500/20 text-blue-700 dark:text-blue-300 rounded-lg px-3 py-2 max-w-xs" data-testid="note-team-disclaimer">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{t("operations:team.fromTaxReturn", { period: financialPeriod })}</span>
            </div>
          ) : null
        ) : (
          <div className="flex items-start gap-1.5 text-xs text-muted-foreground bg-muted/50 border rounded-lg px-3 py-2 max-w-xs" data-testid="note-team-disclaimer">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{t("operations:team.estimatedNote")}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: t("operations:team.totalHeadcount"), value: hasChange ? `${headcounts.total} → ${projected.adjTotal}` : headcounts.total > 0 ? String(headcounts.total) : "—", testId: "stat-team-total", editable: "headcount" as const },
          { label: t("operations:team.salesStaff"), value: hasChange ? `${curSales} → ${newSales}` : curSales > 0 ? String(curSales) : "—", testId: "stat-team-sales", editable: "headcount" as const },
          { label: t("operations:team.fiManagers"), value: hasChange ? `${curFI} → ${newFI}` : curFI > 0 ? String(curFI) : "—", testId: "stat-team-fi", editable: "headcount" as const },
          { label: t("operations:team.monthlyRevenue"), value: inputs.avgMonthlyRevenue != null ? fmt$(inputs.avgMonthlyRevenue) : "—", testId: "stat-team-revenue", editable: "revenue" as const },
        ].map(({ label, value, testId, editable }) => {
          const showEdit = testId === "stat-team-total" || editable === "revenue";
          return (
          <div key={label} className={`relative rounded-xl p-3 text-center border ${hasChange && (testId === "stat-team-total" || testId === "stat-team-sales" || testId === "stat-team-fi") ? "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800" : "bg-muted/40"}`}>
            {showEdit && (
              <button
                type="button"
                onClick={() => editable === "headcount" ? setHeadcountEditOpen(true) : setRevenueEditOpen(true)}
                className="absolute top-1.5 right-1.5 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={t("operations:team.editAria", { label })}
                data-testid={editable === "headcount" ? "button-edit-team-headcount" : "button-edit-team-revenue"}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-sm font-bold mt-0.5" data-testid={testId}>{value}</p>
            {editable === "revenue" && dataSources?.avgMonthlyRevenue && (
              <div className="mt-1 flex justify-center">
                <TeamSourceBadge source={dataSources.avgMonthlyRevenue as TeamDataSource} taxYear={teamData?.taxYear} />
              </div>
            )}
          </div>
          );
        })}
      </div>

      <TeamHeadcountEditDialog open={headcountEditOpen} onOpenChange={setHeadcountEditOpen} headcounts={headcounts} />
      <TeamRevenueEditDialog
        open={revenueEditOpen}
        onOpenChange={setRevenueEditOpen}
        currentValue={inputs.avgMonthlyRevenue}
        isManual={dataSources?.avgMonthlyRevenue === "manual"}
      />

      {payroll != null && (
        <div className="flex items-center gap-3 bg-blue-500/5 border border-blue-500/20 rounded-lg px-4 py-3" data-testid="callout-team-payroll">
          <DollarSign className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{t("operations:team.monthlyPayroll")}: <span className="font-bold" data-testid="value-team-payroll">{fmt$(payroll)}</span></p>
            <p className="text-xs text-muted-foreground">{t("operations:team.payrollNote")}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TeamMetricCard metricKey="revenuePerEmployee" value={metrics.revenuePerEmployee} projectedValue={projected.revenuePerEmployee} hasChange={hasChange} dealerType={dealerType} dataSource={dataSources[METRIC_DATA_SOURCE_KEY.revenuePerEmployee] as TeamDataSource} taxYear={teamData?.taxYear} />
        <TeamMetricCard metricKey="unitsPerSalesperson" value={metrics.unitsPerSalesperson} projectedValue={projected.unitsPerSalesperson} hasChange={hasChange} dealerType={dealerType} dataSource={dataSources[METRIC_DATA_SOURCE_KEY.unitsPerSalesperson] as TeamDataSource} taxYear={teamData?.taxYear} />
        <TeamMetricCard metricKey="grossPerFI" value={metrics.grossPerFI} projectedValue={projected.grossPerFI} hasChange={hasChange} dealerType={dealerType} dataSource={dataSources[METRIC_DATA_SOURCE_KEY.grossPerFI] as TeamDataSource} taxYear={teamData?.taxYear} />
        <TeamMetricCard metricKey="costPerEmployee" value={metrics.costPerEmployee} projectedValue={projected.costPerEmployee} hasChange={hasChange} dealerType={dealerType} dataSource={dataSources[METRIC_DATA_SOURCE_KEY.costPerEmployee] as TeamDataSource} taxYear={teamData?.taxYear} projectedTotal={projected.adjTotal} origTotal={headcounts.total} />
      </div>

      {/* ─── Staffing Scenarios ──────────────────────────────────────── */}
      <div className="border rounded-xl overflow-hidden" data-testid="section-staffing-scenarios">
        <button
          onClick={() => setSimOpen((o) => !o)}
          className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
          data-testid="button-staffing-scenarios-toggle"
        >
          <div className="flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-semibold">{t("operations:team.staffingScenarios")}</span>
            {hasChange && (
              <span className="text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700 rounded-full px-2 py-0.5">
                {t("operations:team.whatIfActive")}
              </span>
            )}
          </div>
          {simOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        </button>

        {simOpen && (
          <div className="p-4 space-y-5">
            {/* Quick-start buttons */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("operations:team.quickStartScenarios")}</p>
                {activeQuickStart && (
                  <Button size="sm" variant="ghost" onClick={resetSimulator} className="h-6 text-xs gap-1" data-testid="button-quickstart-reset">
                    <RotateCcw className="w-3 h-3" /> {t("operations:team.reset")}
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  {
                    id: "hit-unit-target",
                    label: t("operations:team.quickStart.hitUnitTargetLabel"),
                    desc: t("operations:team.quickStart.hitUnitTargetDesc"),
                    placeholder: t("operations:team.quickStart.hitUnitTargetPlaceholder"),
                    inputValue: unitTargetInput,
                    setInput: setUnitTargetInput,
                  },
                  {
                    id: "reduce-costs",
                    label: t("operations:team.quickStart.reduceCostsLabel"),
                    desc: t("operations:team.quickStart.reduceCostsDesc"),
                    placeholder: t("operations:team.quickStart.reduceCostsPlaceholder"),
                    inputValue: reduceCostsInput,
                    setInput: setReduceCostsInput,
                  },
                  {
                    id: "maximize-throughput",
                    label: t("operations:team.quickStart.maximizeThroughputLabel"),
                    desc: t("operations:team.quickStart.maximizeThroughputDesc"),
                    placeholder: null,
                    inputValue: "",
                    setInput: () => {},
                  },
                ].map(({ id, label, desc, placeholder, inputValue, setInput }) => {
                  const isActive = activeQuickStart === id;
                  const isDisabled = activeQuickStart !== null && !isActive;
                  const feedbackMsg = quickStartFeedback[id];
                  return (
                  <div key={id} className={`border rounded-lg p-3 space-y-2 transition-colors ${isActive ? "border-primary bg-primary/5" : isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-muted-foreground/40"}`} data-testid={`card-quickstart-${id}`}>
                    <p className="text-xs font-semibold">{label}</p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                    {placeholder && (
                      <Input
                        type="number"
                        min={0}
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={(e) => { setInput(e.target.value); setQuickStartFeedback((prev) => ({ ...prev, [id]: "" })); }}
                        className="h-7 text-xs"
                        disabled={isDisabled}
                        data-testid={`input-quickstart-${id}`}
                      />
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full h-7 text-xs"
                      onClick={() => { applyQuickStart(id); }}
                      disabled={isDisabled}
                      data-testid={`button-quickstart-${id}`}
                    >
                      {t("operations:team.apply")}
                    </Button>
                    {feedbackMsg && (
                      <div
                        className="flex items-start gap-1.5 rounded-md border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 p-2"
                        data-testid={`feedback-quickstart-${id}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-[1px] text-emerald-600 dark:text-emerald-400" />
                        <p className="text-[11px] font-medium text-emerald-800 dark:text-emerald-300">{feedbackMsg}</p>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Manual steppers */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("operations:team.adjustManually")}</p>
              {activeQuickStart && (
                <p className="text-[10px] text-muted-foreground mb-2">{t("operations:team.adjustPrompt")}</p>
              )}
              <div className="flex flex-wrap gap-4 items-center">
                {[
                  { label: t("operations:team.salesStaff"), adj: adjSales, setAdj: setAdjSales, cur: curSales, testId: "stepper-sales" },
                  { label: t("operations:team.fiManagers"), adj: adjFI, setAdj: setAdjFI, cur: curFI, testId: "stepper-fi" },
                ].map(({ label, adj, setAdj, cur: curCount, testId }) => {
                  const newCount = Math.max(1, curCount + adj);
                  return (
                    <div key={label} className="flex flex-col gap-1">
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <div className="flex items-center gap-2">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-7 w-7"
                          onClick={() => { setAdj((a) => a - 1); setAiNarrative(null); }}
                          disabled={newCount <= 1 || !!activeQuickStart}
                          data-testid={`${testId}-minus`}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="text-sm font-bold w-6 text-center" data-testid={`${testId}-value`}>{newCount}</span>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-7 w-7"
                          onClick={() => { setAdj((a) => a + 1); setAiNarrative(null); }}
                          disabled={!!activeQuickStart}
                          data-testid={`${testId}-plus`}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                        {adj !== 0 && (
                          <span className={`text-xs font-semibold ${adj > 0 ? "text-emerald-600" : "text-destructive"}`}>
                            {adj > 0 ? `+${adj}` : adj}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground">{t("operations:team.currentCount", { count: curCount })}</p>
                    </div>
                  );
                })}
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-muted-foreground">{t("operations:team.totalHeadcount")}</p>
                  <p className="text-sm font-bold">{projected.adjTotal}</p>
                  {hasChange && <p className="text-[10px] text-muted-foreground">{t("operations:team.fromCount", { count: headcounts.total })}</p>}
                </div>
                {hasChange && (
                  <Button size="sm" variant="ghost" onClick={resetSimulator} className="h-7 text-xs mt-4" disabled={!!activeQuickStart} data-testid="button-sim-reset">
                    <RotateCcw className="w-3 h-3 mr-1" /> {t("operations:team.reset")}
                  </Button>
                )}
              </div>
            </div>

            {/* Scenario Impact Panel */}
            {hasChange && (
              <div className="border rounded-lg p-4 space-y-4 bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800" data-testid="panel-scenario-impact">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold flex items-center gap-1.5"><Target className="w-4 h-4" />{t("operations:team.scenarioImpact")}</p>
                </div>
                <TooltipProvider delayDuration={0}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="bg-background/60 border rounded-md p-3 space-y-1">
                    <div className="flex items-center gap-1">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("operations:team.monthlyCostImpact")}</p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-muted-foreground hover:text-foreground" data-testid="tooltip-trigger-cost-impact" aria-label={t("operations:team.monthlyCostImpact")}>
                            <Info className="w-3 h-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[260px] text-xs">
                          <Trans t={t} i18nKey="operations:team.costImpactTooltip" components={{ red: <span className="text-destructive font-medium" />, green: <span className="text-emerald-500 font-medium" /> }} />
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    {hasChange ? (
                      <p className={`text-base font-bold ${monthlyCostImpact > 0 ? "text-destructive" : "text-emerald-600 dark:text-emerald-400"}`} data-testid="text-cost-impact">
                        {fmt$(Math.abs(monthlyCostImpact))}{t("operations:team.perMo")}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">{t("operations:team.adjustToSeeCost")}</p>
                    )}
                    {hasChange && (
                      <p className="text-xs text-muted-foreground" data-testid="text-cost-impact-description">
                        {monthlyCostImpact > 0
                          ? t("operations:team.costImpactAdd", { amount: fmt$(Math.abs(monthlyCostImpact)) })
                          : t("operations:team.costImpactSave", { amount: fmt$(Math.abs(monthlyCostImpact)) })}
                      </p>
                    )}
                    {hasChange && (
                      <p className="text-xs text-muted-foreground">{t("operations:team.industryAvgNote")}</p>
                    )}
                  </div>
                  <div className="bg-background/60 border rounded-md p-3 space-y-1">
                    <div className="flex items-center gap-1">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("operations:team.breakevenUnits")}</p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-muted-foreground hover:text-foreground" data-testid="tooltip-trigger-breakeven-units" aria-label={t("operations:team.breakevenUnits")}>
                            <Info className="w-3 h-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[260px] text-xs">
                          {t("operations:team.breakevenTooltip")}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    {breakevenUnits != null ? (
                      <>
                        <p className="text-base font-bold text-foreground" data-testid="text-breakeven-units">{t("operations:team.breakevenValue", { count: Math.ceil(breakevenUnits) })}</p>
                        <p className="text-xs text-muted-foreground">{t("operations:team.breakevenNeed", { count: Math.ceil(breakevenUnits) })}</p>
                      </>
                    ) : monthlyCostImpact != null && monthlyCostImpact <= 0 ? (
                      <p className="text-base font-bold text-emerald-600">{t("operations:team.noBreakeven")}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">{t("operations:team.breakevenMissing")}</p>
                    )}
                  </div>
                  <div className="bg-background/60 border rounded-md p-3 space-y-1">
                    <div className="flex items-center gap-1">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("operations:team.productivityShift")}</p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-muted-foreground hover:text-foreground" data-testid="tooltip-trigger-productivity-shift" aria-label={t("operations:team.productivityShift")}>
                            <Info className="w-3 h-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[260px] text-xs">
                          {t("operations:team.productivityTooltip")}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    {projected.unitsPerSalesperson != null ? (
                      <>
                        <div className="flex items-end gap-3" data-testid="text-productivity-shift">
                          {metrics.unitsPerSalesperson != null && (
                            <div className="space-y-0.5">
                              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{t("operations:team.before")}</p>
                              <p className="text-base font-bold">{metrics.unitsPerSalesperson.toFixed(1)}</p>
                            </div>
                          )}
                          {metrics.unitsPerSalesperson != null && (
                            <ArrowRight className="h-4 w-4 text-muted-foreground mb-0.5" />
                          )}
                          <div className="space-y-0.5">
                            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{t("operations:team.after")}</p>
                            <div className="flex items-baseline gap-1.5">
                              <p className="text-base font-bold">{projected.unitsPerSalesperson.toFixed(1)}</p>
                              {metrics.unitsPerSalesperson != null && (() => {
                                const delta = projected.unitsPerSalesperson - metrics.unitsPerSalesperson;
                                const isPositive = delta > 0;
                                const isNeutral = delta === 0;
                                return !isNeutral ? (
                                  <span
                                    className={`text-xs font-bold ${isPositive ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}
                                    data-testid="text-productivity-delta"
                                  >
                                    {isPositive ? "+" : "−"}{Math.abs(delta).toFixed(1)}
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground pb-0.5">{t("operations:team.unitsSoldPerSalesperson")}</p>
                        </div>
                        {productivityShiftDir && (
                          <p className={`text-xs font-medium ${productivityTowardBenchmark ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                            {productivityTowardBenchmark ? t("operations:team.movesToward") : t("operations:team.movesAway")}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">{t("operations:team.na")}</p>
                    )}
                  </div>
                  <div className="bg-background/60 border rounded-md p-3 space-y-1">
                    <div className="flex items-center gap-1">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("operations:team.optimalHeadcountNote")}</p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-muted-foreground hover:text-foreground" data-testid="tooltip-trigger-optimal-headcount" aria-label={t("operations:team.optimalHeadcountNote")}>
                            <Info className="w-3 h-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[260px] text-xs">
                          {t("operations:team.optimalTooltip")}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    {optimalSalesCount != null ? (
                      <p className="text-xs text-muted-foreground" data-testid="text-optimal-headcount">
                        <Trans
                          i18nKey="operations:team.optimalText"
                          values={{ count: optimalSalesCount, median: bm.unitsPerSalesperson[dealerType].median }}
                          components={{ strong: <span className="font-semibold text-foreground" /> }}
                        />
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">{t("operations:team.optimalMissing")}</p>
                    )}
                  </div>
                </div>
                </TooltipProvider>

                {/* AI Assessment button + narrative */}
                <div className="pt-2 border-t border-amber-200 dark:border-amber-800">
                  <Button
                    onClick={handleAssessMyStaffing}
                    disabled={aiLoading}
                    className="gap-1.5"
                    data-testid="button-assess-staffing"
                  >
                    {aiLoading ? <><Loader2 className="w-4 h-4 animate-spin" />{t("operations:team.assessing")}</> : <><Lightbulb className="w-4 h-4" />{t("operations:team.assessMyStaffing")}</>}
                  </Button>
                  {aiError && (
                    <p className="text-xs text-destructive mt-2" data-testid="text-ai-error">{aiError}</p>
                  )}
                  {aiNarrative && (
                    <div className="mt-4 space-y-3" data-testid="section-ai-narrative">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
                        <p className="text-sm font-semibold">{aiNarrative.headline}</p>
                      </div>
                      <div className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                        {aiNarrative.narrative}
                      </div>
                      {aiNarrative.recommendations.length > 0 && (
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Recommendations</p>
                          <ul className="space-y-1.5">
                            {aiNarrative.recommendations.map((r, i) => (
                              <li key={i} className="flex items-start gap-2 text-xs">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                                <span>{r}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Disclaimer */}
                <div className="flex items-start gap-1.5 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2" data-testid="note-sim-disclaimer">
                  <Info className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>{t("operations:team.simDisclaimer")}</span>
                </div>
              </div>
            )}

            {!hasChange && (
              <p className="text-xs text-muted-foreground italic text-center py-2">{t("operations:team.adjustPrompt")}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

const VALID_TABS = ["analytics", "expenses", "pldata", "marketing", "bank-health", "team", "assessment"];
const TAB_ALIASES: Record<string, string> = { "performance-assessment": "assessment" };

function getTabFromSearch(): string {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("tab") ?? "";
  const tab = TAB_ALIASES[raw] ?? raw;
  return VALID_TABS.includes(tab) ? tab : "analytics";
}

const TAB_META: Record<string, { title: string; subtitle: string }> = {
  analytics: {
    title: "Dealership Analysis",
    subtitle: "Enter your P&L figures to compute dealership KPIs and cost-to-carry",
  },
  expenses: {
    title: "Expense Analysis",
    subtitle: "Review and benchmark your dealership's expense categories",
  },
  pldata: {
    title: "P&L Data",
    subtitle: "Enter profit and loss figures for your dealership",
  },
  marketing: {
    title: "Marketing",
    subtitle: "Analyze and optimize your marketing spend and performance",
  },
  "bank-health": {
    title: "Bank Health",
    subtitle: "Monitor your floorplan and banking relationships",
  },
  team: {
    title: "Team",
    subtitle: "Review team composition, compensation, and performance",
  },
  assessment: {
    title: "Assessment",
    subtitle: "Evaluate dealership performance across key operational areas",
  },
};

const MemoAssessmentTab = memo(AssessmentTab);
const MemoPLDataTab = memo(PLDataTab);
const MemoTeamTab = memo(TeamTab);
const MemoBankHealthTab = memo(BankHealthTab);

export default function OperationsPage() {
  const [activeTab, setActiveTab] = useState(getTabFromSearch);
  const search = useSearch();

  useEffect(() => {
    const tab = getTabFromSearch();
    if (VALID_TABS.includes(tab) && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [search]);

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    trackEvent("tab_change", "/operations", { tab });
  }, []);

  const tabMeta = TAB_META[activeTab] ?? TAB_META["analytics"];

  const stableValue = useMemo(() => ({
    activeTab, setActiveTab: handleTabChange,
  }), [activeTab, handleTabChange]);

  return (
    <OperationsCtx.Provider value={stableValue}>
      <OperationsLiveProvider>
        <div className="p-4 md:p-6 space-y-6 max-w-screen-xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="heading-page-title">{tabMeta.title}</h1>
              <p className="text-muted-foreground text-sm mt-0.5" data-testid="heading-page-subtitle">
                {tabMeta.subtitle}
              </p>
            </div>
          </div>
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsContent value="analytics" className="mt-5"><AnalyticsTab /></TabsContent>
            <TabsContent value="expenses" className="mt-5"><ExpensesTab /></TabsContent>
            <TabsContent value="pldata" className="mt-5"><MemoPLDataTab /></TabsContent>
            <TabsContent value="marketing" className="mt-5"><MarketingTab /></TabsContent>
            <TabsContent value="bank-health" className="mt-5"><MemoBankHealthTab /></TabsContent>
            <TabsContent value="team" className="mt-5"><MemoTeamTab /></TabsContent>
            <TabsContent value="assessment" className="mt-5"><MemoAssessmentTab /></TabsContent>
          </Tabs>
        </div>
      </OperationsLiveProvider>
    </OperationsCtx.Provider>
  );
}
